import React, { useState, useRef, useEffect, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { fetchProducts, fetchCategories, fetchBrands } from "./productsApi";
import { loadScaledModel, createLoadingSpinner } from "./ModelLoader";
import { fetchTheme, fetchCategoryColors, DEFAULT_THEME, DEFAULT_CATEGORY_COLORS } from "./settingsApi";
import ModeSwitcher from "./ModeSwitcher";
import LogoMLV from "./assets/logo-mlv.png";
import AverCam550 from "./assets/aver-cam550.png";
import SamsungTheWall from "./assets/samsung-the-wall.png";
import HisenseLED108 from "./assets/hisense-led108.png";
import HisenseLED136DE from "./assets/hisense-led136de.png";
import HisenseLED136GE from "./assets/hisense-led136ge.png";
import HisenseLED163DE from "./assets/hisense-led163de.png";
import HisenseLED163GE from "./assets/hisense-led163ge.png";
import HisenseHTJ from "./assets/hisense-htj.png";
import HisenseRO from "./assets/hisense-ro.png";
import HisenseROHFE from "./assets/hisense-rohfe.png";
import HisenseSPFE from "./assets/hisense-spfe.png";
import HisenseXIH from "./assets/hisense-xih.png";
import HisenseXIHFE from "./assets/hisense-xihfe.png";
import HisenseXIHS from "./assets/hisense-xih-s.png";
import HisenseXIM from "./assets/hisense-xim.png";
import HisenseXOM from "./assets/hisense-xom.png";
import HisenseXOMFE from "./assets/hisense-xomfe.png";
import AverCAM540 from "./assets/aver-cam540.png";
import AverCAM520P3 from "./assets/aver-cam520pro3.png";
import AverCAM130 from "./assets/aver-cam130.png";
import AverVC550 from "./assets/aver-vc550.png";
import AverVC540 from "./assets/aver-vc540.png";
import AverVC520P3 from "./assets/aver-vc520pro3.png";
import ExtronFF120T from "./assets/extron-ff120t.png";
import ExtronFF220T from "./assets/extron-ff220t.png";
import ExtronSF26CTLP from "./assets/extron-sf26ctlp.png";
import ExtronSF8CTSub from "./assets/extron-sf8ctsub.png";
import ExtronSF26CT from "./assets/extron-sf26ct.png";
import ExtronSF26X from "./assets/extron-sf26x.png";
import ExtronSF3CLP from "./assets/extron-sf3clp.png";
import ExtronSF3CTLP from "./assets/extron-sf3ctlp.png";
import CiscoTableMicPro from "./assets/cisco-table-mic-pro.png";
import CiscoTableMic from "./assets/cisco-table-mic.png";
import CiscoCeilingMicPro from "./assets/cisco-ceiling-mic-pro.png";
import CiscoDeskCam1080 from "./assets/cisco-desk-cam-1080.png";
import CiscoDeskCam4K from "./assets/cisco-desk-cam-4k.png";
import CiscoRoomVisionPTZ from "./assets/cisco-room-vision-ptz.png";
import CiscoQuadCamera from "./assets/cisco-quad-camera.png";
import CiscoPTZ4K from "./assets/cisco-ptz-4k.png";
import AverCam570 from "./assets/aver-cam570.png";
import CrestronIC4T from "./assets/crestron-ic4t.png";
import CrestronIC6T from "./assets/crestron-ic6t.png";
import CrestronIC8T from "./assets/crestron-ic8t.png";
import CrestronICI4T from "./assets/crestron-ici4t.png";
import CrestronICI6T from "./assets/crestron-ici6t.png";
import CrestronICT5T22 from "./assets/crestron-ict5t22.png";
import CrestronIC6ULPT from "./assets/crestron-ic6ulpt.png";
import CrestronICS8T from "./assets/crestron-ics8t.png";
import CrestronIC4P from "./assets/crestron-ic4p.png";
import CrestronRefIC6 from "./assets/crestron-ref-ic6.png";
import CrestronRefIC8 from "./assets/crestron-ref-ic8.png";
import CrestronUltIC6 from "./assets/crestron-ult-ic6.png";
import CrestronUltIC8 from "./assets/crestron-ult-ic8.png";
import CrestronUltICS8 from "./assets/crestron-ult-ics8.png";
import CrestronUltIC8DT from "./assets/crestron-ult-ic8dt.png";
import CrestronSB200 from "./assets/crestron-sb200.png";
import BoseDM8C from "./assets/bose-dm8c.png";
import BoseDM8CSub from "./assets/bose-dm8c-sub.png";
import BoseDM6C from "./assets/bose-dm6c.png";
import BoseDM5C from "./assets/bose-dm5c.png";
import BoseDM3C from "./assets/bose-dm3c.png";
import BoseDM2CLP from "./assets/bose-dm2c-lp.png";
import BoseFS4CE from "./assets/bose-fs4ce.png";
import BoseFS2C from "./assets/bose-fs2c.png";
import BoseFS3Sat from "./assets/bose-fs3-satellite.png";
import BoseFS3Sub from "./assets/bose-fs3-acoustimass.png";
import BoseDM6PE from "./assets/bose-dm6pe.png";
import BoseDML88P from "./assets/bose-dml88p.png";
import BoseDM5P from "./assets/bose-dm5p.png";
import BoseDM3P from "./assets/bose-dm3p.png";
import BoseFS2P from "./assets/bose-fs2p.png";


const Icon = {
  Ruler: ({ size = 16, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.204 5.234a1 1 0 0 0-1.415 0L5.238 19.786a1 1 0 0 0 0 1.414l.804.804a1 1 0 0 0 1.415 0L21.908 7.452a1 1 0 0 0 0-1.414z" /><path d="m9 9 1.5 1.5M12 6l1.5 1.5M15 3l1.5 1.5M6 12l1.5 1.5" /></svg>),
  Signal: ({ size = 16, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12 C2 6.5 6.5 2 12 2s10 4.5 10 10" /><path d="M5 12c0-3.9 3.1-7 7-7s7 3.1 7 7" /><path d="M8 12c0-2.2 1.8-4 4-4s4 1.8 4 4" /><circle cx="12" cy="12" r="1" fill={color} /></svg>),
  Download: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>),
  Edit: ({ size = 11, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>),
  Pin: ({ size = 16, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" /></svg>),
  Chair: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 19v-8" /><path d="M18 19v-8" /><path d="M6 11H18" /><path d="M8 11V7a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" /></svg>),
  Trash: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>),
  Mouse: ({ size = 24, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="7" /><path d="M12 6v4" /></svg>),
  Wall: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="4" rx="1" /><rect x="2" y="13" width="20" height="4" rx="1" /><line x1="12" y1="5" x2="12" y2="9" /><line x1="6" y1="13" x2="6" y2="17" /><line x1="18" y1="13" x2="18" y2="17" /></svg>),
  Floor: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20" /><path d="M6 20V8l6-5 6 5v12" /><path d="M10 20v-5h4v5" /></svg>),
  Ceiling: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /><line x1="5" y1="3" x2="19" y2="3" /></svg>),
  Table: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18" /><path d="M3 6h18v3H3z" /><path d="M7 9v9M17 9v9" /></svg>),
  Image: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>),
  PDF: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>),
  Hand: ({ size = 16, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2" /><path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2" /><path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8" /><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" /></svg>),
  Plus: ({ size = 16, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>),
  X: ({ size = 14, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>),
  Camera: ({ size = 12, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>),
  Mic: ({ size = 12, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" /></svg>),
  Speaker: ({ size = 12, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>),
  Display: ({ size = 12, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>),
  ChevronDown: ({ size = 10, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>),
  Dot: ({ size = 8, color = "currentColor" }) => (<svg width={size} height={size} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill={color} /></svg>),
};

// CATEGORY_COVERAGE_COLORS — warna kategori untuk coverage zone 3D.
// Object ini MUTABLE (bukan re-assign, hanya field-nya yang diubah) supaya
// bisa diupdate oleh applyCategoryColorsToCoverageMap() setelah fetch dari
// Supabase (tabel categories), sementara fungsi top-level buildCoverageZone()
// yang membaca object yang sama tetap selalu lihat versi terbaru tanpa
// perlu di-passing ulang sebagai parameter di banyak tempat.
const CATEGORY_COVERAGE_COLORS = {
  display: "#a78bfa",
  camera: "#3b82f6",
  microphone: "#22c55e",
  speaker: "#f59e0b",
};

// Mapping id kategori (sesuai tabel categories di Supabase, PascalCase
// seperti "Display"/"Conference"/dst) -> key type produk (lowercase, sesuai
// field `type` produk seperti "display"/"camera"/dst) yang dipakai sebagai
// key di CATEGORY_COVERAGE_COLORS.
const CATEGORY_ID_TO_TYPE_KEY = {
  Display: "display",
  Conference: "camera",
  Microphone: "microphone",
  Speaker: "speaker",
};

// Catatan: fungsi ini HANYA menyinkronkan warna ke geometri 3D (coverage
// zone) via object mutable di atas. Untuk elemen UI React (footer, sidebar
// katalog, list "Placed Devices") warna kategori disimpan terpisah sebagai
// state `categoryColorsByType` di dalam komponen RoomDesigner, supaya React
// tahu kapan harus re-render saat warna berubah — object mutable seperti
// CATEGORY_COVERAGE_COLORS tidak memicu re-render dengan sendirinya.
function applyCategoryColorsToCoverageMap(categoryColors) {
  for (const [categoryId, typeKey] of Object.entries(CATEGORY_ID_TO_TYPE_KEY)) {
    if (categoryColors[categoryId]) {
      CATEGORY_COVERAGE_COLORS[typeKey] = categoryColors[categoryId];
    }
  }
}

// FALLBACK_PRODUCTS — data cadangan dipakai HANYA kalau fetch dari Supabase
// gagal (offline, error koneksi, dll). Sumber utama katalog produk sekarang
// adalah tabel `products` di Supabase (lihat productsApi.js), yang bisa
// dikelola lewat halaman /admin tanpa perlu deploy ulang aplikasi.
const FALLBACK_PRODUCTS = [
  { id: "samsung-wall-146", category: "Display", type: "display", brand: "Samsung", name: "Samsung The Wall 146″", width: 3.3, height: 1.9, depth: 0.08, mountOn: "wall", color: "#1a1a2e", accentColor: "#a78bfa", image: SamsungTheWall, coverageAngle: 160, coverageDistance: 8 },
  { id: "aver-cam-550", category: "Conference", type: "camera", brand: "AVer", name: "AVer CAM550", width: 0.25, height: 0.15, depth: 0.2, mountOn: "wall", color: "#e67e22", accentColor: "#3b82f6", image: AverCam550, coverageAngle: 85, coverageDistance: 5 },
  { id: "hisense-led-108", category: "Display", type: "display", brand: "Hisense", name: 'Hisense LED 108"', width: 2.404, height: 1.437, depth: 0.032, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED108, coverageAngle: 165, coverageDistance: 8 },
  { id: "hisense-led-136", category: "Display", type: "display", brand: "Hisense", name: 'Hisense LED 136"', width: 3.012, height: 1.695, depth: 0.032, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED108, coverageAngle: 165, coverageDistance: 10 },
  { id: "hisense-led-163", category: "Display", type: "display", brand: "Hisense", name: 'Hisense LED 163"', width: 3.613, height: 2.034, depth: 0.032, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED108, coverageAngle: 165, coverageDistance: 12 },
  { id: "hisense-haio136de", category: "Display", type: "display", brand: "Hisense", name: 'Hisense HAIO136DE 136"', width: 3.004, height: 1.7745, depth: 0.028, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED136DE, coverageAngle: 150, coverageDistance: 10, _meta: { pixelPitch: "1.5625mm", tech: "COB Flip-chip", type3D: "aio-led" } },
  { id: "hisense-haio136ge", category: "Display", type: "display", brand: "Hisense", name: 'Hisense HAIO136GE 136"', width: 3.004, height: 1.7745, depth: 0.028, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED136GE, coverageAngle: 150, coverageDistance: 10, _meta: { pixelPitch: "1.5625mm", tech: "COB Flip-chip", type3D: "aio-led" } },
  { id: "hisense-haio163-a", category: "Display", type: "display", brand: "Hisense", name: 'Hisense HAIO163 163" (A)', width: 3.613, height: 2.034, depth: 0.028, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED163DE, coverageAngle: 160, coverageDistance: 12, _meta: { pixelPitch: "1.875mm", tech: "COB", type3D: "aio-led" } },
  { id: "hisense-haio163-b", category: "Display", type: "display", brand: "Hisense", name: 'Hisense HAIO163 163" (B)', width: 3.613, height: 2.034, depth: 0.028, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseLED163GE, coverageAngle: 160, coverageDistance: 12, _meta: { pixelPitch: "1.875mm", tech: "COB", type3D: "aio-led" } },
  { id: "hisense-htj", category: "Display", type: "display", brand: "Hisense", name: "Hisense HTJ Indoor LED", width: 1.8, height: 1.0125, depth: 0.031, mountOn: "wall", color: "#111111", accentColor: "#a78bfa", image: HisenseHTJ, coverageAngle: 155, coverageDistance: 15, _meta: { pixelPitch: "P0.9~P1.25", tech: "COB Flip-chip", type3D: "led-wall-indoor", cabinetW: 0.6, cabinetH: 0.3375 } },
  { id: "hisense-ro", category: "Display", type: "display", brand: "Hisense", name: "Hisense RO Outdoor LED", width: 2.0, height: 2.0, depth: 0.065, mountOn: "wall", color: "#1a1a1a", accentColor: "#a78bfa", image: HisenseRO, coverageAngle: 160, coverageDistance: 30, _meta: { pixelPitch: "P2.9~P3.9", tech: "SMD IP65", type3D: "led-wall-outdoor", cabinetW: 0.5, cabinetH: 0.5 } },
  { id: "hisense-rohfe", category: "Display", type: "display", brand: "Hisense", name: "Hisense ROHFE Outdoor COB", width: 2.0, height: 2.0, depth: 0.090, mountOn: "wall", color: "#1a1a1a", accentColor: "#a78bfa", image: HisenseROHFE, coverageAngle: 140, coverageDistance: 40, _meta: { pixelPitch: "P1.56", tech: "COB Flip-chip IP65 3000nit", type3D: "led-wall-outdoor", cabinetW: 0.5, cabinetH: 0.5 } },
  { id: "hisense-spfe", category: "Display", type: "display", brand: "Hisense", name: "Hisense SPFE Stadium LED", width: 3.2, height: 1.8, depth: 0.090, mountOn: "wall", color: "#0f0f0f", accentColor: "#a78bfa", image: HisenseSPFE, coverageAngle: 155, coverageDistance: 60, _meta: { pixelPitch: "P6.67~P10", tech: "SMD Perimeter IP65", type3D: "led-wall-outdoor-wide", cabinetW: 1.6, cabinetH: 0.9 } },
  { id: "hisense-xih", category: "Display", type: "display", brand: "Hisense", name: "Hisense XIH Indoor COB", width: 1.8, height: 1.0125, depth: 0.039, mountOn: "wall", color: "#111111", accentColor: "#a78bfa", image: HisenseXIH, coverageAngle: 155, coverageDistance: 12, _meta: { pixelPitch: "P0.78~P1.56", tech: "COB Flip-chip", type3D: "led-wall-indoor", cabinetW: 0.6, cabinetH: 0.3375 } },
  { id: "hisense-xihfe", category: "Display", type: "display", brand: "Hisense", name: "Hisense XIHFE Indoor COB", width: 1.8, height: 1.0125, depth: 0.035, mountOn: "wall", color: "#111111", accentColor: "#a78bfa", image: HisenseXIHFE, coverageAngle: 155, coverageDistance: 12, _meta: { pixelPitch: "P0.9375~P1.875", tech: "COB Flip-chip 46% less power", type3D: "led-wall-indoor", cabinetW: 0.6, cabinetH: 0.3375 } },
  { id: "hisense-xih-s", category: "Display", type: "display", brand: "Hisense", name: "Hisense XIH-S Indoor COB", width: 1.8, height: 1.0125, depth: 0.034, mountOn: "wall", color: "#0d0d0d", accentColor: "#a78bfa", image: HisenseXIHS, coverageAngle: 155, coverageDistance: 10, _meta: { pixelPitch: "P0.78~P1.56", tech: "COB Flip-chip ultra-fine", type3D: "led-wall-indoor", cabinetW: 0.6, cabinetH: 0.3375 } },
  { id: "hisense-xim", category: "Display", type: "display", brand: "Hisense", name: "Hisense XIM Indoor SMD", width: 1.92, height: 1.44, depth: 0.048, mountOn: "wall", color: "#1a1a1a", accentColor: "#a78bfa", image: HisenseXIM, coverageAngle: 160, coverageDistance: 12, _meta: { pixelPitch: "P1.86", tech: "SMD 1515", type3D: "led-wall-indoor", cabinetW: 0.64, cabinetH: 0.48 } },
  { id: "hisense-xom", category: "Display", type: "display", brand: "Hisense", name: "Hisense XOM Outdoor SMD", width: 2.88, height: 1.92, depth: 0.106, mountOn: "wall", color: "#1a1a1a", accentColor: "#a78bfa", image: HisenseXOM, coverageAngle: 160, coverageDistance: 35, _meta: { pixelPitch: "P5", tech: "SMD Gold Wire IP65", type3D: "led-wall-outdoor", cabinetW: 0.96, cabinetH: 0.96 } },
  { id: "hisense-xomfe", category: "Display", type: "display", brand: "Hisense", name: "Hisense XOMFE Outdoor IP66", width: 2.88, height: 1.92, depth: 0.088, mountOn: "wall", color: "#111111", accentColor: "#a78bfa", image: HisenseXOMFE, coverageAngle: 155, coverageDistance: 40, _meta: { pixelPitch: "P5.7~P10", tech: "SMD IP66 billboard", type3D: "led-wall-outdoor", cabinetW: 0.96, cabinetH: 0.64 } },
  { id: "aver-cam-540", category: "Conference", type: "camera", brand: "AVer", name: "AVer CAM540", width: 0.165, height: 0.149, depth: 0.165, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: AverCAM540, coverageAngle: 85, coverageDistance: 7 },
  { id: "aver-cam-570", category: "Conference", type: "camera", brand: "AVer", name: "AVer CAM570", width: 0.1708, height: 0.1905, depth: 0.173, mountOn: "wall", color: "#1e1e1e", accentColor: "#3b82f6", image: AverCam570, coverageAngle: 95, coverageDistance: 10, _meta: { zoom: "36X total (12X optical + 3X digital)", aiLensFOV: "95°", wideFOV: "DFOV 85° / HFOV 76° / VFOV 46°", weight: "2.1kg (4.63 lbs)", tracking: "Dual-lens AI audio tracking, beamforming mic array" } },
  { id: "aver-cam-520pro3", category: "Conference", type: "camera", brand: "AVer", name: "AVer CAM520 Pro3", width: 0.155, height: 0.143, depth: 0.155, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: AverCAM520P3, coverageAngle: 80.5, coverageDistance: 6 },
  { id: "aver-cam-130", category: "Conference", type: "camera", brand: "AVer", name: "AVer CAM130", width: 0.119, height: 0.068, depth: 0.058, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: AverCAM130, coverageAngle: 120, coverageDistance: 5 },
  { id: "aver-vc-550", category: "Conference", type: "camera", brand: "AVer", name: "AVer VC550", width: 0.1708, height: 0.1905, depth: 0.173, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: AverVC550, coverageAngle: 85, coverageDistance: 7 },
  { id: "aver-vc-540", category: "Conference", type: "camera", brand: "AVer", name: "AVer VC540", width: 0.165, height: 0.149, depth: 0.165, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: AverVC540, coverageAngle: 85, coverageDistance: 6 },
  { id: "aver-vc-520pro3", category: "Conference", type: "camera", brand: "AVer", name: "AVer VC520 Pro3", width: 0.155, height: 0.143, depth: 0.155, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: AverVC520P3, coverageAngle: 80.5, coverageDistance: 5 },
  { id: "extron-ff120t", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron FF 120T", width: 0.305, height: 0.083, depth: 0.61, mountOn: "ceiling", color: "#f0f0f0", accentColor: "#f59e0b", image: ExtronFF120T, coverageAngle: 340, coverageDistance: 6, _meta: { tech: "Flat Field® 70/100V", driver: "3\" horn-loaded", freqRange: "68Hz–18kHz", dispersion: "170° conical", power: "16W", type3D: "ceiling-tile-speaker", tileW: 0.305, tileD: 0.61 } },
  { id: "extron-ff220t", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron FF 220T", width: 0.603, height: 0.083, depth: 0.603, mountOn: "ceiling", color: "#f0f0f0", accentColor: "#f59e0b", image: ExtronFF220T, coverageAngle: 340, coverageDistance: 8, _meta: { tech: "Flat Field® 70/100V", driver: "3\" horn-loaded", freqRange: "68Hz–18kHz", dispersion: "170° conical", power: "16W (32W program)", type3D: "ceiling-tile-speaker", tileW: 0.603, tileD: 0.603 } },
  { id: "extron-sf26ctlp", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron SF 26CT LP", width: 0.270, height: 0.107, depth: 0.270, mountOn: "ceiling", color: "#f0f0f0", accentColor: "#f59e0b", image: ExtronSF26CTLP, coverageAngle: 95, coverageDistance: 5, _meta: { tech: "SoundField XD 2-way 70/100V Low Profile", driver: "6.5\" woofer + ¾\" dome tweeter", freqRange: "85Hz–22kHz", dispersion: "95° conical", power: "130W program", type3D: "ceiling-round-speaker", backCanDepth: 0.107, driverSize: 0.165, } },
  { id: "extron-sf8ctsub", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron SF 8CT SUB", width: 0.324, height: 0.367, depth: 0.324, mountOn: "ceiling", color: "#e8e8e8", accentColor: "#f59e0b", image: ExtronSF8CTSub, coverageAngle: 360, coverageDistance: 6, _meta: { tech: "SoundField 8\" In-Ceiling Subwoofer 70/100V", driver: "8\" long-throw woofer", freqRange: "45Hz–300Hz", dispersion: "Omnidirectional (subwoofer)", power: "200W program", type3D: "ceiling-round-sub", backCanDepth: 0.367, driverSize: 0.203, } },
  { id: "extron-sf26ct", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron SF 26CT", width: 0.270, height: 0.203, depth: 0.270, mountOn: "ceiling", color: "#f0f0f0", accentColor: "#f59e0b", image: ExtronSF26CT, coverageAngle: 111, coverageDistance: 6, _meta: { tech: "SoundField XD 2-way 70/100V", driver: "6.5\" woofer + ¾\" PEI dome tweeter", freqRange: "65Hz–22kHz", dispersion: "111° conical", power: "130W program", type3D: "ceiling-round-speaker", backCanDepth: 0.203, driverSize: 0.165, } },
  { id: "extron-sf26x", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron SF 26X", width: 0.193, height: 0.090, depth: 0.193, mountOn: "ceiling", color: "#f2f2f2", accentColor: "#f59e0b", image: ExtronSF26X, coverageAngle: 107, coverageDistance: 5, _meta: { tech: "SoundField 2-way Open Back 8 Ohm", driver: "6.5\" woofer + ¾\" dome tweeter pivoting", freqRange: "50Hz–20kHz", dispersion: "107° conical", power: "50W program", type3D: "ceiling-round-openback", backCanDepth: 0, driverSize: 0.165, } },
  { id: "extron-sf3clp", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron SF 3C LP", width: 0.226, height: 0.102, depth: 0.226, mountOn: "ceiling", color: "#f0f0f0", accentColor: "#f59e0b", image: ExtronSF3CLP, coverageAngle: 165, coverageDistance: 4, _meta: { tech: "SoundField XD 3\" Full-Range 8 Ohm LP", driver: "3\" paper cone", freqRange: "65Hz–20kHz", dispersion: "165° conical", power: "32W program", type3D: "ceiling-round-small", backCanDepth: 0.102, driverSize: 0.076, } },
  { id: "extron-sf3ctlp", category: "Speaker", type: "speaker", brand: "Extron", name: "Extron SF 3CT LP", width: 0.226, height: 0.102, depth: 0.226, mountOn: "ceiling", color: "#f0f0f0", accentColor: "#f59e0b", image: ExtronSF3CTLP, coverageAngle: 165, coverageDistance: 4, _meta: { tech: "SoundField XD 3\" Full-Range 70/100V LP", driver: "3\" paper cone", freqRange: "65Hz–20kHz", dispersion: "165° conical", power: "32W program", type3D: "ceiling-round-small", backCanDepth: 0.102, driverSize: 0.076, } },
  { id: "cisco-table-mic-pro", category: "Microphone", type: "microphone", brand: "Cisco", name: "Cisco Table Microphone Pro", width: 0.098, height: 0.029, depth: 0.098, mountOn: "table", color: "#1a1a1a", accentColor: "#22c55e", image: CiscoTableMicPro, coverageAngle: 360, coverageDistance: 1.5, _meta: { tech: "4-element array, Audio over IP, AES-128 encrypted", connector: "Ethernet RJ-45", type3D: "table-mic-round", colors: "First Light / Carbon Black", } },
  { id: "cisco-table-mic", category: "Microphone", type: "microphone", brand: "Cisco", name: "Cisco Table Microphone", width: 0.102, height: 0.025, depth: 0.102, mountOn: "table", color: "#f0f0f0", accentColor: "#22c55e", image: CiscoTableMic, coverageAngle: 360, coverageDistance: 1.5, _meta: { tech: "Omnidirectional boundary layer, Analog", connector: "4-pin mini jack / Euroblock", type3D: "table-mic-round-analog", colors: "White", } },
  { id: "cisco-ceiling-mic-pro", category: "Microphone", type: "microphone", brand: "Cisco", name: "Cisco Ceiling Microphone Pro", width: 0.220, height: 0.044, depth: 0.220, mountOn: "ceiling", color: "#f5f5f5", accentColor: "#22c55e", image: CiscoCeilingMicPro, coverageAngle: 360, coverageDistance: 7.5, _meta: { tech: "64-element array, 8 AI adaptive beams, Audio over IP", connector: "Ethernet RJ-45 (PoE)", type3D: "ceiling-mic-round", colors: "Arctic White / Carbon Black" } },
  { id: "cisco-desk-cam-1080", category: "Conference", type: "camera", brand: "Cisco", name: "Cisco Desk Camera 1080p", width: 0.0906, height: 0.0195, depth: 0.041, mountOn: "table", color: "#1a1a1a", accentColor: "#3b82f6", image: CiscoDeskCam1080, coverageAngle: 83, coverageDistance: 4, _meta: { tech: "1080p Full HD, 8MP sensor, 4x digital zoom", connector: "USB-C 2.0", type3D: "webcam-bar", colors: "Carbon Black / Platinum", fov: "83° max (adjustable)", features: "Privacy shutter, dual noise-reduction mic, clip+tripod mount", } },
  { id: "cisco-desk-cam-4k", category: "Conference", type: "camera", brand: "Cisco", name: "Cisco Desk Camera 4K", width: 0.0906, height: 0.0195, depth: 0.041, mountOn: "table", color: "#1a1a1a", accentColor: "#3b82f6", image: CiscoDeskCam4K, coverageAngle: 83, coverageDistance: 5, _meta: { tech: "4K UHD 30fps / 1080p 60fps, 12MP, 5x digital zoom, HDR", connector: "USB-C 3.1", type3D: "webcam-bar-4k", colors: "Carbon Black / Platinum", fov: "83° max", features: "Privacy shutter, dual high-sensitivity mic, tripod mount", weight: "153g", } },
  { id: "cisco-room-vision-ptz", category: "Conference", type: "camera", brand: "Cisco", name: "Cisco Room Vision PTZ", width: 0.165, height: 0.221, depth: 0.176, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: CiscoRoomVisionPTZ, coverageAngle: 75, coverageDistance: 12, _meta: { tech: "4K UHD, AI presenter tracking, silent optical PTZ", connector: "Ethernet PoE+ (IEEE802.3at) + HDMI", type3D: "ptz-dome-ai", colors: "Carbon Black / Arctic White", fov: "~75° wide (optical zoom), Pan ±170°, Tilt -20° to +90°", features: "AI head tracking, PresenterTrack, multi-camera, Control Hub", weight: "2.36kg", } },
  { id: "cisco-quad-camera", category: "Conference", type: "camera", brand: "Cisco", name: "Cisco Quad Camera", width: 0.950, height: 0.104, depth: 0.119, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: CiscoQuadCamera, coverageAngle: 83, coverageDistance: 9, _meta: { tech: "4x CMOS sensors, up to 5K (5120×2880), 7x digital zoom", connector: "HDMI + Ethernet", type3D: "video-bar-quad", colors: "Carbon Black", fov: "83° wide (main) + 3×50° telephoto", features: "SpeakerTrack 60, GroupFraming, AI switching, fixed-lens", sensors: "1× 83° wide + 3× 50° telephoto lenses", } },
  { id: "cisco-ptz-4k", category: "Conference", type: "camera", brand: "Cisco", name: "Cisco PTZ 4K Camera", width: 0.160, height: 0.178, depth: 0.203, mountOn: "wall", color: "#1a1a1a", accentColor: "#3b82f6", image: CiscoPTZ4K, coverageAngle: 70, coverageDistance: 15, _meta: { tech: "4K UHD 30fps, 20x optical + 12x digital zoom", connector: "HDMI 2.0 + Ethernet control", type3D: "ptz-yoke-4k", colors: "Carbon Black", fov: "70° horizontal, Pan ±170°, Tilt -20° to +90°", features: "AI face/body framing, PresenterTrack, VISCA-over-IP", weight: "~1.5kg", } },

  // ── CRESTRON AIRMEDIA — wireless presentation receiver/gateway.
  // Bukan kamera/mic/speaker/display, jadi type-nya "receiver" (bukan
  // salah satu dari 4 type baku) — tetap dikelompokkan di kategori
  // "Conference" karena satu family produk "Presentation and
  // Conferencing" Crestron. Konsekuensinya: tidak muncul di hitungan
  // footer Cameras/Mics/dst (memang bukan salah satu itu), tapi tetap
  // tampil normal di sidebar katalog, daftar placed devices, & PDF
  // export. Tidak ada file .glb tersedia — geometri 3D dibuat manual
  // dari primitive three.js (lihat pid "crestron-am3000wf"/"crestron-
  // am3100wf"/"crestron-am3200" di addDevice()). coverageDistance
  // dipakai merepresentasikan perkiraan jangkauan Wi-Fi (omnidirectional),
  // bukan FOV/coverage audio seperti kamera/mic/speaker.
  { id: "crestron-am3000wf", category: "Conference", type: "receiver", brand: "Crestron", name: "Crestron AM-3000-WF AirMedia Receiver 3000", width: 0.127, height: 0.031, depth: 0.127, mountOn: "table", color: "#161616", accentColor: "#3b82f6", image: null, coverageAngle: 360, coverageDistance: 30, _meta: { tech: "AirMedia wireless presentation, welcome screen, XiO Cloud, digital signage (Appspace)", connectivity: "Wi-Fi dual-band 802.11a/b/g/n/ac/ax + Ethernet 100/1000 (butuh access point Wi-Fi eksternal terpisah)", output: "HDMI (HDCP 2.2) hingga 4K UHD 60Hz", chassis: "Plastik hitam, 4 lubang mounting, ventilasi sisi", weight: "±330g (chassis sama dengan AM-3100-WF, belum ada data berat resmi terpisah)", note: "Perkiraan jangkauan Wi-Fi ~30m (100ft), tergantung access point & kondisi lokasi" } },
  { id: "crestron-am3100wf", category: "Conference", type: "receiver", brand: "Crestron", name: "Crestron AM-3100-WF AirMedia Receiver 3100", width: 0.127, height: 0.031, depth: 0.127, mountOn: "table", color: "#161616", accentColor: "#3b82f6", image: null, coverageAngle: 360, coverageDistance: 30, _meta: { tech: "AirMedia wireless presentation + fitur tambahan dibanding AM-3000-WF (lihat datasheet Crestron untuk detail lengkap perbedaan fitur)", connectivity: "Wi-Fi dual-band 802.11a/b/g/n/ac/ax (~30m/100ft @80Mbps) + Ethernet 100/1000, Bluetooth 5.0", output: "HDMI (HDCP 2.2) hingga 4K UHD 60Hz", chassis: "Plastik hitam, 4 lubang mounting, ventilasi sisi, freestanding/surface mount", dimensions: "127×127×31mm (5×5×1.21in)", weight: "330g (11.64oz)" } },
  { id: "crestron-am3200", category: "Conference", type: "receiver", brand: "Crestron", name: "Crestron AM-3200 AirMedia Receiver 3200", width: 0.188, height: 0.033, depth: 0.177, mountOn: "table", color: "#161616", accentColor: "#3b82f6", image: null, coverageAngle: 360, coverageDistance: 30, _meta: { tech: "AirMedia wireless presentation tier tertinggi — HDMI input tambahan (dual HDMI in/out), AirMedia Canvas multi-source", connectivity: "Wi-Fi dual-band 802.11a/b/g/n/ac/ax (antena eksternal) + Gigabit Ethernet PoE", output: "Dual HDMI in/out (HDCP 2.2) hingga 4K UHD", chassis: "Plastik/metal hitam, rack-mountable, rail-mountable, desktop, 2 antena eksternal", dimensions: "188×177×33mm (7.4×6.93×1.26in)", weight: "±862g (1.90lb)" } },

  // ── EXTRON CABLE CUBBY SERIES — cable/power access enclosure yang
  // dibenamkan (recessed) ke lubang meja. category "Connectivity" (baru,
  // sebelumnya belum ada di kategori bawaan) & type "connectivity" (baru).
  // PENTING soal field height: diisi ketebalan LID yang kelihatan DI ATAS
  // meja (beberapa mm), BUKAN "enclosure depth" resmi Extron (itu
  // kedalaman DI BAWAH meja, tersembunyi). Nilai resmi disimpan di
  // _meta.trueDepthBelowSurface untuk referensi teknis instalasi.
  // coverageAngle/coverageDistance diisi nilai minimal (tidak relevan
  // secara fungsional — cable cubby tidak punya FOV/jangkauan sinyal).
  { id: "extron-cablecubby100", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 100", width: 0.132, height: 0.008, depth: 0.132, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Round furniture-mount cable/power access enclosure, tool-free install, soft-close lid", capacity: "2 single-space MAAP modules, 1 Retractor, atau 3 kabel AV (non-powered hingga 4 MAAP)", cutout: "4in (102mm) hole saw, bundar", topBezelDiameter: "132mm (5.2in)", trueDepthBelowSurface: "74mm (2.9in) — di bawah meja, tidak terlihat di ruangan", finish: "Black anodized / brushed aluminum", type3D: "cablecubby-round" } },
  { id: "extron-cablecubby1252ms", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 1252 MS", width: 0.269, height: 0.003, depth: 0.150, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Matching-surface enclosure — lid jadi dudukan material kayu/batu senada meja, nyaris tak terlihat saat tertutup, bezel-free height-adjustable", capacity: "AC/AC+USB power module + kombinasi kabel AV/Retractor/AAP", finish: "Lid custom mengikuti material meja (bukan logam polos)", note: "Dimensi width/depth ESTIMASI mendekati keluarga Cable Cubby 1202/1402 — spesifikasi presisi resmi tidak ditemukan di datasheet publik Extron saat pencarian; verifikasi ke datasheet resmi sebelum dipakai untuk pengukuran presisi", type3D: "cablecubby-single" } },
  { id: "extron-cablecubby202", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 202", width: 0.203, height: 0.010, depth: 0.112, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "1 AC outlet fixed unswitched + Cable Pass-Through Bracket hingga 4 kabel AV, tilt-up lid dengan magnetic catch", cutout: "178×82mm (7.00×3.25in)", topPlate: "203×112mm (7.99×4.39in)", trueDepthBelowSurface: "81mm (3.20in) — di bawah meja, tidak terlihat di ruangan", finish: "Black powder coat", type3D: "cablecubby-single" } },
  { id: "extron-cablecubby500", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 500", width: 0.162, height: 0.010, depth: 0.162, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Modular cable access enclosure, integrated clamp system tool-free, mendukung AC/AC+USB power module + Retractor/AV cable/AAP", cutout: "146×146mm (5.75×5.75in)", topPlate: "162×162mm (6.38×6.38in) persegi", trueDepthBelowSurface: "164mm (6.46in) — di bawah meja, tidak terlihat di ruangan", finish: "Black anodized / brushed aluminum", type3D: "cablecubby-single" } },
  { id: "extron-cablecubby650ut", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 650 UT", width: 0.178, height: 0.005, depth: 0.165, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Under-table mounted, desain terbuka (bukan lid metal baku) — lid table-matching dibuat custom oleh tukang kayu instalasi", capacity: "3 AAP atau 8 kabel AV + 3 Retractor Series/2, bisa digabung (ganged) antar unit", cutout: "127×108mm (5.00×4.25in)", topPlate: "178×165mm (7.00×6.50in)", trueDepthBelowSurface: "102mm (4.00in) — di bawah meja, tidak terlihat di ruangan", finish: "Black metal frame, lid mengikuti material meja", type3D: "cablecubby-open" } },
  { id: "extron-cablecubby700", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 700", width: 0.198, height: 0.010, depth: 0.162, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Mid-size modular cable access enclosure, integrated clamp system tool-free, mendukung AC/AC+USB power module + Retractor/AV cable/AAP", cutout: "181×146mm (7.13×5.75in)", topPlate: "198×162mm (7.79×6.38in)", trueDepthBelowSurface: "164mm (6.46in) — di bawah meja, tidak terlihat di ruangan", finish: "Black anodized / brushed aluminum", type3D: "cablecubby-single" } },
  { id: "extron-cablecubby1202", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 1202", width: 0.269, height: 0.008, depth: 0.116, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Slim low-profile enclosure (2in lebih pendek dari model sebelumnya), lid damper halus & senyap, full-width cable pass-through", cutout: "254×102mm (10.00×4.00in)", topPlate: "269×116mm (10.57×4.58in)", trueDepthBelowSurface: "113mm (4.46in) — di bawah meja, tidak terlihat di ruangan", finish: "Black anodized / brushed aluminum", type3D: "cablecubby-single" } },
  { id: "extron-cablecubby1402", category: "Connectivity", type: "connectivity", brand: "Extron", name: "Extron Cable Cubby 1402", width: 0.269, height: 0.008, depth: 0.189, mountOn: "table", color: "#1c1c1c", accentColor: "#64748b", image: null, coverageAngle: 1, coverageDistance: 0.1, _meta: { tech: "Double-sided enclosure — 2 sisi konektivitas independen, masing-masing mendukung 1 AC/AC+USB power module + Retractor/AV cable/AAP", cutout: "254×172mm (10.00×6.75in)", topPlate: "269×189mm (10.57×7.42in)", trueDepthBelowSurface: "113mm (4.41in) — di bawah meja, tidak terlihat di ruangan", finish: "Black anodized / brushed aluminum", type3D: "cablecubby-double" } },

  { id: "crestron-ic4t", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros IC4T 4\" Ceiling Speaker", width: 0.165, height: 0.10, depth: 0.165, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronIC4T, coverageAngle: 110, coverageDistance: 4, _meta: { driver: "4in woofer + dome tweeter", power: "30W program", dispersion: "110° nominal coverage pattern" } },
  { id: "crestron-ic6t", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros IC6T 6.5\" Ceiling Speaker", width: 0.224, height: 0.115, depth: 0.224, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronIC6T, coverageAngle: 100, coverageDistance: 5, _meta: { driver: "6.5in woofer + 0.98in titanium dome tweeter", power: "125W program @ 8Ω", dispersion: "100° nominal coverage pattern", freqResponse: "50Hz-20kHz ±3dB" } },
  { id: "crestron-ic8t", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros IC8T 8\" Ceiling Speaker", width: 0.305, height: 0.13, depth: 0.305, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronIC8T, coverageAngle: 95, coverageDistance: 6, _meta: { driver: "8in polypropylene woofer + 1in titanium dome", power: "150W program", dispersion: "95° conical (nominal)", backcanDia: "12in (305mm)" } },
  { id: "crestron-ici4t", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros ICI4T Integrator 4\" Speaker", width: 0.206, height: 0.149, depth: 0.206, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronICI4T, coverageAngle: 120, coverageDistance: 4, _meta: { driver: "4in woofer + 0.79in dome tweeter", power: "30W program", dispersion: "120° conical (nominal)", tier: "cost-efficient" } },
  { id: "crestron-ici6t", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros ICI6T Integrator 6.5\" Speaker", width: 0.253, height: 0.219, depth: 0.253, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronICI6T, coverageAngle: 110, coverageDistance: 5, _meta: { driver: "6.5in woofer + 3/4in dome tweeter", power: "50W program", dispersion: "110° nominal coverage pattern", dia: "9.95in (253mm)", depth: "8.63in (219mm)" } },
  { id: "crestron-ict5t22", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros ICT5T-22 Tile Speaker (2x2)", width: 0.603, height: 0.102, depth: 0.603, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronICT5T22, coverageAngle: 120, coverageDistance: 4, _meta: { format: "2ft x 2ft drop ceiling tile", height: "4in (102mm) low-profile", dispersion: "120° beamwidth", power: "30W LoZ" } },
  { id: "crestron-ic6ulpt", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros IC6ULPT Low-Profile 6.5\"", width: 0.250, height: 0.061, depth: 0.250, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronIC6ULPT, coverageAngle: 140, coverageDistance: 5, _meta: { tier: "ultra low-profile", dispersion: "150° speech / 140° music coverage", power: "35W program", mountDepth: "2.4in (61mm)", forTightPlenum: true } },
  { id: "crestron-ics8t", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros ICS8T In-Ceiling Subwoofer", width: 0.287, height: 0.14, depth: 0.287, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronICS8T, coverageAngle: 360, coverageDistance: 4, _meta: { type: "subwoofer", power: "100W program @ 8Ω", freqResponse: "45Hz-125Hz ±3dB, usable to 35Hz", cutout: "11-5/16in (287mm)" } },
  { id: "crestron-ic4p", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros IC4P Ceiling Speaker (BMR)", width: 0.165, height: 0.10, depth: 0.165, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: CrestronIC4P, coverageAngle: 150, coverageDistance: 4, _meta: { format: "flush ceiling-mount (bukan pendant)", driver: "single 2.83in (72mm) Balanced Mode Radiator (BMR)", freqRange: "90Hz-30kHz", dispersion: "broad coverage, melebihi conical woofer biasa" } },
  { id: "crestron-ref-ic6", category: "Speaker", type: "speaker", brand: "Crestron", name: "Reference IC6 2-Way Speaker", width: 0.246, height: 0.091, depth: 0.246, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: CrestronRefIC6, coverageAngle: 100, coverageDistance: 5, _meta: { tier: "audiophile (Origin Acoustics)", cutout: "9.66in (246mm)", grille: "8.29in (211mm)", depth: "3.58in (91mm)" } },
  { id: "crestron-ref-ic8", category: "Speaker", type: "speaker", brand: "Crestron", name: "Reference IC8 2-Way Speaker", width: 0.308, height: 0.104, depth: 0.308, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: CrestronRefIC8, coverageAngle: 100, coverageDistance: 6, _meta: { tier: "audiophile (Origin Acoustics)", cutout: "12.15in (308mm)", grille: "10.20in (259mm)", depth: "4.09in (104mm)" } },
  { id: "crestron-ult-ic6", category: "Speaker", type: "speaker", brand: "Crestron", name: "Ultimate IC6-AW All-Weather Speaker", width: 0.246, height: 0.094, depth: 0.246, mountOn: "ceiling", color: "#e0ded9", accentColor: "#f59e0b", image: CrestronUltIC6, coverageAngle: 100, coverageDistance: 5, _meta: { tier: "premium all-weather (IP66)", power: "150W program / 300W peak", freqRange: "52Hz-30kHz", depth: "3.70in (94mm)" } },
  { id: "crestron-ult-ic8", category: "Speaker", type: "speaker", brand: "Crestron", name: "Ultimate IC8-AW All-Weather Speaker", width: 0.308, height: 0.118, depth: 0.308, mountOn: "ceiling", color: "#e0ded9", accentColor: "#f59e0b", image: CrestronUltIC8, coverageAngle: 100, coverageDistance: 6, _meta: { tier: "premium all-weather", rated: "outdoor/indoor" } },
  { id: "crestron-ult-ics8", category: "Speaker", type: "speaker", brand: "Crestron", name: "Ultimate ICS8-AW Subwoofer", width: 0.308, height: 0.145, depth: 0.308, mountOn: "ceiling", color: "#e0ded9", accentColor: "#f59e0b", image: CrestronUltICS8, coverageAngle: 360, coverageDistance: 5, _meta: { type: "subwoofer", tier: "premium all-weather" } },
  { id: "crestron-ult-ic8dt", category: "Speaker", type: "speaker", brand: "Crestron", name: "Ultimate IC8-DT Dual-Tweeter Speaker", width: 0.308, height: 0.118, depth: 0.308, mountOn: "ceiling", color: "#e0ded9", accentColor: "#f59e0b", image: CrestronUltIC8DT, coverageAngle: 110, coverageDistance: 6, _meta: { tier: "premium all-weather", config: "dual-tweeter stereo" } },
  { id: "crestron-sb200", category: "Speaker", type: "speaker", brand: "Crestron", name: "Saros SB-200-P Sound Bar", width: 1.031, height: 0.106, depth: 0.076, mountOn: "wall", color: "#1a1a1a", accentColor: "#f59e0b", image: CrestronSB200, coverageAngle: 130, coverageDistance: 5, _meta: { power: "20W/channel", format: "soundbar under display", inputs: "5-pin terminal + 2xRCA" } },
  // ── BOSE PROFESSIONAL DESIGNMAX (ceiling) ──
  { id: "bose-dm8c", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM8C Ceiling Speaker", width: 0.409, height: 0.255, depth: 0.409, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseDM8C, coverageAngle: 91, coverageDistance: 7, _meta: { driver: "8in woofer + 1in coaxial compression driver", power: "150W (8Ω) / 80W (70/100V)", freqRange: "52Hz-20kHz", dispersion: "91° (Bose Design Guide)", cutout: "349mm (13.75in)", backcan: "340x240mm" } },
  { id: "bose-dm8c-sub", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM8C-SUB In-Ceiling Subwoofer", width: 0.409, height: 0.255, depth: 0.409, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseDM8CSub, coverageAngle: 360, coverageDistance: 6, _meta: { type: "subwoofer", driver: "8in woofer", power: "180W", freqRange: "38Hz-155Hz", cutout: "349mm (13.75in)" } },
  { id: "bose-dm6c", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM6C Ceiling Speaker", width: 0.356, height: 0.239, depth: 0.356, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseDM6C, coverageAngle: 88, coverageDistance: 6, _meta: { driver: "6.5in woofer + 1.25in coaxial tweeter", power: "100W (8Ω) / 80W (70/100V)", freqRange: "59Hz-20kHz", dispersion: "88° (Bose Design Guide)", cutout: "292mm (11.5in)", backcan: "287x223mm" } },
  { id: "bose-dm5c", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM5C Ceiling Speaker", width: 0.303, height: 0.205, depth: 0.303, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseDM5C, coverageAngle: 87, coverageDistance: 5, _meta: { driver: "5.25in woofer + 1in coaxial tweeter", power: "50W (60W marketing spec)", freqRange: "65Hz-20kHz", dispersion: "87° (Bose Design Guide)", backcan: "243x192mm" } },
  { id: "bose-dm3c", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM3C Ceiling Speaker", width: 0.255, height: 0.154, depth: 0.255, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseDM3C, coverageAngle: 83, coverageDistance: 4, _meta: { driver: "3.5in woofer + 0.75in coaxial tweeter", power: "25W (30W marketing spec)", freqRange: "75Hz-20kHz", dispersion: "83° (Bose Design Guide)", backcan: "203x144mm" } },
  { id: "bose-dm2c-lp", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM2C-LP Low-Profile Speaker", width: 0.198, height: 0.110, depth: 0.198, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseDM2CLP, coverageAngle: 90, coverageDistance: 3.5, _meta: { driver: "2.25in transducer", power: "16W (20W marketing spec)", freqRange: "85Hz-19kHz", backcan: "155x99mm low-profile" } },
  // ── BOSE PROFESSIONAL FREESPACE (ceiling) ──
  { id: "bose-fs4ce", category: "Speaker", type: "speaker", brand: "Bose", name: "FreeSpace FS4CE In-Ceiling Speaker", width: 0.272, height: 0.172, depth: 0.272, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseFS4CE, coverageAngle: 88, coverageDistance: 5, _meta: { driver: "4.5in driver", power: "40W (Bose Design Guide)", freqRange: "70Hz-17kHz", dispersion: "88° (Bose Design Guide)", rated: "IP55 outdoor/wet", backcan: "220x163mm" } },
  { id: "bose-fs2c", category: "Speaker", type: "speaker", brand: "Bose", name: "FreeSpace FS2C In-Ceiling Speaker", width: 0.198, height: 0.110, depth: 0.198, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseFS2C, coverageAngle: 86, coverageDistance: 3.5, _meta: { driver: "2.25in woofer", power: "16W (Bose Design Guide)", freqRange: "83Hz-19kHz", dispersion: "86° (Bose Design Guide)" } },
  { id: "bose-fs3-satellite", category: "Speaker", type: "speaker", brand: "Bose", name: "FreeSpace 3 Flush-Mount Satellite", width: 0.157, height: 0.123, depth: 0.157, mountOn: "ceiling", color: "#f0eee9", accentColor: "#f59e0b", image: BoseFS3Sat, coverageAngle: 170, coverageDistance: 4, _meta: { driver: "2.5in full-range driver", dispersion: "170° conical (nominal avg, -6dB)", cutout: "127mm (5.0in)" } },
  { id: "bose-fs3-acoustimass", category: "Speaker", type: "speaker", brand: "Bose", name: "FreeSpace 3 Acoustimass Bass Module", width: 0.356, height: 0.192, depth: 0.356, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: BoseFS3Sub, coverageAngle: 360, coverageDistance: 5, _meta: { type: "subwoofer module (omnidirectional below 200Hz)", driver: "5.25in woofer (133mm)", freqLow: "40Hz", mount: "flush/ceiling/pendant/wall" } },
  // ── BOSE PROFESSIONAL PENDANT SERIES ──
  { id: "bose-dm6pe", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM6PE Pendant Speaker", width: 0.300, height: 0.300, depth: 0.300, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: BoseDM6PE, coverageAngle: 130, coverageDistance: 6, _meta: { format: "pendant", driver: "6.5in coaxial woofer + 1.25in tweeter", power: "125W", freqRange: "62Hz-20kHz", rated: "IP55 outdoor" } },
  { id: "bose-dml88p", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax Luna DML88P Pendant Speaker", width: 0.360, height: 0.220, depth: 0.360, mountOn: "ceiling", color: "#1a1a1a", accentColor: "#f59e0b", image: BoseDML88P, coverageAngle: 140, coverageDistance: 8, _meta: { format: "pendant Ring Array", driver: "8x 2in mid-high + 1x 8in woofer", maxSPL: "111dB", freqLow: "40Hz", coverage: "140° consistent" } },
  { id: "bose-dm5p", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM5P Pendant Speaker", width: 0.159, height: 0.299, depth: 0.159, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: BoseDM5P, coverageAngle: 120, coverageDistance: 5, _meta: { format: "pendant", driver: "5.25in coaxial woofer + 1in tweeter", power: "60W", freqRange: "65Hz-20kHz", height: "299mm", topDia: "159mm" } },
  { id: "bose-dm3p", category: "Speaker", type: "speaker", brand: "Bose", name: "DesignMax DM3P Pendant Speaker", width: 0.140, height: 0.260, depth: 0.140, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: BoseDM3P, coverageAngle: 115, coverageDistance: 4, _meta: { format: "pendant", driver: "3.5in coaxial woofer + 0.75in tweeter", power: "30W", freqRange: "75Hz-20kHz" } },
  { id: "bose-fs2p", category: "Speaker", type: "speaker", brand: "Bose", name: "FreeSpace FS2P Pendant Speaker", width: 0.130, height: 0.230, depth: 0.130, mountOn: "ceiling", color: "#e8e6e2", accentColor: "#f59e0b", image: BoseFS2P, coverageAngle: 110, coverageDistance: 3.5, _meta: { format: "pendant", driver: "2.25in woofer", power: "20W" } },
];

function clipPolygonToRoom(points, roomWidth, roomDepth) {
  if (points.length === 0) return [];
  const edges = [
    { axis: "x", min: true, val: 0 }, { axis: "x", min: false, val: roomWidth },
    { axis: "z", min: true, val: 0 }, { axis: "z", min: false, val: roomDepth },
  ];
  let poly = points;
  for (const edge of edges) {
    if (poly.length === 0) return [];
    const output = [];
    const n = poly.length;
    for (let i = 0; i < n; i++) {
      const cur = poly[i], nxt = poly[(i + 1) % n];
      const insideCur = edge.axis === "x" ? (edge.min ? cur[0] >= edge.val : cur[0] <= edge.val) : (edge.min ? cur[1] >= edge.val : cur[1] <= edge.val);
      const insideNxt = edge.axis === "x" ? (edge.min ? nxt[0] >= edge.val : nxt[0] <= edge.val) : (edge.min ? nxt[1] >= edge.val : nxt[1] <= edge.val);
      if (insideCur) { output.push(cur); if (!insideNxt) output.push(intersectEdge(cur, nxt, edge)); }
      else if (insideNxt) { output.push(intersectEdge(cur, nxt, edge)); }
    }
    poly = output;
  }
  return poly;
}
function intersectEdge(a, b, edge) {
  const dx = b[0] - a[0], dz = b[1] - a[1];
  if (edge.axis === "x") { const t = dx === 0 ? 0 : (edge.val - a[0]) / dx; return [edge.val, a[1] + t * dz]; }
  else { const t = dz === 0 ? 0 : (edge.val - a[1]) / dz; return [a[0] + t * dx, edge.val]; }
}
function buildClippedCircle(cx, cz, radius, roomWidth, roomDepth, segments = 64) {
  const rawPoints = [];
  for (let i = 0; i < segments; i++) { const angle = (i / segments) * Math.PI * 2; rawPoints.push([cx + Math.cos(angle) * radius, cz + Math.sin(angle) * radius]); }
  return clipPolygonToRoom(rawPoints, roomWidth, roomDepth);
}
function buildClippedCone(originX, originZ, facingAngle, coverageAngle, dist, roomWidth, roomDepth, segments = 64) {
  const halfAngle = (coverageAngle * Math.PI / 180) / 2;
  const rawPoints = [[originX, originZ]];
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * coverageAngle * Math.PI / 180 - halfAngle;
    const lx = Math.sin(t) * dist, lz = Math.cos(t) * dist;
    rawPoints.push([originX + lx * Math.cos(facingAngle) + lz * Math.sin(facingAngle), originZ - lx * Math.sin(facingAngle) + lz * Math.cos(facingAngle)]);
  }
  rawPoints.push([originX, originZ]);
  return clipPolygonToRoom(rawPoints, roomWidth, roomDepth);
}
function polygonToMesh(clippedPoly, yLevel = 0.02) {
  if (clippedPoly.length < 3) return null;
  const positions = [], indices = [];
  const cx = clippedPoly.reduce((s, p) => s + p[0], 0) / clippedPoly.length;
  const cz = clippedPoly.reduce((s, p) => s + p[1], 0) / clippedPoly.length;
  positions.push(cx, yLevel, cz);
  for (const [x, z] of clippedPoly) positions.push(x, yLevel, z);
  const n = clippedPoly.length;
  for (let i = 1; i <= n; i++) indices.push(0, i, i === n ? 1 : i + 1);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices); geo.computeVertexNormals();
  return geo;
}
function polygonToLine(clippedPoly, yLevel = 0.03) {
  if (clippedPoly.length < 2) return null;
  const pts = clippedPoly.map(([x, z]) => new THREE.Vector3(x, yLevel, z));
  pts.push(pts[0].clone());
  return new THREE.BufferGeometry().setFromPoints(pts);
}

// Membangun "selimut" kerucut 3D: menghubungkan satu titik apex (posisi
// device, mis. di plafon/wall) ke setiap titik tepi base polygon (area
// coverage yang sudah di-clip ke batas ruangan, di lantai). Hasilnya adalah
// permukaan miring kerucut yang benar-benar terlihat menyorot dari device
// ke area cakupannya — bukan sekadar bidang flat di lantai.
function buildConeLateralMesh(apex, clippedPoly, baseY) {
  if (clippedPoly.length < 2) return null;
  const positions = [];
  const n = clippedPoly.length;
  // Apex jadi vertex index 0, lalu setiap titik base berurutan
  positions.push(apex.x, apex.y, apex.z);
  for (const [px, pz] of clippedPoly) positions.push(px, baseY, pz);

  const indices = [];
  for (let i = 1; i <= n; i++) {
    const a = i;
    const b = i === n ? 1 : i + 1;
    indices.push(0, a, b);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

// Garis-garis "rib" dari apex ke tepi base, untuk mempertegas kesan kerucut
// (seperti garis rangka payung) — dibuat jarang (tidak ke semua titik base)
// agar tidak terlalu padat secara visual.
function buildConeRibLines(apex, clippedPoly, baseY, ribCount = 8) {
  if (clippedPoly.length < 2) return null;
  const n = clippedPoly.length;
  const step = Math.max(1, Math.floor(n / ribCount));
  const pts = [];
  for (let i = 0; i < n; i += step) {
    const [px, pz] = clippedPoly[i];
    pts.push(apex.clone(), new THREE.Vector3(px, baseY, pz));
  }
  if (pts.length === 0) return null;
  return new THREE.BufferGeometry().setFromPoints(pts);
}


function buildCoverageZone(item, roomWidth, roomDepth) {
  const { product, surface, x, z } = item;
  const dist = product.coverageDistance || 3;
  const color = new THREE.Color(CATEGORY_COVERAGE_COLORS[product.type] || "#3b82f6");
  const group = new THREE.Group();
  const isCeiling = surface === "ceiling";

  // Untuk device di PLAFON: coverage digambar sebagai kerucut 3D sungguhan,
  // apex di posisi device (atas), base di lantai (bawah) — karena yang
  // relevan secara visual adalah area yang "disorot" turun ke ruangan.
  //
  // PENTING — makna coverageAngle untuk device CEILING (lihat diagram
  // speaker ceiling): ini adalah SUDUT PEMBUKAAN KERUCUT (dari sumbu
  // vertikal di bawah speaker ke tepi kerucut), bukan sudut sektor azimuth
  // horizontal. Speaker ceiling memancar simetris ke SEGALA ARAH (360°
  // azimuth) — coverageAngle 90° vs 120° hanya mengubah LEBAR/SEMPIT
  // kerucut itu (radius alasnya), bukan membuatnya jadi sektor sebagian
  // seperti "kue terpotong". Maka untuk ceiling, base SELALU berbentuk
  // lingkaran bundar penuh, radiusnya dihitung dari trigonometri kerucut:
  //   radius = tinggi_jatuh × tan(coverageAngle / 2)
  // lalu dibatasi juga oleh coverageDistance (radius maksimum produk) agar
  // tidak melebihi jangkauan riil speaker.
  //
  // Untuk device di WALL/TABLE/FLOOR: coverage tetap bidang flat SEJAJAR
  // dengan device itu sendiri (sektor directional horizontal seperti
  // kamera PTZ menghadap arah tertentu — ini konteks yang berbeda dari
  // kerucut speaker ceiling, sehingga logika sektor-pie tetap dipakai).
  const flatY = item.y > 0.1 ? item.y : 0.02;
  const baseY = 0.02;
  const apex = new THREE.Vector3(x, Math.max(item.y, baseY + 0.02), z);
  const dropHeight = Math.max(apex.y - baseY, 0.01); // tinggi jatuh dari speaker ke lantai

  const matFill = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.18,
    side: THREE.DoubleSide, depthWrite: false,
  });
  const matSide = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.16,
    side: THREE.DoubleSide, depthWrite: false,
  });
  const matBase = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.22,
    side: THREE.DoubleSide, depthWrite: false,
  });
  const matLine = new THREE.LineBasicMaterial({
    color, transparent: true, opacity: 0.6,
  });
  const matRib = new THREE.LineBasicMaterial({
    color, transparent: true, opacity: 0.3,
  });

  let clipped;

  if (isCeiling) {
    // ── CEILING — SELALU lingkaran bundar penuh (360° azimuth), apapun
    // nilai coverageAngle-nya. coverageAngle hanya mempengaruhi radius. ──
    let radius;
    if (product.coverageAngle >= 300) {
      // Omnidirectional eksplisit — pakai coverageDistance langsung sebagai radius
      radius = dist;
    } else {
      // coverageAngle adalah sudut pembukaan kerucut (vertex angle).
      // radius = tinggi × tan(setengah sudut), dibatasi oleh coverageDistance
      // supaya tidak melebihi jangkauan maksimum produk.
      const halfConeRad = (product.coverageAngle * Math.PI / 180) / 2;
      radius = Math.min(dropHeight * Math.tan(halfConeRad), dist);
    }
    clipped = buildClippedCircle(x, z, radius, roomWidth, roomDepth);

  } else if (product.coverageAngle >= 300) {
    // Omnidirectional pada wall/table/floor — lingkaran penuh, pan tidak berpengaruh
    clipped = buildClippedCircle(x, z, dist, roomWidth, roomDepth);

  } else {
    // Directional pada wall/table/floor — sektor horizontal (facing angle + pan)
    let baseFacingAngle = 0;
    let ox = x, oz = z;

    if (surface === "wall-back") {
      baseFacingAngle = 0;             // menghadap +Z (ke dalam ruangan)
      ox = x; oz = 0;
    } else if (surface === "wall-left") {
      baseFacingAngle = Math.PI / 2;   // menghadap +X
      ox = 0; oz = z;
    } else if (surface === "wall-right") {
      baseFacingAngle = -Math.PI / 2;  // menghadap -X
      ox = roomWidth; oz = z;
    } else if (surface === "wall-front") {
      baseFacingAngle = Math.PI;       // menghadap -Z
      ox = x; oz = roomDepth;
    } else {
      // floor / table — default menghadap ke wall-back
      baseFacingAngle = 0;
      ox = x; oz = z;
    }

    // Pan dari slider (derajat → radian), default 0
    const panRad = ((item.rotation?.pan ?? 0) * Math.PI) / 180;
    const facingAngle = baseFacingAngle + panRad;

    clipped = buildClippedCone(ox, oz, facingAngle, product.coverageAngle, dist, roomWidth, roomDepth);
  }

  if (clipped.length < 3) return group;

  if (isCeiling) {
    // ── MODE CONE 3D — khusus device di plafon ──

    // Selimut kerucut — permukaan miring dari apex (device) ke tepi base
    const sideGeo = buildConeLateralMesh(apex, clipped, baseY);
    if (sideGeo) group.add(new THREE.Mesh(sideGeo, matSide));

    // Alas kerucut — bidang datar di lantai (area cakupan akhir)
    const baseGeo = polygonToMesh(clipped, baseY);
    if (baseGeo) group.add(new THREE.Mesh(baseGeo, matBase));

    // Outline tepi alas (garis tegas di lantai)
    const baseLineGeo = polygonToLine(clipped, baseY + 0.005);
    if (baseLineGeo) group.add(new THREE.Line(baseLineGeo, matLine));

    // Rib lines — garis dari apex ke tepi base, mempertegas bentuk kerucut
    const ribGeo = buildConeRibLines(apex, clipped, baseY, 10);
    if (ribGeo) group.add(new THREE.LineSegments(ribGeo, matRib));

    // Titik apex kecil menandai sumber asal coverage
    if (apex.y > baseY + 0.05) {
      const apexDot = new THREE.Mesh(
        new THREE.SphereGeometry(0.025, 12, 10),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5 })
      );
      apexDot.position.copy(apex);
      group.add(apexDot);
    }

  } else {
    // ── MODE FLAT — wall/table/floor, sejajar dengan ketinggian device (seperti semula) ──
    const fillGeo = polygonToMesh(clipped, flatY);
    if (fillGeo) group.add(new THREE.Mesh(fillGeo, matFill));

    const lineGeo = polygonToLine(clipped, flatY + 0.01);
    if (lineGeo) group.add(new THREE.Line(lineGeo, matLine));
  }

  return group;
}

/* ================= 3D SCENE MANAGER ================= */
class RoomScene {
  constructor(canvas, room) {
    this.canvas = canvas;
    this.room = room;
    this.objects3D = new Map();
    this.coverageZones = new Map();
    this.furnitureGroups = new Map();
    this.surfaces = [];
    this.furnitureSurfaces = [];
    this.ghostMesh = null;
    this.pendingProduct = null;
    this.selectedId = null;
    this.showCoverage = false;
    this.isDraggingFurniture = false;
    this.draggingFurnitureId = null;
    this.furnitureDragOffset = new THREE.Vector3();
    this._roomGroup = new THREE.Group();
    this._lightGroup = new THREE.Group();
    this._placedItemsRef = [];
    this._spinnerTicks = new Map(); // itemId -> tick(deltaSeconds) untuk loading spinner model .glb
    this._lastFrameTime = performance.now();
    // Model 3D custom untuk meja & kursi (diisi via setFurnitureModels()
    // setelah fetch pengaturan dari Supabase). null/kosong = pakai
    // geometri three.js bawaan (box meja + kursi kantor prosedural).
    this.furnitureModels = { tableUrl: null, tableScale: 1, tableRotationY: 0, chairUrl: null, chairScale: 1, chairRotationY: 0 };
    this.init();
  }

  init() {
    const w = this.canvas.clientWidth || 800;
    const h = this.canvas.clientHeight || 600;
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false, preserveDrawingBuffer: true });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color("#f0f2f5");
    this.scene.fog = new THREE.FogExp2("#dde1e8", 0.035);
    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    this.camera.position.set(this.room.width * 0.8, this.room.height * 1.4, this.room.depth * 1.2);
    this.camera.lookAt(this.room.width / 2, 0, this.room.depth / 2);
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.target.set(this.room.width / 2, this.room.height * 0.4, this.room.depth / 2);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.minPolarAngle = 0.2;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.05;
    this.controls.minDistance = 1;
    this.controls.maxDistance = 25;
    this.raycaster = new THREE.Raycaster();
    this.scene.add(this._roomGroup);
    this.scene.add(this._lightGroup);
    this.buildRoom();
    this.buildLights();
    this.buildDefaultFurniture();
    this.animate();
  }

  buildRoom() {
    while (this._roomGroup.children.length > 0) this._roomGroup.remove(this._roomGroup.children[0]);
    this.surfaces = [];
    const { width, height, depth } = this.room;
    const floorMat = new THREE.MeshStandardMaterial({ color: "#e8e4de", roughness: 0.85, metalness: 0.0 });
    const wallMat = new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.9, metalness: 0.0, side: THREE.FrontSide });
    const ceilMat = new THREE.MeshStandardMaterial({ color: "#ffffff", roughness: 0.95, metalness: 0.0, side: THREE.FrontSide });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), floorMat);
    floor.rotation.x = -Math.PI / 2; floor.position.set(width / 2, 0, depth / 2);
    floor.receiveShadow = true; floor.userData = { surface: "floor" };
    this._roomGroup.add(floor); this.surfaces.push(floor);
    const gridHelper = new THREE.GridHelper(Math.max(width, depth) * 2, Math.max(width, depth) * 4, "#e0dbd4", "#d0ccc6");
    gridHelper.position.set(width / 2, 0.001, depth / 2); gridHelper.material.opacity = 0.4; gridHelper.material.transparent = true;
    this._roomGroup.add(gridHelper);
    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), ceilMat);
    ceiling.rotation.x = Math.PI / 2; ceiling.position.set(width / 2, height, depth / 2);
    ceiling.receiveShadow = true; ceiling.userData = { surface: "ceiling" };
    this._roomGroup.add(ceiling); this.surfaces.push(ceiling);
    const backWall = new THREE.Mesh(new THREE.PlaneGeometry(width, height), wallMat);
    backWall.position.set(width / 2, height / 2, 0); backWall.receiveShadow = true;
    backWall.userData = { surface: "wall-back", normal: new THREE.Vector3(0, 0, 1) };
    this._roomGroup.add(backWall); this.surfaces.push(backWall);
    const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(depth, height), wallMat);
    leftWall.rotation.y = Math.PI / 2; leftWall.position.set(0, height / 2, depth / 2);
    leftWall.receiveShadow = true; leftWall.userData = { surface: "wall-left", normal: new THREE.Vector3(1, 0, 0) };
    this._roomGroup.add(leftWall); this.surfaces.push(leftWall);
    const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(depth, height), wallMat.clone());
    rightWall.rotation.y = -Math.PI / 2; rightWall.position.set(width, height / 2, depth / 2);
    rightWall.receiveShadow = true; rightWall.userData = { surface: "wall-right", normal: new THREE.Vector3(-1, 0, 0) };
    this._roomGroup.add(rightWall); this.surfaces.push(rightWall);
    const frontWall = new THREE.Mesh(new THREE.PlaneGeometry(width, height), wallMat.clone());
    frontWall.rotation.y = Math.PI; frontWall.position.set(width / 2, height / 2, depth);
    frontWall.receiveShadow = true; frontWall.userData = { surface: "wall-front", normal: new THREE.Vector3(0, 0, -1) };
    this._roomGroup.add(frontWall); this.surfaces.push(frontWall);
    const baseMat = new THREE.MeshStandardMaterial({ color: "#e0dbd4", roughness: 0.7 });
    const baseBack = new THREE.Mesh(new THREE.BoxGeometry(width, 0.08, 0.04), baseMat);
    baseBack.position.set(width / 2, 0.04, 0.02); this._roomGroup.add(baseBack);
    const baseLeft = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.08, depth), baseMat);
    baseLeft.position.set(0.02, 0.04, depth / 2); this._roomGroup.add(baseLeft);
    const baseRight = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.08, depth), baseMat);
    baseRight.position.set(width - 0.02, 0.04, depth / 2); this._roomGroup.add(baseRight);
  }

  buildLights() {
    while (this._lightGroup.children.length > 0) this._lightGroup.remove(this._lightGroup.children[0]);
    const { width, height, depth } = this.room;
    this._lightGroup.add(new THREE.AmbientLight("#dde1e8", 0.7));
    const ceilingLight = new THREE.PointLight("#fff5e6", 2.5, 15);
    ceilingLight.position.set(width / 2, height - 0.1, depth / 2);
    ceilingLight.castShadow = true; ceilingLight.shadow.mapSize.set(1024, 1024); ceilingLight.shadow.radius = 4;
    this._lightGroup.add(ceilingLight);
    const fill1 = new THREE.PointLight("#d4e4ff", 0.6, 12); fill1.position.set(0.5, height * 0.7, 0.5);
    this._lightGroup.add(fill1);
    const fill2 = new THREE.PointLight("#ffe8d4", 0.4, 12); fill2.position.set(width - 0.5, height * 0.5, depth - 0.5);
    this._lightGroup.add(fill2);
    const dir = new THREE.DirectionalLight("#ffffff", 0.8);
    dir.position.set(width * 1.5, height * 2, depth * 1.5); dir.target.position.set(width / 2, 0, depth / 2);
    dir.castShadow = true; dir.shadow.mapSize.set(2048, 2048);
    dir.shadow.camera.near = 0.5; dir.shadow.camera.far = 40;
    dir.shadow.camera.left = -10; dir.shadow.camera.right = 10;
    dir.shadow.camera.top = 10; dir.shadow.camera.bottom = -10;
    this._lightGroup.add(dir); this._lightGroup.add(dir.target);
  }

  buildDefaultFurniture(chairSides = { front: true, back: true, left: true, right: true }) {
    const { width, depth } = this.room;
    this.addFurniture({ id: "default-table", type: "table", x: width / 2 - 1.2, z: depth / 2 - 0.5, tableWidth: 2.4, tableDepth: 1.2, tableHeight: 0.75, chairSides });
  }

  // Dipanggil dari React setelah fetch pengaturan model furniture dari
  // Supabase (lihat useEffect di komponen RoomDesigner). Menyimpan URL
  // model + rebuild meja/kursi yang sudah ada di scene supaya perubahan
  // pengaturan langsung terlihat tanpa perlu reload manual, memakai pola
  // yang sama seperti updateFurnitureSize/updateChairSides (pertahankan
  // posisi & chairSides saat ini, cuma ganti geometrinya).
  setFurnitureModels(models) {
    this.furnitureModels = { ...this.furnitureModels, ...models };
    const existing = this.furnitureGroups.get("default-table");
    if (!existing) return; // belum ada meja default (belum sempat ke-init)
    const { tableWidth, tableDepth, tableHeight, chairSides } = existing.userData;
    const pos = existing.position;
    this.addFurniture({
      id: "default-table", type: "table",
      x: pos.x - tableWidth / 2, z: pos.z - tableDepth / 2,
      tableWidth, tableDepth, tableHeight, chairSides,
    });
  }

  // Load model .glb custom untuk furniture (meja/kursi) secara async,
  // lalu tempel sebagai child dari parentGroup setelah selesai. localOffset
  // dipakai untuk kursi (posisi & rotasi berbeda-beda per kursi dalam
  // group meja yang sama) — meja sendiri tidak perlu offset (selalu di
  // titik 0,0,0 relatif terhadap group-nya sendiri).
  async _loadFurnitureModel(url, targetW, targetH, targetD, extraScale, rotationYDeg, parentGroup, furnitureId, localOffset = null) {
    try {
      const model = await loadScaledModel(url, targetW, targetH, targetD, extraScale || 1, rotationYDeg || 0);
      // Guard: kalau furniture sudah dihapus/diganti/di-rebuild sebelum
      // loading selesai (mis. admin ganti model lagi), parentGroup yang
      // lama ini sudah bukan group yang terdaftar lagi — abaikan hasilnya
      // supaya tidak nyangkut nambahin model ke group yang sudah "mati".
      if (this.furnitureGroups.get(furnitureId) !== parentGroup) return;

      if (localOffset) {
        model.position.x += localOffset.x || 0;
        model.position.z += localOffset.z || 0;
        if (localOffset.rotY) model.rotation.y += localOffset.rotY;
      }
      model.userData = { isFurniturePart: true, furnitureId };
      model.traverse((c) => { if (c.isMesh) c.userData = { isFurniturePart: true, furnitureId }; });
      parentGroup.add(model);
    } catch (err) {
      console.error(`Gagal load model 3D furniture (${url}):`, err);
    }
  }

  // ── FIX UTAMA: addFurniture menyimpan semua state dengan benar ──
  addFurniture({ id, type, x, z, tableWidth, tableDepth, tableHeight, chairSides }) {
    // Normalize: pastikan setiap key ada dan bertipe boolean
    const sides = {
      front: chairSides?.front !== false,
      back: chairSides?.back !== false,
      left: chairSides?.left !== false,
      right: chairSides?.right !== false,
    };

    // Hapus group lama jika ada
    if (this.furnitureGroups.has(id)) {
      const old = this.furnitureGroups.get(id);
      this.scene.remove(old);
      this.furnitureSurfaces = this.furnitureSurfaces.filter(m => m.userData.furnitureId !== id);
    }

    const group = new THREE.Group();
    // Simpan semua metadata di userData — ini satu-satunya sumber kebenaran di sisi 3D
    group.userData = { furnitureId: id, type, isFurniture: true, chairSides: sides, tableWidth, tableDepth, tableHeight };

    if (type === "table") {
      const usingCustomTable = !!this.furnitureModels.tableUrl;

      // Permukaan tempat device diletakkan — SELALU dibuat (dipakai untuk
      // raycasting klik "taruh device di meja"), terlepas dari mode
      // geometri. Kalau pakai model .glb custom, plane ini dibuat
      // transparan (opacity 0) supaya tidak dobel keliatan dengan lid/top
      // model asli, TAPI tetap "visible" (bukan visible=false) karena
      // Three.js raycaster mengabaikan object dengan visible=false —
      // jadi klik-untuk-taruh tetap presisi walau bentuk visual meja GLB
      // tidak rata sempurna.
      const topMat = new THREE.MeshStandardMaterial({
        color: "#f0efed", roughness: 0.4, metalness: 0.1,
        transparent: usingCustomTable, opacity: usingCustomTable ? 0 : 1,
      });
      const top = new THREE.Mesh(new THREE.BoxGeometry(tableWidth, 0.05, tableDepth), topMat);
      top.position.y = tableHeight + 0.025;
      top.castShadow = !usingCustomTable; top.receiveShadow = true;
      top.userData = { surface: "table", furnitureId: id, tableTop: tableHeight + 0.05, isFurniturePart: true };
      group.add(top); this.furnitureSurfaces.push(top);

      if (usingCustomTable) {
        this._loadFurnitureModel(
          this.furnitureModels.tableUrl, tableWidth, tableHeight, tableDepth,
          this.furnitureModels.tableScale, this.furnitureModels.tableRotationY,
          group, id
        );
      } else {
        const legMat = new THREE.MeshStandardMaterial({ color: "#c8c4be", roughness: 0.5 });
        const legGeo = new THREE.CylinderGeometry(0.03, 0.03, tableHeight, 8);
        [[-tableWidth / 2 + 0.1, tableHeight / 2, -tableDepth / 2 + 0.1], [tableWidth / 2 - 0.1, tableHeight / 2, -tableDepth / 2 + 0.1],
        [-tableWidth / 2 + 0.1, tableHeight / 2, tableDepth / 2 - 0.1], [tableWidth / 2 - 0.1, tableHeight / 2, tableDepth / 2 - 0.1]
        ].forEach(([lx, ly, lz]) => {
          const leg = new THREE.Mesh(legGeo, legMat);
          leg.position.set(lx, ly, lz); leg.castShadow = true;
          leg.userData = { isFurniturePart: true, furnitureId: id };
          group.add(leg);
        });
      }

      this._addChairs(group, tableWidth, tableDepth, tableHeight, id, sides);
    }

    group.position.set(x + tableWidth / 2, 0, z + tableDepth / 2);
    this.scene.add(group);
    this.furnitureGroups.set(id, group);
    return group;
  }

  // ── FIX: _addChairs menggunakan objek sides yang sudah dinormalisasi ──
  _addChairs(tableGroup, tableWidth, tableDepth, tableHeight, furnitureId, sides) {
    // Jarak MINIMUM antar pusat kursi yang bertetangga di sisi yang sama.
    // Kursi kantor (termasuk armrest ±0.2725m & base roda radius 0.28m)
    // punya footprint total ~0.56m — 0.75m menyisakan jarak napas ~19cm
    // antar kursi supaya tidak terlihat menempel/nyambung.
    //
    // BUG SEBELUMNYA: seatsPerSide dihitung dari (tableWidth / 0.7), TAPI
    // posisi aktual kursi dihitung ulang dari (tableWidth / (seatsPerSide+1))
    // — dua rumus berbeda yang bisa saling tidak sinkron. Contoh nyata:
    // tableWidth=2.4 → seatsPerSide=round(2.4/0.7)=3, tapi spacing aktual
    // jadi 2.4/(3+1)=0.6m, LEBIH KECIL dari 0.56m footprint kursi — kursi
    // jadi nyaris menempel. Sekarang seatsPerSide pakai floor() (bukan
    // round()) dan posisi kursi pakai SEAT_SPACING tetap yang sama persis
    // dengan yang dipakai menghitung jumlah kursi — dijamin selalu presisi
    // konsisten berapa pun ukuran mejanya.
    const SEAT_SPACING = 0.75;
    const seatsPerSide = Math.max(1, Math.floor(tableWidth / SEAT_SPACING));
    const seatsPerEnd = Math.max(1, Math.floor(tableDepth / SEAT_SPACING));
    const mark = mesh => { mesh.userData = { isFurniturePart: true, furnitureId }; };

    // Kalau admin sudah upload model .glb custom untuk kursi, semua posisi
    // kursi memakai model itu (di-load async per posisi) alih-alih kursi
    // kantor prosedural. Target dimensi kursi standar dipakai untuk
    // auto-scaling model (lebar bahu ~0.46m, tinggi total termasuk
    // sandaran ~1.0m, kedalaman dudukan+sandaran ~0.55m) — kalau hasilnya
    // kurang pas, admin bisa koreksi lewat "Skala Model" di pengaturan.
    const useCustomChair = !!this.furnitureModels.chairUrl;
    const CHAIR_TARGET = { w: 0.46, h: 1.0, d: 0.55 };
    const loadChairModelAt = (cx, cz, rotY) => {
      this._loadFurnitureModel(
        this.furnitureModels.chairUrl, CHAIR_TARGET.w, CHAIR_TARGET.h, CHAIR_TARGET.d,
        this.furnitureModels.chairScale, this.furnitureModels.chairRotationY,
        tableGroup, furnitureId, { x: cx, z: cz, rotY }
      );
    };

    // Materials
    const fabricMat = new THREE.MeshStandardMaterial({ color: "#d6d2cb", roughness: 0.75, metalness: 0.0 });
    const shellMat = new THREE.MeshStandardMaterial({ color: "#e8e5e0", roughness: 0.4, metalness: 0.05 });
    const metalMat = new THREE.MeshStandardMaterial({ color: "#b0aca6", roughness: 0.3, metalness: 0.6 });
    const wheelMat = new THREE.MeshStandardMaterial({ color: "#888480", roughness: 0.5, metalness: 0.3 });
    const gasMat = new THREE.MeshStandardMaterial({ color: "#c0bcb6", roughness: 0.2, metalness: 0.8 });

    const seatH = 0.46;
    const backH = 0.52;
    // Jarak dari tepi meja ke titik pusat kursi (bukan sekadar estetika —
    // kursi kantor punya base roda radius 0.28m yang menjorok ke arah meja,
    // jadi buffer ini harus cukup besar supaya base roda & dudukan kursi
    // tidak "mepet"/nyaris nempel kaki meja. 0.50m menyisakan jarak napas
    // ~22cm dari tepi meja ke ujung depan base roda, dan ~28cm ke ujung
    // depan dudukan — sesuai standar clearance furniture konferensi.
    const offsetZ = tableDepth / 2 + 0.50;

    const buildOfficeChair = (furnitureId) => {
      const cg = new THREE.Group();
      cg.userData = { isFurniturePart: true, furnitureId };

      // ── Seat cushion (slightly rounded box)
      const seat = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.07, 0.44), fabricMat);
      seat.position.y = seatH;
      seat.castShadow = true; mark(seat); cg.add(seat);

      // Seat front lip (ergonomic curve hint)
      const lip = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.46, 12), fabricMat);
      lip.rotation.z = Math.PI / 2;
      lip.position.set(0, seatH - 0.015, 0.22);
      mark(lip); cg.add(lip);

      // ── Backrest shell + cushion
      const backShell = new THREE.Mesh(new THREE.BoxGeometry(0.44, backH, 0.045), shellMat);
      backShell.position.set(0, seatH + backH / 2 + 0.04, -0.20);
      backShell.rotation.x = -0.08; // slight recline
      backShell.castShadow = true; mark(backShell); cg.add(backShell);

      const backCushion = new THREE.Mesh(new THREE.BoxGeometry(0.38, backH * 0.82, 0.035), fabricMat);
      backCushion.position.set(0, seatH + backH / 2 + 0.04, -0.175);
      backCushion.rotation.x = -0.08;
      mark(backCushion); cg.add(backCushion);

      // Lumbar bump
      const lumbar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.36, 12), fabricMat);
      lumbar.rotation.z = Math.PI / 2;
      lumbar.position.set(0, seatH + 0.12, -0.19);
      lumbar.rotation.x = -0.08;
      mark(lumbar); cg.add(lumbar);

      // Headrest
      const headrest = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.16, 0.06), fabricMat);
      headrest.position.set(0, seatH + backH + 0.1, -0.195);
      headrest.rotation.x = -0.08;
      mark(headrest); cg.add(headrest);

      // Headrest post
      const hrPost = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.12, 8), metalMat);
      hrPost.position.set(0, seatH + backH + 0.03, -0.195);
      mark(hrPost); cg.add(hrPost);

      // ── Armrests (left & right)
      [-0.24, 0.24].forEach(ax => {
        const armSupport = new THREE.Mesh(new THREE.CylinderGeometry(0.013, 0.013, seatH * 0.55, 8), metalMat);
        armSupport.position.set(ax, seatH * 0.72, 0.0);
        mark(armSupport); cg.add(armSupport);

        const armPad = new THREE.Mesh(new THREE.BoxGeometry(0.065, 0.025, 0.22), shellMat);
        armPad.position.set(ax, seatH + 0.18, 0.0);
        mark(armPad); cg.add(armPad);
      });

      // ── Gas cylinder
      const gas = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.032, seatH - 0.1, 10), gasMat);
      gas.position.set(0, (seatH - 0.1) / 2, 0);
      mark(gas); cg.add(gas);

      // ── 5-point star base
      const numLegs = 5;
      const baseRadius = 0.28;
      for (let i = 0; i < numLegs; i++) {
        const angle = (i / numLegs) * Math.PI * 2;
        const lx = Math.cos(angle) * baseRadius;
        const lz = Math.sin(angle) * baseRadius;

        // Arm of star
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.018, baseRadius), metalMat);
        arm.position.set(lx / 2, 0.01, lz / 2);
        arm.rotation.y = -angle;
        mark(arm); cg.add(arm);

        // Wheel (small double cylinder)
        const wheelHub = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.045, 10), wheelMat);
        wheelHub.rotation.x = Math.PI / 2;
        wheelHub.position.set(lx, 0.024, lz);
        mark(wheelHub); cg.add(wheelHub);

        const wheelL = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.015, 10), wheelMat);
        wheelL.rotation.x = Math.PI / 2;
        wheelL.position.set(lx, 0.024, lz - 0.018);
        mark(wheelL); cg.add(wheelL);

        const wheelR = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.015, 10), wheelMat);
        wheelR.rotation.x = Math.PI / 2;
        wheelR.position.set(lx, 0.024, lz + 0.018);
        mark(wheelR); cg.add(wheelR);
      }

      return cg;
    };

    // Kursi dipusatkan (centered) di sepanjang lebar meja, dengan jarak
    // antar pusat kursi TEPAT SEAT_SPACING — bukan hasil bagi rata lebar
    // meja yang bisa menyusut jadi lebih kecil dari footprint kursi.
    const xSpan = (seatsPerSide - 1) * SEAT_SPACING;
    const xPositions = [];
    for (let i = 0; i < seatsPerSide; i++)
      xPositions.push(-xSpan / 2 + i * SEAT_SPACING);

    // Front & Back
    [{ side: -1, key: "back" }, { side: 1, key: "front" }].forEach(({ side, key }) => {
      if (!sides[key]) return;
      xPositions.forEach(cx => {
        if (useCustomChair) {
          loadChairModelAt(cx, side * offsetZ, side === 1 ? Math.PI : 0);
          return;
        }
        const cg = buildOfficeChair(furnitureId);
        cg.position.set(cx, 0, side * offsetZ);
        if (side === 1) cg.rotation.y = Math.PI;
        tableGroup.add(cg);
      });
    });

    // Left & Right
    const zSpan = (seatsPerEnd - 1) * SEAT_SPACING;
    const zPositions = [];
    for (let i = 0; i < seatsPerEnd; i++)
      zPositions.push(-zSpan / 2 + i * SEAT_SPACING);

    // Buffer sama dengan offsetZ di atas — konsisten di keempat sisi meja
    [{ hx: -tableWidth / 2 - 0.50, key: "left", rotY: Math.PI / 2 },
    { hx: tableWidth / 2 + 0.50, key: "right", rotY: -Math.PI / 2 }
    ].forEach(({ hx, key, rotY }) => {
      if (!sides[key]) return;
      zPositions.forEach(cz => {
        if (useCustomChair) {
          loadChairModelAt(hx, cz, rotY);
          return;
        }
        const cg = buildOfficeChair(furnitureId);
        cg.position.set(hx, 0, cz);
        cg.rotation.y = rotY;
        tableGroup.add(cg);
      });
    });
  }

  addDevice(item) {
    const group = new THREE.Group();
    const { width, height, depth, color, accentColor } = item.product;
    const pid = item.product.id;

    // ── MODEL 3D DINAMIS (.glb) — kalau produk ini punya modelUrl dari
    // database (diupload lewat halaman admin), lewati semua geometri
    // hardcoded di bawah dan pasang loading spinner dulu sebagai
    // placeholder. Model asli akan menyusul secara async lewat
    // _loadDynamicModel() yang dipanggil setelah addDevice() selesai. ──
    const hasModel = !!item.product.modelUrl;
    if (hasModel) {
      const { group: spinnerGroup, tick } = createLoadingSpinner(width, height, depth);
      group.add(spinnerGroup);
      group.userData.spinnerGroup = spinnerGroup;
      this._spinnerTicks.set(item.id, tick);
    }

    // ─────────────────────────────────────────────
    // SAMSUNG THE WALL 146" — ultra-slim LED wall
    // ─────────────────────────────────────────────
    if (hasModel) {
      // Lewati semua blok geometri hardcoded di bawah — device ini
      // memakai model .glb dinamis, bukan geometri kode manual.
    }
    else if (pid === "samsung-wall-146") {
      // Cabinet body — very thin (80mm)
      const cabinetMat = new THREE.MeshStandardMaterial({ color: "#0d0d0d", roughness: 0.3, metalness: 0.8 });
      const cabinet = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), cabinetMat);
      cabinet.castShadow = true;
      group.add(cabinet);

      // Screen — micro LED, near-zero bezel
      const screenMat = new THREE.MeshStandardMaterial({ color: "#020408", roughness: 0.0, metalness: 0.1, emissive: "#0d1f3c", emissiveIntensity: 0.5 });
      const screen = new THREE.Mesh(new THREE.PlaneGeometry(width * 0.995, height * 0.995), screenMat);
      screen.position.z = depth / 2 + 0.001;
      group.add(screen);

      // Samsung logo strip bottom-center
      const logoMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.2, metalness: 0.9 });
      const logo = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.018, 0.005), logoMat);
      logo.position.set(0, -height / 2 + 0.02, depth / 2 + 0.002);
      group.add(logo);

      // Wall bracket visible at back-bottom
      const bracketMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.5, metalness: 0.7 });
      const bracket = new THREE.Mesh(new THREE.BoxGeometry(width * 0.7, 0.04, 0.06), bracketMat);
      bracket.position.set(0, -height / 2 + 0.02, -depth / 2 - 0.03);
      group.add(bracket);
    }

    else if (pid === "aver-cam-570") {
      const cW = 0.1708, cH = 0.1905, cD = 0.173;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.42, metalness: 0.25 });
      const bodyMatSft = new THREE.MeshStandardMaterial({ color: "#202020", roughness: 0.48, metalness: 0.20 });
      const fabricMat = new THREE.MeshStandardMaterial({ color: "#3a3a3c", roughness: 0.95, metalness: 0.0 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a14", roughness: 0, metalness: 0.85, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#020205", roughness: 0, metalness: 1 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#0f0f0f", roughness: 0.25, metalness: 0.75 });
      const rim2Mat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.30, metalness: 0.65 });
      const ledBlueMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2.2, roughness: 0 });
      const logoMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.4, metalness: 0.3 });

      // ── 1. BASE — persegi rounded, lebar & datar ──
      const baseW = cW * 1.05, baseH = cH * 0.30, baseD = cD * 0.95;

      const baseCore = new THREE.Mesh(new THREE.BoxGeometry(baseW - 0.024, baseH, baseD - 0.024), bodyMat);
      baseCore.castShadow = true;
      baseCore.position.set(0, -cH * 0.46, 0);
      group.add(baseCore);

      // Corner rounding pakai cylinder kecil di 4 sudut
      const cr = 0.012;
      [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) => {
        const corner = new THREE.Mesh(new THREE.CylinderGeometry(cr, cr, baseH, 16), bodyMat);
        corner.position.set(sx * (baseW / 2 - cr), -cH * 0.46, sz * (baseD / 2 - cr));
        group.add(corner);
      });
      // Edge fillers
      [[0, -1, baseW - cr * 2, baseD], [0, 1, baseW - cr * 2, baseD]].forEach(() => { });
      const edgeFront = new THREE.Mesh(new THREE.BoxGeometry(baseW - cr * 2, baseH, cr * 2), bodyMat);
      edgeFront.position.set(0, -cH * 0.46, baseD / 2 - cr);
      group.add(edgeFront);
      const edgeBack = new THREE.Mesh(new THREE.BoxGeometry(baseW - cr * 2, baseH, cr * 2), bodyMat);
      edgeBack.position.set(0, -cH * 0.46, -(baseD / 2 - cr));
      group.add(edgeBack);
      const edgeLeft = new THREE.Mesh(new THREE.BoxGeometry(cr * 2, baseH, baseD - cr * 2), bodyMat);
      edgeLeft.position.set(-(baseW / 2 - cr), -cH * 0.46, 0);
      group.add(edgeLeft);
      const edgeRight = new THREE.Mesh(new THREE.BoxGeometry(cr * 2, baseH, baseD - cr * 2), bodyMat);
      edgeRight.position.set(baseW / 2 - cr, -cH * 0.46, 0);
      group.add(edgeRight);

      // ── 2. FABRIC GRILLE — sisi kanan base depan ──
      const grille = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.38, baseH * 0.72, 0.006), fabricMat);
      grille.position.set(baseW * 0.26, -cH * 0.44, baseD / 2 + 0.001);
      group.add(grille);

      // Tekstur garis horizontal halus pada grille
      for (let i = 0; i < 5; i++) {
        const line = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.36, 0.0015, 0.002),
          new THREE.MeshStandardMaterial({ color: "#2a2a2c", roughness: 1 }));
        line.position.set(baseW * 0.26, -cH * 0.50 + i * 0.011, baseD / 2 + 0.003);
        group.add(line);
      }

      // ── 3. BLUE LED — kiri bawah base depan ──
      const ledDot = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.004, 14), ledBlueMat);
      ledDot.rotation.x = Math.PI / 2;
      ledDot.position.set(-baseW * 0.30, -cH * 0.44, baseD / 2 + 0.003);
      group.add(ledDot);

      // ── 4. STATUS/POWER DOT — tengah-kiri ──
      const statusDot = new THREE.Mesh(new THREE.CylinderGeometry(0.0035, 0.0035, 0.003, 10), rimMat);
      statusDot.rotation.x = Math.PI / 2;
      statusDot.position.set(-baseW * 0.12, -cH * 0.44, baseD / 2 + 0.003);
      group.add(statusDot);

      // ── 5. AVer LOGO — tengah base depan ──
      const logoText = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.20, 0.014, 0.003), logoMat);
      logoText.position.set(baseW * 0.02, -cH * 0.44, baseD / 2 + 0.002);
      group.add(logoText);

      const yokeMat = bodyMatSft;

      // Titik-titik path yoke (dari base kiri → naik → melengkung di belakang head → turun ke kanan)
      // Dibangun sebagai serangkaian segmen silinder pendek yang saling terhubung,
      // mengikuti kurva C secara manual (bukan torus penuh)
      const yokePoints = [
        [-cW * 0.34, -cH * 0.28, -cD * 0.02],  // mulai dari base, agak ke belakang
        [-cW * 0.38, -cH * 0.05, -cD * 0.06],
        [-cW * 0.36, cH * 0.18, -cD * 0.10],
        [-cW * 0.24, cH * 0.34, -cD * 0.08],
        [0.00, cH * 0.40, -cD * 0.02],   // titik tertinggi, di belakang head
        [cW * 0.24, cH * 0.34, cD * 0.06],
        [cW * 0.34, cH * 0.18, cD * 0.12],
        [cW * 0.36, -cH * 0.02, cD * 0.14],   // turun menopang head dari kanan-depan
      ];

      const yokeRadius = 0.040;

      // Buat tabung mengikuti path dengan CatmullRomCurve3 + TubeGeometry
      // (lebih natural & terkontrol dibanding torus manual)
      const yokeCurvePts = yokePoints.map(([x, y, z]) => new THREE.Vector3(x, y, z));
      const yokeCurve = new THREE.CatmullRomCurve3(yokeCurvePts, false, "catmullrom", 0.3);
      const yokeTubeGeo = new THREE.TubeGeometry(yokeCurve, 40, yokeRadius, 16, false);
      const yokeTube = new THREE.Mesh(yokeTubeGeo, yokeMat);
      yokeTube.castShadow = true;
      group.add(yokeTube);

      // Cap bulat di kedua ujung yoke supaya tidak terlihat terpotong
      const yokeCapStart = new THREE.Mesh(new THREE.SphereGeometry(yokeRadius, 16, 12), yokeMat);
      yokeCapStart.position.set(...yokePoints[0]);
      group.add(yokeCapStart);

      const yokeCapEnd = new THREE.Mesh(new THREE.SphereGeometry(yokeRadius * 0.85, 16, 12), yokeMat);
      yokeCapEnd.position.set(...yokePoints[yokePoints.length - 1]);
      group.add(yokeCapEnd);

      // Pivot connector kecil antara ujung yoke kanan & head
      const pivotR = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.026, 18), rim2Mat);
      pivotR.rotation.z = Math.PI / 2;
      pivotR.position.set(cW * 0.38, cH * 0.02, cD * 0.15);
      group.add(pivotR);

      // ── 7. CAMERA HEAD — sphere besar, dudukan lensa ──
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.082, 40, 32), bodyMat);
      head.position.set(0, cH * 0.16, cD * 0.06);
      group.add(head);


      // ── 8. LENS ASSEMBLY — besar, menonjol ke depan ──
      // Outer ring (chrome/dark)
      // const lensOuterRing = new THREE.Mesh(new THREE.CylinderGeometry(0.062, 0.066, 0.020, 36), rim2Mat);
      // lensOuterRing.rotation.x = Math.PI / 2;
      // lensOuterRing.position.set(0, cH * 0.16, cD * 0.06 + 0.072);
      // group.add(lensOuterRing);

      // Main barrel
      const lensBarrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.057, 0.060, 0.068, 34), rimMat);
      lensBarrel1.rotation.x = Math.PI / 2;
      lensBarrel1.position.set(0, cH * 0.16, cD * 0.06 + 0.086);
      group.add(lensBarrel1);

      // Inner barrel
      const lensBarrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.054, 0.038, 30), bodyMat);
      lensBarrel2.rotation.x = Math.PI / 2;
      lensBarrel2.position.set(0, cH * 0.16, cD * 0.06 + 0.115);
      group.add(lensBarrel2);

      // Innermost barrel ring
      const lensBarrel3 = new THREE.Mesh(new THREE.CylinderGeometry(0.041, 0.046, 0.022, 28), rimMat);
      lensBarrel3.rotation.x = Math.PI / 2;
      lensBarrel3.position.set(0, cH * 0.16, cD * 0.06 + 0.132);
      group.add(lensBarrel3);

      // Glass layers (multi-coating realistis)
      const lensGlassOuter = new THREE.Mesh(new THREE.CircleGeometry(0.039, 34), glassMat);
      lensGlassOuter.position.set(0, cH * 0.16, cD * 0.06 + 0.144);
      group.add(lensGlassOuter);

      const lensGlassMid = new THREE.Mesh(new THREE.CircleGeometry(0.030, 28),
        new THREE.MeshStandardMaterial({ color: "#0a0a30", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.88 }));
      lensGlassMid.position.set(0, cH * 0.16, cD * 0.06 + 0.1455);
      group.add(lensGlassMid);

      const lensGlassInner = new THREE.Mesh(new THREE.CircleGeometry(0.021, 22),
        new THREE.MeshStandardMaterial({ color: "#050318", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.95 }));
      lensGlassInner.position.set(0, cH * 0.16, cD * 0.06 + 0.147);
      group.add(lensGlassInner);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.012, 18), lensMat);
      lensCore.position.set(0, cH * 0.16, cD * 0.06 + 0.1485);
      group.add(lensCore);

      // Chrome rim luar lensa
      // const lensRimOuter = new THREE.Mesh(new THREE.TorusGeometry(0.039, 0.004, 9, 34), rim2Mat);
      // lensRimOuter.rotation.x = Math.PI / 2;
      // lensRimOuter.position.set(0, cH * 0.16, cD * 0.06 + 0.143);
      // group.add(lensRimOuter);

      // Lens reflection highlight
      const lensHL = new THREE.Mesh(new THREE.CircleGeometry(0.007, 10),
        new THREE.MeshStandardMaterial({ color: "#2a4a8a", emissive: "#1a2a5a", emissiveIntensity: 0.6, roughness: 0 }));
      lensHL.position.set(-0.018, cH * 0.16 + 0.024, cD * 0.06 + 0.1495);
      group.add(lensHL);

      // ── 9. "12X ZOOM 4K" LABEL — di sisi lensa kiri ──
      const labelBg = new THREE.Mesh(new THREE.PlaneGeometry(0.035, 0.014),
        new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.6 }));
      labelBg.position.set(-0.052, cH * 0.16 + 0.034, cD * 0.06 + 0.088);
      labelBg.rotation.y = 0.15;
      group.add(labelBg);

      // Text dummy via small white strip (representasi label putih)
      const labelText = new THREE.Mesh(new THREE.PlaneGeometry(0.030, 0.005),
        new THREE.MeshStandardMaterial({ color: "#e5e5e5", roughness: 0.5 }));
      labelText.position.set(-0.052, cH * 0.16 + 0.036, cD * 0.06 + 0.0885);
      labelText.rotation.y = 0.15;
      group.add(labelText);

      // Red "4K" accent dot
      const fourKDot = new THREE.Mesh(new THREE.CircleGeometry(0.003, 8),
        new THREE.MeshStandardMaterial({ color: "#dc2626", emissive: "#dc2626", emissiveIntensity: 1.2, roughness: 0 }));
      fourKDot.position.set(-0.040, cH * 0.16 + 0.038, cD * 0.06 + 0.0888);
      fourKDot.rotation.y = 0.15;
      group.add(fourKDot);

      // ── 10. BACK PORTS ──
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 });
      [[-0.030, 0], [0.005, 0], [0.040, 0]].forEach(([px, py]) => {
        const port = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.009, 0.005), portMat);
        port.position.set(px, -cH * 0.40 + py, -baseD / 2 - 0.002);
        group.add(port);
      });

      // ── 11. WALL MOUNT BRACKET ──
      const mountMat = new THREE.MeshStandardMaterial({ color: "#222", roughness: 0.4, metalness: 0.8 });
      const mountBar = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.7, 0.012, 0.030), mountMat);
      mountBar.position.set(0, -cH * 0.59, -baseD * 0.30);
      group.add(mountBar);
    }


    // ─────────────────────────────────────────────
    // AVER CAM550 — 170.8×190.5×173mm dual-lens PTZ
    // Orange/dark body, dual lens front, PTZ head
    // ─────────────────────────────────────────────
    else if (pid === "aver-cam-550") {
      const cW = 0.1708, cH = 0.1905, cD = 0.173;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.42, metalness: 0.25 });
      const bodyMatSft = new THREE.MeshStandardMaterial({ color: "#202020", roughness: 0.48, metalness: 0.20 });
      const fabricMat = new THREE.MeshStandardMaterial({ color: "#3a3a3c", roughness: 0.95, metalness: 0.0 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a14", roughness: 0, metalness: 0.85, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#020205", roughness: 0, metalness: 1 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#0f0f0f", roughness: 0.25, metalness: 0.75 });
      const rim2Mat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.30, metalness: 0.65 });
      const ledBlueMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2.2, roughness: 0 });
      const logoMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.4, metalness: 0.3 });

      // ── PROPORSI TEGAK: base(bawah) → yoke(tengah) → head(atas) ──
      const baseW = cW * 1.0, baseH = cH * 0.22, baseD = cD * 0.85;
      const baseY = -cH * 0.39;

      // ── 1. BASE ──
      const baseCore = new THREE.Mesh(new THREE.BoxGeometry(baseW, baseH, baseD), bodyMat);
      baseCore.castShadow = true;
      baseCore.position.set(0, baseY, 0);
      group.add(baseCore);

      // ── 2. FABRIC GRILLE — kiri base depan ──
      const grille = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.30, baseH * 0.7, 0.005), fabricMat);
      grille.position.set(-baseW * 0.30, baseY, baseD / 2 + 0.001);
      group.add(grille);

      // ── 3. AI LENS kecil — base depan tengah-kanan ──
      const aiGlass = new THREE.Mesh(new THREE.CircleGeometry(0.010, 18), glassMat);
      aiGlass.position.set(baseW * 0.04, baseY, baseD / 2 + 0.002);
      group.add(aiGlass);
      const aiCore = new THREE.Mesh(new THREE.CircleGeometry(0.005, 14), lensMat);
      aiCore.position.set(baseW * 0.04, baseY, baseD / 2 + 0.003);
      group.add(aiCore);

      // ── 4. LOGO ──
      const logoText = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.16, 0.012, 0.002), logoMat);
      logoText.position.set(baseW * 0.28, baseY, baseD / 2 + 0.002);
      group.add(logoText);

      // ── 5. LED biru — strip TIPIS di tepi BAWAH base (bukan di tengah) ──
      const ledStrip = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.85, 0.004, 0.003), ledBlueMat);
      ledStrip.position.set(0, baseY - baseH / 2 - 0.001, baseD / 2 - 0.010);
      group.add(ledStrip);

      // ── 6. YOKE — DUA LENGAN BOX TEGAK + ARC ATAS (jelas terlihat dari samping) ──
      const yokeW = 0.034;
      const yokeH = cH * 0.40; // lengan cukup tinggi agar yoke terlihat jelas
      const yokeY = baseY + baseH / 2 + yokeH / 2; // langsung di atas base, tanpa neck terpisah

      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(yokeW, yokeH, cD * 0.40), bodyMatSft);
      yokeL.position.set(-cW * 0.36, yokeY, -cD * 0.05);
      group.add(yokeL);

      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(yokeW, yokeH, cD * 0.40), bodyMatSft);
      yokeR.position.set(cW * 0.36, yokeY, -cD * 0.05);
      group.add(yokeR);

      // Arc atas — pakai BOX melengkung sederhana (beberapa segmen kecil membentuk kurva)
      const archSegments = 7;
      const archRadius = cW * 0.36;
      const archY = yokeY + yokeH / 2;
      for (let i = 0; i <= archSegments; i++) {
        const t = i / archSegments; // 0..1
        const angle = Math.PI * (1 - t); // dari kiri (PI) ke kanan (0)
        const ax = Math.cos(angle) * archRadius;
        const ay = archY + Math.sin(angle) * archRadius * 0.6;
        const seg = new THREE.Mesh(new THREE.BoxGeometry(0.030, 0.030, cD * 0.36), bodyMatSft);
        seg.position.set(ax, ay, -cD * 0.05);
        group.add(seg);
      }

      // ── 7. CAMERA HEAD — sphere, posisinya DI ATAS arc, menonjol ke DEPAN ──
      const headY = archY + 0.020;
      const headZ = cD * 0.12; // head menonjol ke depan relatif terhadap yoke
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.072, 36, 28), bodyMat);
      head.position.set(0, headY, headZ);
      group.add(head);

      // ── 8. LENS ASSEMBLY — menonjol di depan head ──
      const lensRing = new THREE.Mesh(new THREE.TorusGeometry(0.052, 0.005, 10, 32), rim2Mat);
      lensRing.position.set(0, headY, headZ + 0.062);
      group.add(lensRing);

      const barrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.050, 0.058, 30), rimMat);
      barrel1.rotation.x = Math.PI / 2;
      barrel1.position.set(0, headY, headZ + 0.075);
      group.add(barrel1);

      const barrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.045, 0.032, 26), bodyMat);
      barrel2.rotation.x = Math.PI / 2;
      barrel2.position.set(0, headY, headZ + 0.100);
      group.add(barrel2);

      const glassOuter = new THREE.Mesh(new THREE.CircleGeometry(0.033, 28), glassMat);
      glassOuter.position.set(0, headY, headZ + 0.118);
      group.add(glassOuter);

      const glassMid = new THREE.Mesh(new THREE.CircleGeometry(0.025, 22),
        new THREE.MeshStandardMaterial({ color: "#1a1a08", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      glassMid.position.set(0, headY, headZ + 0.119);
      group.add(glassMid);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.012, 16), lensMat);
      lensCore.position.set(0, headY, headZ + 0.121);
      group.add(lensCore);

      // Lens highlight — KECIL, tepat di kaca lensa (bukan di luar)
      const lensHL = new THREE.Mesh(new THREE.CircleGeometry(0.004, 8),
        new THREE.MeshStandardMaterial({ color: "#8a6a2a", emissive: "#5a4a1a", emissiveIntensity: 0.4, roughness: 0 }));
      lensHL.position.set(-0.012, headY + 0.018, headZ + 0.1215);
      group.add(lensHL);

      // ── 9. BACK PORTS ──
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 });
      const port = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.010, 0.005), portMat);
      port.position.set(0, baseY, -baseD / 2 - 0.002);
      group.add(port);
    }

    // ── HISENSE AIO LED (HAIO108/136/163) — ultra-slim all-in-one, 27-32mm
    else if (["hisense-led-108", "hisense-led-136", "hisense-led-163",
      "hisense-haio136de", "hisense-haio136ge",
      "hisense-haio163-a", "hisense-haio163-b"].includes(pid)) {
      const cabinetMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.2, metalness: 0.85 });
      const cabinet = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), cabinetMat);
      cabinet.castShadow = true; group.add(cabinet);

      // Ultra-slim bezel (4mm)
      const bz = 0.004;
      const bzMat = new THREE.MeshStandardMaterial({ color: "#050505", roughness: 0.15, metalness: 0.95 });
      [[0, height / 2 - bz / 2, depth / 2 + 0.001, width, bz, 0.003],
      [0, -height / 2 + bz / 2, depth / 2 + 0.001, width, bz, 0.003],
      [-width / 2 + bz / 2, 0, depth / 2 + 0.001, bz, height, 0.003],
      [width / 2 - bz / 2, 0, depth / 2 + 0.001, bz, height, 0.003]
      ].forEach(([x, y, z, w, h, d]) => {
        const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), bzMat);
        b.position.set(x, y, z); group.add(b);
      });

      // Screen
      const screenMat = new THREE.MeshStandardMaterial({ color: "#010306", roughness: 0, metalness: 0.05, emissive: "#050f20", emissiveIntensity: 0.7 });
      const screen = new THREE.Mesh(new THREE.PlaneGeometry(width - bz * 2, height - bz * 2), screenMat);
      screen.position.z = depth / 2 + 0.002; group.add(screen);

      // Brand logo strip bottom
      const logoMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.2, metalness: 0.9 });
      const logo = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.012, 0.004), logoMat);
      logo.position.set(0, -height / 2 + 0.012, depth / 2 + 0.002); group.add(logo);

      // Blue status LED
      const sled = new THREE.Mesh(new THREE.CircleGeometry(0.005, 8),
        new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2.5, roughness: 0 }));
      sled.position.set(width / 2 - 0.04, -height / 2 + 0.012, depth / 2 + 0.003); group.add(sled);

      // Wall bracket
      const bmMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.4, metalness: 0.8 });
      const bm = new THREE.Mesh(new THREE.BoxGeometry(width * 0.7, 0.03, 0.05), bmMat);
      bm.position.set(0, -height / 2 + 0.015, -depth / 2 - 0.025); group.add(bm);
    }

    // ── HISENSE INDOOR LED WALL (HTJ/XIH/XIHFE/XIH-S/XIM) — modular cabinet wall
    else if (["hisense-htj", "hisense-xih", "hisense-xihfe", "hisense-xih-s", "hisense-xim"].includes(pid)) {
      const p = item.product;
      const cW = p._meta?.cabinetW || 0.6;
      const cH = p._meta?.cabinetH || 0.3375;
      const cD = depth;

      const frameMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.3, metalness: 0.75 });
      const screenMat = new THREE.MeshStandardMaterial({ color: "#010204", roughness: 0, emissive: "#050f1e", emissiveIntensity: 0.6 });
      const seamMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.8 });

      const nCols = Math.max(1, Math.round(width / cW));
      const nRows = Math.max(1, Math.round(height / cH));

      for (let row = 0; row < nRows; row++) {
        for (let col = 0; col < nCols; col++) {
          const ox = -width / 2 + (col + 0.5) * cW;
          const oy = -height / 2 + (row + 0.5) * cH;

          // Cabinet body
          const cab = new THREE.Mesh(new THREE.BoxGeometry(cW - 0.002, cH - 0.002, cD), frameMat);
          cab.position.set(ox, oy, 0); cab.castShadow = true; group.add(cab);

          // Screen per cabinet
          const scr = new THREE.Mesh(new THREE.PlaneGeometry(cW - 0.006, cH - 0.006), screenMat);
          scr.position.set(ox, oy, cD / 2 + 0.001); group.add(scr);

          // Seam lines
          const seamH = new THREE.Mesh(new THREE.BoxGeometry(cW, 0.002, cD + 0.001), seamMat);
          seamH.position.set(ox, oy + cH / 2, 0); group.add(seamH);
          const seamV = new THREE.Mesh(new THREE.BoxGeometry(0.002, cH, cD + 0.001), seamMat);
          seamV.position.set(ox + cW / 2, oy, 0); group.add(seamV);

        }
      }

      // Front maintenance access indicator (green strip top-right per cabinet)
      const accessMat = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 1.5, roughness: 0 });
      const accDot = new THREE.Mesh(new THREE.CircleGeometry(0.006, 8), accessMat);
      accDot.position.set(width / 2 - 0.03, height / 2 - 0.03, cD / 2 + 0.003); group.add(accDot);

      // Cable-less badge (small silver panel bottom)
      const panelMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.5, metalness: 0.8 });
      const panel = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.04, 0.02), panelMat);
      panel.position.set(-width / 2 + 0.05, -height / 2 + 0.025, -cD / 2 - 0.01); group.add(panel);
    }

    // ── HISENSE OUTDOOR LED WALL (RO/ROHFE/XOM/XOMFE) — weatherproof cabinet IP65
    else if (["hisense-ro", "hisense-rohfe", "hisense-xom", "hisense-xomfe"].includes(pid)) {
      const p = item.product;
      const cW = p._meta?.cabinetW || 0.5;
      const cH = p._meta?.cabinetH || 0.5;
      const cD = depth;
      // const isROHFE = pid === "hisense-rohfe";

      // Outdoor cabinet — thicker, weatherproof die-cast aluminium
      const frameMat = new THREE.MeshStandardMaterial({ color: "#141414", roughness: 0.45, metalness: 0.7 });
      const screenMat = new THREE.MeshStandardMaterial({ color: "#020202", roughness: 0.05, emissive: "#0a1a0a", emissiveIntensity: 0.4 });
      const sealMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.9, metalness: 0.1 });

      const nCols = Math.max(1, Math.round(width / cW));
      const nRows = Math.max(1, Math.round(height / cH));

      for (let row = 0; row < nRows; row++) {
        for (let col = 0; col < nCols; col++) {
          const ox = -width / 2 + (col + 0.5) * cW;
          const oy = -height / 2 + (row + 0.5) * cH;

          // Thick outdoor cabinet body
          const cab = new THREE.Mesh(new THREE.BoxGeometry(cW - 0.003, cH - 0.003, cD), frameMat);
          cab.position.set(ox, oy, 0); cab.castShadow = true; group.add(cab);

          // IP65 rubber seal edge
          const seal = new THREE.Mesh(new THREE.BoxGeometry(cW - 0.001, cH - 0.001, cD * 0.1), sealMat);
          seal.position.set(ox, oy, cD / 2 - cD * 0.05); group.add(seal);

          // Screen
          const scr = new THREE.Mesh(new THREE.PlaneGeometry(cW - 0.01, cH - 0.01), screenMat);
          scr.position.set(ox, oy, cD / 2 + 0.001); group.add(scr);

          // Cabinet handles (top + bottom, outdoor style)
          const handleMat = new THREE.MeshStandardMaterial({ color: "#333", roughness: 0.5, metalness: 0.8 });
          const hTop = new THREE.Mesh(new THREE.BoxGeometry(cW * 0.3, 0.018, 0.025), handleMat);
          hTop.position.set(ox, oy + cH / 2 - 0.01, cD / 2 - 0.012); group.add(hTop);

          // Quick-lock connectors (visible on edges)
          const lockMat = new THREE.MeshStandardMaterial({ color: "#555", roughness: 0.3, metalness: 0.9 });
          [-cW * 0.35, cW * 0.35].forEach(lx => {
            const lock = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.014, 8), lockMat);
            lock.rotation.z = Math.PI / 2;
            lock.position.set(ox + lx, oy, cD / 2 + 0.007); group.add(lock);
          });

        }
      }

      // IP65/66 rating badge (bottom corner)
      const ipMat = new THREE.MeshStandardMaterial({ color: "#1a3a5c", roughness: 0.5 });
      const ipBadge = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.025, 0.008), ipMat);
      ipBadge.position.set(-width / 2 + 0.04, -height / 2 + 0.02, cD / 2 + 0.004); group.add(ipBadge);
    }

    // ── HISENSE SPFE STADIUM — ultra-wide perimeter LED, 16:9 cabinet 1600×900
    else if (pid === "hisense-spfe") {
      const cW = 1.6, cH = 0.9, cD = depth;
      const frameMat = new THREE.MeshStandardMaterial({ color: "#0f0f0f", roughness: 0.4, metalness: 0.75 });
      const screenMat = new THREE.MeshStandardMaterial({ color: "#010101", roughness: 0, emissive: "#100800", emissiveIntensity: 0.5 });
      const impactMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.6, metalness: 0.5 });

      const nCols = Math.max(1, Math.round(width / cW));

      for (let col = 0; col < nCols; col++) {
        const ox = -width / 2 + (col + 0.5) * cW;

        const cab = new THREE.Mesh(new THREE.BoxGeometry(cW - 0.003, cH - 0.003, cD), frameMat);
        cab.position.set(ox, 0, 0); cab.castShadow = true; group.add(cab);

        const scr = new THREE.Mesh(new THREE.PlaneGeometry(cW - 0.01, cH - 0.01), screenMat);
        scr.position.set(ox, 0, cD / 2 + 0.001); group.add(scr);

        // Impact-resistant face shield (cushioned edge)
        const shield = new THREE.Mesh(new THREE.BoxGeometry(cW - 0.005, 0.04, cD * 0.4), impactMat);
        shield.position.set(ox, cH / 2 - 0.02, cD * 0.3); group.add(shield);
        const shieldB = new THREE.Mesh(new THREE.BoxGeometry(cW - 0.005, 0.04, cD * 0.4), impactMat);
        shieldB.position.set(ox, -cH / 2 + 0.02, cD * 0.3); group.add(shieldB);

        // Rotation hinge (perimeter displays can rotate 90°)
        const hingeMat = new THREE.MeshStandardMaterial({ color: "#555", roughness: 0.3, metalness: 0.9 });
        const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.04, 10), hingeMat);
        hinge.rotation.z = Math.PI / 2;
        hinge.position.set(ox, -cH / 2 + 0.02, -cD / 2 - 0.02); group.add(hinge);
      }
    }


    // ─────────────────────────────────────────────
    // AVer CAM520 Pro3 — Full HD PTZ
    // Yoke-style U-bracket, large sphere head, prominent lens
    // Base: flat oval with AVer logo strip, blue LED
    // ─────────────────────────────────────────────
    else if (pid === "aver-cam-520pro3") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.35, metalness: 0.55 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });
      // const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a1a", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#050510", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.15, metalness: 0.9 });
      const blueLedMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2, roughness: 0 });

      // — OVAL BASE —
      // Flat oval trapezoid base, wider than tall
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.095, 0.110, 0.038, 40, 1, false), bodyMat);
      base.castShadow = true;
      base.position.set(0, -0.085, 0);
      group.add(base);

      // Base front bevel strip (logo area)
      const logoStrip = new THREE.Mesh(new THREE.BoxGeometry(0.120, 0.020, 0.010), darkMat);
      logoStrip.position.set(0, -0.075, 0.100);
      group.add(logoStrip);

      // Blue LED on base front
      const blueLed = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.006, 0.003), blueLedMat);
      blueLed.position.set(0, -0.082, 0.111);
      group.add(blueLed);

      // — YOKE / U-BRACKET —
      // Left arm
      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.110, 0.022), bodyMat);
      yokeL.position.set(-0.068, 0.010, 0.005);
      group.add(yokeL);

      // Right arm
      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.110, 0.022), bodyMat);
      yokeR.position.set(0.068, 0.010, 0.005);
      group.add(yokeR);

      // Yoke bottom connector (joins arms to base)
      const yokeBase = new THREE.Mesh(new THREE.BoxGeometry(0.158, 0.022, 0.022), bodyMat);
      yokeBase.position.set(0, -0.042, 0.005);
      group.add(yokeBase);

      // Yoke top arc (curved top of U) — use torus segment
      const yokeArc = new THREE.Mesh(new THREE.TorusGeometry(0.068, 0.011, 12, 24, Math.PI), bodyMat);
      yokeArc.rotation.z = Math.PI;
      yokeArc.position.set(0, 0.065, 0.005);
      group.add(yokeArc);

      // — CAMERA HEAD (sphere) —
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.058, 32, 24), bodyMat);
      head.position.set(0, 0.018, 0.005);
      group.add(head);

      // — LENS ASSEMBLY (large, protruding) —
      // Outer barrel
      const barrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.044, 0.055, 28), lensRingMat);
      barrel1.rotation.x = Math.PI / 2;
      barrel1.position.set(0, 0.018, 0.068);
      group.add(barrel1);

      // Inner barrel
      const barrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.040, 0.030, 28), bodyMat);
      barrel2.rotation.x = Math.PI / 2;
      barrel2.position.set(0, 0.018, 0.090);
      group.add(barrel2);

      // Lens glass (multi-layer)
      // const lensOuter = new THREE.Mesh(new THREE.CircleGeometry(0.034, 28), glassMat);
      // lensOuter.position.set(0, 0.018, 0.106);
      // group.add(lensOuter);

      const lensMid = new THREE.Mesh(new THREE.CircleGeometry(0.024, 24),
        new THREE.MeshStandardMaterial({ color: "#1a1060", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      lensMid.position.set(0, 0.018, 0.1075);
      group.add(lensMid);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.013, 20), lensMat);
      lensCore.position.set(0, 0.018, 0.109);
      group.add(lensCore);

      // Lens rim chrome ring
      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.034, 0.004, 8, 28), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0.018, 0.105);
      group.add(lensRim);

      // "12X ZOOM LENS" text label plane on top of head
      const labelMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5, metalness: 0.2 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.060, 0.005, 0.018), labelMat);
      label.position.set(0, 0.060, 0.010);
      group.add(label);
    }

    // ─────────────────────────────────────────────
    // AVer CAM540 — 4K PTZ
    // Very similar yoke style to 520Pro3 but slightly larger,
    // slightly bigger lens, same oval base with blue LED
    // ─────────────────────────────────────────────
    else if (pid === "aver-cam-540") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.35, metalness: 0.55 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });
      // const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a1a", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#050510", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.15, metalness: 0.9 });
      const blueLedMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2, roughness: 0 });

      // — OVAL BASE — slightly larger than 520
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.105, 0.122, 0.040, 40), bodyMat);
      base.castShadow = true;
      base.position.set(0, -0.090, 0);
      group.add(base);

      const logoStrip = new THREE.Mesh(new THREE.BoxGeometry(0.130, 0.022, 0.010), darkMat);
      logoStrip.position.set(0, -0.080, 0.110);
      group.add(logoStrip);

      const blueLed = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.007, 0.003), blueLedMat);
      blueLed.position.set(0, -0.088, 0.122);
      group.add(blueLed);

      // — YOKE —
      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.120, 0.024), bodyMat);
      yokeL.position.set(-0.075, 0.012, 0.005);
      group.add(yokeL);

      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.120, 0.024), bodyMat);
      yokeR.position.set(0.075, 0.012, 0.005);
      group.add(yokeR);

      const yokeBase = new THREE.Mesh(new THREE.BoxGeometry(0.174, 0.024, 0.024), bodyMat);
      yokeBase.position.set(0, -0.048, 0.005);
      group.add(yokeBase);

      const yokeArc = new THREE.Mesh(new THREE.TorusGeometry(0.075, 0.012, 12, 24, Math.PI), bodyMat);
      yokeArc.rotation.z = Math.PI;
      yokeArc.position.set(0, 0.072, 0.005);
      group.add(yokeArc);

      // — CAMERA HEAD —
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.063, 32, 24), bodyMat);
      head.position.set(0, 0.020, 0.005);
      group.add(head);

      // — LENS (4K, bigger) —
      const barrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.050, 0.060, 28), lensRingMat);
      barrel1.rotation.x = Math.PI / 2;
      barrel1.position.set(0, 0.020, 0.074);
      group.add(barrel1);

      const barrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.045, 0.032, 28), bodyMat);
      barrel2.rotation.x = Math.PI / 2;
      barrel2.position.set(0, 0.020, 0.098);
      group.add(barrel2);

      // const lensOuter = new THREE.Mesh(new THREE.CircleGeometry(0.038, 28), glassMat);
      // lensOuter.position.set(0, 0.020, 0.115);
      // group.add(lensOuter);

      const lensMid = new THREE.Mesh(new THREE.CircleGeometry(0.028, 24),
        new THREE.MeshStandardMaterial({ color: "#1a1060", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      lensMid.position.set(0, 0.020, 0.1165);
      group.add(lensMid);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.015, 20), lensMat);
      lensCore.position.set(0, 0.020, 0.118);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.038, 0.005, 8, 28), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0.020, 0.113);
      group.add(lensRim);

      const labelMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5, metalness: 0.2 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.065, 0.005, 0.020), labelMat);
      label.position.set(0, 0.066, 0.012);
      group.add(label);
    }

    // ─────────────────────────────────────────────
    // AVer CAM550 — 4K Dual-Lens PTZ
    // Same yoke architecture, but head has 2 lenses (main + AI tracking)
    // Base has speaker grille on side (VC bundle)
    // ─────────────────────────────────────────────
    else if (pid === "aver-cam-550") {
      const cW = 0.1708, cH = 0.1905, cD = 0.173;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.42, metalness: 0.25 });
      const bodyMatSft = new THREE.MeshStandardMaterial({ color: "#202020", roughness: 0.48, metalness: 0.20 });
      const fabricMat = new THREE.MeshStandardMaterial({ color: "#3a3a3c", roughness: 0.95, metalness: 0.0 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a14", roughness: 0, metalness: 0.85, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#020205", roughness: 0, metalness: 1 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#0f0f0f", roughness: 0.25, metalness: 0.75 });
      const rim2Mat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.30, metalness: 0.65 });
      const ledBlueMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2.2, roughness: 0 });
      const logoMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.4, metalness: 0.3 });

      // ── 1. BASE — persegi rounded, memanjang ──
      const baseW = cW * 1.05, baseH = cH * 0.30, baseD = cD * 0.95;

      const baseCore = new THREE.Mesh(new THREE.BoxGeometry(baseW - 0.024, baseH, baseD - 0.024), bodyMat);
      baseCore.castShadow = true;
      baseCore.position.set(0, -cH * 0.46, 0);
      group.add(baseCore);

      const cr = 0.012;
      [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) => {
        const corner = new THREE.Mesh(new THREE.CylinderGeometry(cr, cr, baseH, 16), bodyMat);
        corner.position.set(sx * (baseW / 2 - cr), -cH * 0.46, sz * (baseD / 2 - cr));
        group.add(corner);
      });
      const edgeFront = new THREE.Mesh(new THREE.BoxGeometry(baseW - cr * 2, baseH, cr * 2), bodyMat);
      edgeFront.position.set(0, -cH * 0.46, baseD / 2 - cr);
      group.add(edgeFront);
      const edgeBack = new THREE.Mesh(new THREE.BoxGeometry(baseW - cr * 2, baseH, cr * 2), bodyMat);
      edgeBack.position.set(0, -cH * 0.46, -(baseD / 2 - cr));
      group.add(edgeBack);
      const edgeLeft = new THREE.Mesh(new THREE.BoxGeometry(cr * 2, baseH, baseD - cr * 2), bodyMat);
      edgeLeft.position.set(-(baseW / 2 - cr), -cH * 0.46, 0);
      group.add(edgeLeft);
      const edgeRight = new THREE.Mesh(new THREE.BoxGeometry(cr * 2, baseH, baseD - cr * 2), bodyMat);
      edgeRight.position.set(baseW / 2 - cr, -cH * 0.46, 0);
      group.add(edgeRight);

      // ── 2. FABRIC GRILLE — sisi KIRI base depan (ventilasi diagonal) ──
      const grille = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.34, baseH * 0.70, 0.006), fabricMat);
      grille.position.set(-baseW * 0.30, -cH * 0.44, baseD / 2 + 0.001);
      group.add(grille);

      for (let i = 0; i < 6; i++) {
        const line = new THREE.Mesh(new THREE.BoxGeometry(0.0015, baseH * 0.65, 0.002),
          new THREE.MeshStandardMaterial({ color: "#2a2a2c", roughness: 1 }));
        line.position.set(-baseW * 0.42 + i * 0.009, -cH * 0.44, baseD / 2 + 0.003);
        group.add(line);
      }

      // ── 3. AI/SECONDARY LENS — kecil, bulat, di base depan kanan-tengah ──
      const aiLensRing = new THREE.Mesh(new THREE.TorusGeometry(0.014, 0.0025, 8, 24), rim2Mat);
      aiLensRing.position.set(baseW * 0.02, -cH * 0.40, baseD / 2 + 0.003);
      group.add(aiLensRing);

      const aiLensGlass = new THREE.Mesh(new THREE.CircleGeometry(0.012, 20), glassMat);
      aiLensGlass.position.set(baseW * 0.02, -cH * 0.40, baseD / 2 + 0.004);
      group.add(aiLensGlass);

      const aiLensCore = new THREE.Mesh(new THREE.CircleGeometry(0.006, 16), lensMat);
      aiLensCore.position.set(baseW * 0.02, -cH * 0.40, baseD / 2 + 0.005);
      group.add(aiLensCore);

      // ── 4. AVer LOGO — kanan dari AI lens ──
      const logoText = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.18, 0.014, 0.003), logoMat);
      logoText.position.set(baseW * 0.30, -cH * 0.40, baseD / 2 + 0.002);
      group.add(logoText);

      // ── 5. BLUE LED STRIP — sepanjang bawah base depan ──
      const ledStrip = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.92, 0.006, 0.004), ledBlueMat);
      ledStrip.position.set(0, -cH * 0.555, baseD / 2 - 0.005);
      group.add(ledStrip);

      // LED glow effect
      const ledGlow = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.92, 0.012, 0.006),
        new THREE.MeshStandardMaterial({ color: "#93c5fd", emissive: "#60a5fa", emissiveIntensity: 1.2, roughness: 0, transparent: true, opacity: 0.4 }));
      ledGlow.position.set(0, -cH * 0.555, baseD / 2 - 0.004);
      group.add(ledGlow);

      // ── 6. C-SHAPED YOKE — organik, menghubungkan base ke head ──
      // Base of yoke kiri (silinder naik dari base)
      const yokeBaseL = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.044, cH * 0.32, 24), bodyMatSft);
      yokeBaseL.position.set(-cW * 0.32, -cH * 0.18, -cD * 0.05);
      group.add(yokeBaseL);

      // Lengkungan besar dari kiri-bawah ke atas (membentuk huruf C, menutupi head dari belakang)
      const yokeArc = new THREE.Mesh(new THREE.TorusGeometry(cW * 0.42, 0.044, 20, 32, Math.PI * 1.05), bodyMatSft);
      yokeArc.position.set(-cW * 0.0, cH * 0.10, -cD * 0.05);
      group.add(yokeArc);

      // Lengan kanan (turun dari arc, menopang head dari kanan-belakang)
      const yokeArmR = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.036, cH * 0.28, 24), bodyMatSft);
      yokeArmR.rotation.x = -0.30;
      yokeArmR.position.set(cW * 0.36, cH * 0.04, cD * 0.06);
      group.add(yokeArmR);

      // Knuckle penghubung yoke kanan ke head (rounded joint)
      const yokeKnuckle = new THREE.Mesh(new THREE.SphereGeometry(0.040, 20, 16), bodyMatSft);
      yokeKnuckle.position.set(cW * 0.38, cH * 0.16, cD * 0.10);
      group.add(yokeKnuckle);

      // ── 7. CAMERA HEAD — sphere besar ──
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.078, 40, 32), bodyMat);
      head.position.set(0, cH * 0.16, cD * 0.05);
      group.add(head);

      // ── 8. LENS ASSEMBLY — besar, menonjol ke depan ──
      const lensOuterRing = new THREE.Mesh(new THREE.CylinderGeometry(0.058, 0.062, 0.018, 36), rim2Mat);
      lensOuterRing.rotation.x = Math.PI / 2;
      lensOuterRing.position.set(0, cH * 0.16, cD * 0.05 + 0.068);
      group.add(lensOuterRing);

      const lensBarrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.053, 0.056, 0.064, 34), rimMat);
      lensBarrel1.rotation.x = Math.PI / 2;
      lensBarrel1.position.set(0, cH * 0.16, cD * 0.05 + 0.082);
      group.add(lensBarrel1);

      const lensBarrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.044, 0.050, 0.036, 30), bodyMat);
      lensBarrel2.rotation.x = Math.PI / 2;
      lensBarrel2.position.set(0, cH * 0.16, cD * 0.05 + 0.110);
      group.add(lensBarrel2);

      const lensBarrel3 = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.043, 0.020, 28), rimMat);
      lensBarrel3.rotation.x = Math.PI / 2;
      lensBarrel3.position.set(0, cH * 0.16, cD * 0.05 + 0.126);
      group.add(lensBarrel3);

      // Glass layers
      const lensGlassOuter = new THREE.Mesh(new THREE.CircleGeometry(0.036, 32), glassMat);
      lensGlassOuter.position.set(0, cH * 0.16, cD * 0.05 + 0.138);
      group.add(lensGlassOuter);

      const lensGlassMid = new THREE.Mesh(new THREE.CircleGeometry(0.028, 26),
        new THREE.MeshStandardMaterial({ color: "#1a1a08", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      lensGlassMid.position.set(0, cH * 0.16, cD * 0.05 + 0.1395);
      group.add(lensGlassMid);

      const lensGlassInner = new THREE.Mesh(new THREE.CircleGeometry(0.019, 20),
        new THREE.MeshStandardMaterial({ color: "#1f1505", roughness: 0, metalness: 0.85, transparent: true, opacity: 0.9 }));
      lensGlassInner.position.set(0, cH * 0.16, cD * 0.05 + 0.141);
      group.add(lensGlassInner);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.011, 16), lensMat);
      lensCore.position.set(0, cH * 0.16, cD * 0.05 + 0.1425);
      group.add(lensCore);

      const lensRimOuter = new THREE.Mesh(new THREE.TorusGeometry(0.036, 0.004, 9, 32), rim2Mat);
      lensRimOuter.rotation.x = Math.PI / 2;
      lensRimOuter.position.set(0, cH * 0.16, cD * 0.05 + 0.137);
      group.add(lensRimOuter);

      // Lens highlight
      const lensHL = new THREE.Mesh(new THREE.CircleGeometry(0.006, 10),
        new THREE.MeshStandardMaterial({ color: "#8a6a2a", emissive: "#5a4a1a", emissiveIntensity: 0.5, roughness: 0 }));
      lensHL.position.set(-0.016, cH * 0.16 + 0.022, cD * 0.05 + 0.1435);
      group.add(lensHL);

      // ── 9. "12X ZOOM 4K" LABEL — di atas lensa ──
      const labelBg = new THREE.Mesh(new THREE.PlaneGeometry(0.034, 0.012),
        new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.6 }));
      labelBg.position.set(0, cH * 0.16 + 0.060, cD * 0.05 + 0.095);
      labelBg.rotation.x = -0.35;
      group.add(labelBg);

      const labelText = new THREE.Mesh(new THREE.PlaneGeometry(0.028, 0.004),
        new THREE.MeshStandardMaterial({ color: "#e5e5e5", roughness: 0.5 }));
      labelText.position.set(0, cH * 0.16 + 0.062, cD * 0.05 + 0.0965);
      labelText.rotation.x = -0.35;
      group.add(labelText);

      const fourKDot = new THREE.Mesh(new THREE.CircleGeometry(0.0025, 8),
        new THREE.MeshStandardMaterial({ color: "#f59e0b", emissive: "#f59e0b", emissiveIntensity: 1.2, roughness: 0 }));
      fourKDot.position.set(0.012, cH * 0.16 + 0.063, cD * 0.05 + 0.0968);
      fourKDot.rotation.x = -0.35;
      group.add(fourKDot);

      // ── 10. BACK PORTS ──
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 });
      [[-0.030, 0], [0.005, 0], [0.040, 0]].forEach(([px, py]) => {
        const port = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.009, 0.005), portMat);
        port.position.set(px, -cH * 0.40 + py, -baseD / 2 - 0.002);
        group.add(port);
      });

      // ── 11. WALL MOUNT BRACKET ──
      const mountMat = new THREE.MeshStandardMaterial({ color: "#222", roughness: 0.4, metalness: 0.8 });
      const mountBar = new THREE.Mesh(new THREE.BoxGeometry(baseW * 0.7, 0.012, 0.030), mountMat);
      mountBar.position.set(0, -cH * 0.59, -baseD * 0.30);
      group.add(mountBar);
    }

    // ─────────────────────────────────────────────
    // AVer VC520 Pro3 — same camera body as CAM520 Pro3
    // ─────────────────────────────────────────────
    else if (pid === "aver-vc-520pro3") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.35, metalness: 0.55 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });
      // const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a1a", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#050510", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.15, metalness: 0.9 });
      const blueLedMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2, roughness: 0 });

      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.095, 0.110, 0.038, 40), bodyMat);
      base.castShadow = true;
      base.position.set(0, -0.085, 0);
      group.add(base);

      const logoStrip = new THREE.Mesh(new THREE.BoxGeometry(0.120, 0.020, 0.010), darkMat);
      logoStrip.position.set(0, -0.075, 0.100);
      group.add(logoStrip);

      const blueLed = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.006, 0.003), blueLedMat);
      blueLed.position.set(0, -0.082, 0.111);
      group.add(blueLed);

      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.110, 0.022), bodyMat);
      yokeL.position.set(-0.068, 0.010, 0.005);
      group.add(yokeL);

      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.110, 0.022), bodyMat);
      yokeR.position.set(0.068, 0.010, 0.005);
      group.add(yokeR);

      const yokeBase = new THREE.Mesh(new THREE.BoxGeometry(0.158, 0.022, 0.022), bodyMat);
      yokeBase.position.set(0, -0.042, 0.005);
      group.add(yokeBase);

      const yokeArc = new THREE.Mesh(new THREE.TorusGeometry(0.068, 0.011, 12, 24, Math.PI), bodyMat);
      yokeArc.rotation.z = Math.PI;
      yokeArc.position.set(0, 0.065, 0.005);
      group.add(yokeArc);

      const head = new THREE.Mesh(new THREE.SphereGeometry(0.058, 32, 24), bodyMat);
      head.position.set(0, 0.018, 0.005);
      group.add(head);

      const barrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.044, 0.055, 28), lensRingMat);
      barrel1.rotation.x = Math.PI / 2;
      barrel1.position.set(0, 0.018, 0.068);
      group.add(barrel1);

      const barrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.040, 0.030, 28), bodyMat);
      barrel2.rotation.x = Math.PI / 2;
      barrel2.position.set(0, 0.018, 0.090);
      group.add(barrel2);

      // const lensOuter = new THREE.Mesh(new THREE.CircleGeometry(0.034, 28), glassMat);
      // lensOuter.position.set(0, 0.018, 0.106);
      // group.add(lensOuter);

      const lensMid = new THREE.Mesh(new THREE.CircleGeometry(0.024, 24),
        new THREE.MeshStandardMaterial({ color: "#1a1060", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      lensMid.position.set(0, 0.018, 0.1075);
      group.add(lensMid);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.013, 20), lensMat);
      lensCore.position.set(0, 0.018, 0.109);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.034, 0.004, 8, 28), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0.018, 0.105);
      group.add(lensRim);

      const labelMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5, metalness: 0.2 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.060, 0.005, 0.018), labelMat);
      label.position.set(0, 0.060, 0.010);
      group.add(label);
    }

    // ─────────────────────────────────────────────
    // AVer VC540 — same camera body as CAM540
    // ─────────────────────────────────────────────
    else if (pid === "aver-vc-540") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.35, metalness: 0.55 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });
      // const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a1a", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#050510", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.15, metalness: 0.9 });
      const blueLedMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2, roughness: 0 });

      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.105, 0.122, 0.040, 40), bodyMat);
      base.castShadow = true;
      base.position.set(0, -0.090, 0);
      group.add(base);

      const logoStrip = new THREE.Mesh(new THREE.BoxGeometry(0.130, 0.022, 0.010), darkMat);
      logoStrip.position.set(0, -0.080, 0.110);
      group.add(logoStrip);

      const blueLed = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.007, 0.003), blueLedMat);
      blueLed.position.set(0, -0.088, 0.122);
      group.add(blueLed);

      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.120, 0.024), bodyMat);
      yokeL.position.set(-0.075, 0.012, 0.005);
      group.add(yokeL);

      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.120, 0.024), bodyMat);
      yokeR.position.set(0.075, 0.012, 0.005);
      group.add(yokeR);

      const yokeBase = new THREE.Mesh(new THREE.BoxGeometry(0.174, 0.024, 0.024), bodyMat);
      yokeBase.position.set(0, -0.048, 0.005);
      group.add(yokeBase);

      const yokeArc = new THREE.Mesh(new THREE.TorusGeometry(0.075, 0.012, 12, 24, Math.PI), bodyMat);
      yokeArc.rotation.z = Math.PI;
      yokeArc.position.set(0, 0.072, 0.005);
      group.add(yokeArc);

      const head = new THREE.Mesh(new THREE.SphereGeometry(0.063, 32, 24), bodyMat);
      head.position.set(0, 0.020, 0.005);
      group.add(head);

      const barrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.050, 0.060, 28), lensRingMat);
      barrel1.rotation.x = Math.PI / 2;
      barrel1.position.set(0, 0.020, 0.074);
      group.add(barrel1);

      const barrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.045, 0.032, 28), bodyMat);
      barrel2.rotation.x = Math.PI / 2;
      barrel2.position.set(0, 0.020, 0.098);
      group.add(barrel2);

      // const lensOuter = new THREE.Mesh(new THREE.CircleGeometry(0.038, 28), glassMat);
      // lensOuter.position.set(0, 0.020, 0.115);
      // group.add(lensOuter);

      const lensMid = new THREE.Mesh(new THREE.CircleGeometry(0.028, 24),
        new THREE.MeshStandardMaterial({ color: "#1a1060", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      lensMid.position.set(0, 0.020, 0.1165);
      group.add(lensMid);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.015, 20), lensMat);
      lensCore.position.set(0, 0.020, 0.118);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.038, 0.005, 8, 28), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0.020, 0.113);
      group.add(lensRim);

      const labelMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5, metalness: 0.2 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.065, 0.005, 0.020), labelMat);
      label.position.set(0, 0.066, 0.012);
      group.add(label);
    }

    // ─────────────────────────────────────────────
    // AVer VC550 — same camera body as CAM550 (dual-lens)
    // ─────────────────────────────────────────────
    else if (pid === "aver-vc-550") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.35, metalness: 0.55 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#0a0a1a", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#050510", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.15, metalness: 0.9 });
      const blueLedMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2, roughness: 0 });

      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.108, 0.125, 0.042, 40), bodyMat);
      base.castShadow = true;
      base.position.set(0, -0.095, 0);
      group.add(base);

      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 5; col++) {
          const dot = new THREE.Mesh(new THREE.CircleGeometry(0.002, 6),
            new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 1 }));
          dot.position.set(0.085 + col * 0.007, -0.082 + row * 0.008, 0.088);
          dot.rotation.y = -0.4;
          group.add(dot);
        }
      }

      const logoStrip = new THREE.Mesh(new THREE.BoxGeometry(0.135, 0.022, 0.010), darkMat);
      logoStrip.position.set(0, -0.084, 0.114);
      group.add(logoStrip);

      const blueLed = new THREE.Mesh(new THREE.BoxGeometry(0.026, 0.007, 0.003), blueLedMat);
      blueLed.position.set(0, -0.092, 0.126);
      group.add(blueLed);

      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.125, 0.025), bodyMat);
      yokeL.position.set(-0.078, 0.014, 0.006);
      group.add(yokeL);

      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.125, 0.025), bodyMat);
      yokeR.position.set(0.078, 0.014, 0.006);
      group.add(yokeR);

      const yokeBase = new THREE.Mesh(new THREE.BoxGeometry(0.181, 0.025, 0.025), bodyMat);
      yokeBase.position.set(0, -0.050, 0.006);
      group.add(yokeBase);

      const yokeArc = new THREE.Mesh(new THREE.TorusGeometry(0.078, 0.0125, 12, 24, Math.PI), bodyMat);
      yokeArc.rotation.z = Math.PI;
      yokeArc.position.set(0, 0.076, 0.006);
      group.add(yokeArc);

      const head = new THREE.Mesh(new THREE.SphereGeometry(0.066, 32, 24), bodyMat);
      head.position.set(0, 0.022, 0.006);
      group.add(head);

      const barrel1 = new THREE.Mesh(new THREE.CylinderGeometry(0.050, 0.052, 0.065, 28), lensRingMat);
      barrel1.rotation.x = Math.PI / 2;
      barrel1.position.set(0, 0.022, 0.078);
      group.add(barrel1);

      const barrel2 = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.047, 0.034, 28), bodyMat);
      barrel2.rotation.x = Math.PI / 2;
      barrel2.position.set(0, 0.022, 0.104);
      group.add(barrel2);

      // const lensOuter = new THREE.Mesh(new THREE.CircleGeometry(0.040, 28), glassMat);
      // lensOuter.position.set(0, 0.022, 0.122);
      // group.add(lensOuter);

      const lensMid = new THREE.Mesh(new THREE.CircleGeometry(0.029, 24),
        new THREE.MeshStandardMaterial({ color: "#1a1060", roughness: 0, metalness: 0.8, transparent: true, opacity: 0.85 }));
      lensMid.position.set(0, 0.022, 0.1235);
      group.add(lensMid);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.016, 20), lensMat);
      lensCore.position.set(0, 0.022, 0.125);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.040, 0.005, 8, 28), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0.022, 0.120);
      group.add(lensRim);

      // Small AI tracking lens
      const aiBarrel = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.016, 0.022, 16), bodyMat);
      aiBarrel.rotation.x = Math.PI / 2;
      aiBarrel.position.set(0.038, -0.020, 0.058);
      group.add(aiBarrel);

      const aiGlass = new THREE.Mesh(new THREE.CircleGeometry(0.011, 14), glassMat);
      aiGlass.position.set(0.038, -0.020, 0.070);
      group.add(aiGlass);

      const aiCore = new THREE.Mesh(new THREE.CircleGeometry(0.006, 12), lensMat);
      aiCore.position.set(0.038, -0.020, 0.072);
      group.add(aiCore);

      const labelMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5, metalness: 0.2 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.070, 0.005, 0.022), labelMat);
      label.position.set(0, 0.070, 0.014);
      group.add(label);
    }

    // ─────────────────────────────────────────────
    // AVer CAM130 — Wide angle conference bar camera
    // Horizontal bar body, BIG white ring light around lens,
    // centered lens, clip/stand at bottom, white/black two-tone
    // ─────────────────────────────────────────────
    else if (pid === "aver-cam-130") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.25, metalness: 0.6 });
      const whiteMat = new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.3, metalness: 0.1 });
      // const glassMat = new THREE.MeshStandardMaterial({ color: "#080818", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#020208", roughness: 0, metalness: 1 });
      const ringLightMat = new THREE.MeshStandardMaterial({
        color: "#ffffff", emissive: "#ffffff", emissiveIntensity: 1.8, roughness: 0.1, transparent: true, opacity: 0.95
      });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });

      // — MAIN BAR BODY — white face plate, black back
      // Back body (black)
      const bodyBack = new THREE.Mesh(new THREE.BoxGeometry(0.175, 0.088, 0.048), bodyMat);
      bodyBack.castShadow = true;
      bodyBack.position.set(0, 0, 0);
      group.add(bodyBack);

      // Front face plate (dark, recessed)
      const facePlate = new THREE.Mesh(new THREE.BoxGeometry(0.168, 0.082, 0.005), bodyMat);
      facePlate.position.set(0, 0, 0.026);
      group.add(facePlate);

      // White ring light bezel (the big white rectangle border)
      const ringBezel = new THREE.Mesh(new THREE.BoxGeometry(0.165, 0.079, 0.004), whiteMat);
      ringBezel.position.set(0, 0, 0.029);
      group.add(ringBezel);

      // Inner black area (inside ring light)
      const innerBlack = new THREE.Mesh(new THREE.BoxGeometry(0.085, 0.062, 0.003), bodyMat);
      innerBlack.position.set(0, 0, 0.031);
      group.add(innerBlack);

      // Ring light - top bar
      const ringTop = new THREE.Mesh(new THREE.BoxGeometry(0.155, 0.010, 0.004), ringLightMat);
      ringTop.position.set(0, 0.034, 0.030);
      group.add(ringTop);

      // Ring light - bottom bar
      const ringBottom = new THREE.Mesh(new THREE.BoxGeometry(0.155, 0.010, 0.004), ringLightMat);
      ringBottom.position.set(0, -0.034, 0.030);
      group.add(ringBottom);

      // Ring light - left bar
      const ringLeft = new THREE.Mesh(new THREE.BoxGeometry(0.010, 0.060, 0.004), ringLightMat);
      ringLeft.position.set(-0.077, 0, 0.030);
      group.add(ringLeft);

      // Ring light - right bar
      const ringRight = new THREE.Mesh(new THREE.BoxGeometry(0.010, 0.060, 0.004), ringLightMat);
      ringRight.position.set(0.077, 0, 0.030);
      group.add(ringRight);

      // Ring light corners (rounded feel)
      [[-0.072, 0.030], [0.072, 0.030], [-0.072, -0.030], [0.072, -0.030]].forEach(([cx, cy]) => {
        const corner = new THREE.Mesh(new THREE.BoxGeometry(0.010, 0.010, 0.004), ringLightMat);
        corner.position.set(cx, cy, 0.030);
        group.add(corner);
      });

      // — LENS ASSEMBLY — centered
      const lensMount = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.024, 0.016, 22), bodyMat);
      lensMount.rotation.x = Math.PI / 2;
      lensMount.position.set(0, 0, 0.038);
      group.add(lensMount);

      // const lensOuter = new THREE.Mesh(new THREE.CircleGeometry(0.018, 22), glassMat);
      // lensOuter.position.set(0, 0, 0.047);
      // group.add(lensOuter);

      const lensMid2 = new THREE.Mesh(new THREE.CircleGeometry(0.013, 18),
        new THREE.MeshStandardMaterial({ color: "#101050", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.9 }));
      lensMid2.position.set(0, 0, 0.0485);
      group.add(lensMid2);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.007, 14), lensMat);
      lensCore.position.set(0, 0, 0.050);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.018, 0.003, 8, 22), darkMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0, 0.046);
      group.add(lensRim);

      // Small LED dots (left and right of lens, on face)
      const ledOrangeMat = new THREE.MeshStandardMaterial({ color: "#f97316", emissive: "#f97316", emissiveIntensity: 1.5, roughness: 0 });
      const ledL = new THREE.Mesh(new THREE.CircleGeometry(0.003, 8), ledOrangeMat);
      ledL.position.set(-0.025, 0, 0.032);
      group.add(ledL);

      const ledGreenMat = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 1.5, roughness: 0 });
      const ledR = new THREE.Mesh(new THREE.CircleGeometry(0.003, 8), ledGreenMat);
      ledR.position.set(0.025, 0, 0.032);
      group.add(ledR);

      // Rounded end caps (left & right)
      [-0.0875, 0.0875].forEach((ex) => {
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.044, 0.044, 0.048, 20), bodyMat);
        cap.rotation.x = Math.PI / 2;
        cap.position.set(ex, 0, 0);
        group.add(cap);
      });

      // — CLIP / STAND at bottom —
      const clipBody = new THREE.Mesh(new THREE.BoxGeometry(0.068, 0.042, 0.036), bodyMat);
      clipBody.position.set(0, -0.065, 0.006);
      group.add(clipBody);

      // Clip logo strip (AVer branding)
      const clipLogoStrip = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.015, 0.004), darkMat);
      clipLogoStrip.position.set(0, -0.060, 0.025);
      group.add(clipLogoStrip);

      // Clip hinge
      const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.050, 12), darkMat);
      hinge.rotation.z = Math.PI / 2;
      hinge.position.set(0, -0.044, 0.002);
      group.add(hinge);
    }

    // ─────────────────────────────────────────────
    // EXTRON FF 120T — 1'×2' ceiling tile speaker
    // White perforated grille, flat rectangular body,
    // horn driver centered, T-bar mounting, ultra-low profile
    // ─────────────────────────────────────────────
    else if (pid === "extron-ff120t") {
      const tW = 0.295; // tile width (30.5cm minus seams)
      const tD = 0.595; // tile depth (61cm minus seams)
      const tH = 0.050; // body depth (8.3cm)

      // Main tile body (aluminized composite enclosure)
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.6, metalness: 0.15 });
      const body = new THREE.Mesh(new THREE.BoxGeometry(tW, tH, tD), bodyMat);
      body.castShadow = true;
      group.add(body);

      // White perforated grille face (bottom face — faces down toward room)
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.5, metalness: 0.1 });
      const grille = new THREE.Mesh(new THREE.BoxGeometry(tW, 0.004, tD), grilleMat);
      grille.position.y = -tH / 2 + 0.002;
      group.add(grille);

      // Perforation pattern — small dot grid on grille
      const holeMat = new THREE.MeshStandardMaterial({ color: "#c8c8c8", roughness: 0.8 });
      const cols = 14, rows = 28;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const dot = new THREE.Mesh(new THREE.CircleGeometry(0.005, 6), holeMat);
          dot.rotation.x = Math.PI / 2;
          dot.position.set(
            -tW / 2 + 0.01 + (c / (cols - 1)) * (tW - 0.02),
            -tH / 2 - 0.001,
            -tD / 2 + 0.01 + (r / (rows - 1)) * (tD - 0.02)
          );
          group.add(dot);
        }
      }

      // Horn driver unit (visible from grille side, centered)
      const hornMat = new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.3, metalness: 0.4 });
      const hornRing = new THREE.Mesh(new THREE.TorusGeometry(0.038, 0.006, 10, 28), hornMat);
      hornRing.rotation.x = Math.PI / 2;
      hornRing.position.y = -tH / 2 - 0.001;
      group.add(hornRing);

      const hornCenter = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.034, 0.016, 20), hornMat);
      hornCenter.rotation.x = Math.PI / 2;
      hornCenter.position.y = -tH / 2 - 0.005;
      group.add(hornCenter);

      const hornDome = new THREE.Mesh(new THREE.SphereGeometry(0.018, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: "#b0b0b0", roughness: 0.2, metalness: 0.5 }));
      hornDome.rotation.x = Math.PI;
      hornDome.position.y = -tH / 2 - 0.006;
      group.add(hornDome);

      // T-bar mounting frame (silver aluminum rails visible on edges)
      const tbarMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      // Long rails
      [[-tW / 2 - 0.005, 0, 0], [tW / 2 + 0.005, 0, 0]].forEach(([bx, by, bz]) => {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(0.010, tH * 0.4, tD + 0.012), tbarMat);
        rail.position.set(bx, by + tH * 0.15, bz);
        group.add(rail);
      });
      // Short rails
      [[0, 0, -tD / 2 - 0.005], [0, 0, tD / 2 + 0.005]].forEach(([bx, by, bz]) => {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(tW + 0.020, tH * 0.4, 0.010), tbarMat);
        rail.position.set(bx, by + tH * 0.15, bz);
        group.add(rail);
      });

      // Extron brand label (small badge on grille corner)
      const labelMat = new THREE.MeshStandardMaterial({ color: "#505050", roughness: 0.5 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.003, 0.016), labelMat);
      label.position.set(-tW / 2 + 0.035, -tH / 2 - 0.002, tD / 2 - 0.015);
      group.add(label);

      // Wiring connector block on back
      const connMat = new THREE.MeshStandardMaterial({ color: "#3a3a3a", roughness: 0.7 });
      const conn = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.020, 0.025), connMat);
      conn.position.set(0.08, tH / 2 - 0.010, -tD / 2 + 0.020);
      group.add(conn);

      // Volume tap selector (small switch block)
      const tapMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 });
      const tap = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.014, 0.040), tapMat);
      tap.position.set(-0.08, tH / 2 - 0.007, -tD / 2 + 0.030);
      group.add(tap);
    }

    // ─────────────────────────────────────────────
    // EXTRON FF 220T — 2'×2' / 600×600mm ceiling tile speaker
    // Same Flat Field tech, larger square enclosure
    // ─────────────────────────────────────────────
    else if (pid === "extron-ff220t") {
      const tW = 0.593; // tile width (600mm minus seams)
      const tD = 0.593; // tile depth (600mm — square)
      const tH = 0.078; // body depth (8.3cm)

      // Main body
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.6, metalness: 0.15 });
      const body = new THREE.Mesh(new THREE.BoxGeometry(tW, tH, tD), bodyMat);
      body.castShadow = true;
      group.add(body);

      // Grille face (bottom)
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.5, metalness: 0.1 });
      const grille = new THREE.Mesh(new THREE.BoxGeometry(tW, 0.004, tD), grilleMat);
      grille.position.y = -tH / 2 + 0.002;
      group.add(grille);

      // Perforation dots (square tile = denser grid)
      const holeMat = new THREE.MeshStandardMaterial({ color: "#c8c8c8", roughness: 0.8 });
      const cols = 22, rows = 22;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const dot = new THREE.Mesh(new THREE.CircleGeometry(0.006, 6), holeMat);
          dot.rotation.x = Math.PI / 2;
          dot.position.set(
            -tW / 2 + 0.015 + (c / (cols - 1)) * (tW - 0.03),
            -tH / 2 - 0.001,
            -tD / 2 + 0.015 + (r / (rows - 1)) * (tD - 0.03)
          );
          group.add(dot);
        }
      }

      // Horn driver (centered, same as FF 120T but slightly larger area)
      const hornMat = new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.3, metalness: 0.4 });
      const hornRing = new THREE.Mesh(new THREE.TorusGeometry(0.042, 0.007, 10, 28), hornMat);
      hornRing.rotation.x = Math.PI / 2;
      hornRing.position.y = -tH / 2 - 0.001;
      group.add(hornRing);

      const hornBarrel = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.038, 0.020, 20), hornMat);
      hornBarrel.rotation.x = Math.PI / 2;
      hornBarrel.position.y = -tH / 2 - 0.005;
      group.add(hornBarrel);

      const hornDome = new THREE.Mesh(new THREE.SphereGeometry(0.020, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: "#b0b0b0", roughness: 0.2, metalness: 0.5 }));
      hornDome.rotation.x = Math.PI;
      hornDome.position.y = -tH / 2 - 0.006;
      group.add(hornDome);

      // T-bar rails (all 4 sides, square tile)
      const tbarMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      [[-tW / 2 - 0.005, 0], [tW / 2 + 0.005, 0]].forEach(([bx]) => {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(0.010, tH * 0.4, tD + 0.012), tbarMat);
        rail.position.set(bx, tH * 0.15, 0);
        group.add(rail);
      });
      [[0, -tD / 2 - 0.005], [0, tD / 2 + 0.005]].forEach(([, bz]) => {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(tW + 0.020, tH * 0.4, 0.010), tbarMat);
        rail.position.set(0, tH * 0.15, bz);
        group.add(rail);
      });

      // Brand label
      const labelMat = new THREE.MeshStandardMaterial({ color: "#505050", roughness: 0.5 });
      const label = new THREE.Mesh(new THREE.BoxGeometry(0.065, 0.003, 0.018), labelMat);
      label.position.set(-tW / 2 + 0.040, -tH / 2 - 0.002, tD / 2 - 0.018);
      group.add(label);

      // Back connectors
      const connMat = new THREE.MeshStandardMaterial({ color: "#3a3a3a", roughness: 0.7 });
      const conn = new THREE.Mesh(new THREE.BoxGeometry(0.060, 0.022, 0.028), connMat);
      conn.position.set(0.10, tH / 2 - 0.011, -tD / 2 + 0.022);
      group.add(conn);

      const tap = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.016, 0.045),
        new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 }));
      tap.position.set(-0.10, tH / 2 - 0.008, -tD / 2 + 0.032);
      group.add(tap);
    }

    // ─────────────────────────────────────────────
    // EXTRON SF 26CT LP — 6.5" 2-way ceiling speaker, LOW PROFILE back can
    // Round circular speaker: thin bezel ring, composite back can (107mm),
    // woofer cone center, dome tweeter on hexagonal bridge, rotary tap switch
    // ─────────────────────────────────────────────
    else if (pid === "extron-sf26ctlp") {
      const outerR = 0.135;  // outer radius 270mm/2
      const backH = 0.107;  // back can depth 107mm (low profile)
      const drvR = 0.050; // woofer radius 165mm/2

      // ── Back can (composite cylinder, fire-resistant)
      const canMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.7, metalness: 0.1 });
      const backCan = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.94, outerR * 0.94, backH, 40), canMat);
      backCan.position.y = backH / 2;
      backCan.castShadow = true;
      group.add(backCan);

      // ── Baffle ring (white composite, front face)
      const baffleMat = new THREE.MeshStandardMaterial({ color: "#f0f0f0", roughness: 0.45, metalness: 0.05 });
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(outerR, outerR, 0.018, 40), baffleMat);
      baffle.position.y = -0.009;
      group.add(baffle);

      // ── Thin-edge bezel ring (slim plastic rim)
      const bezelMat = new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.3, metalness: 0.15 });
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(outerR - 0.004, 0.005, 10, 40), bezelMat);
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = -0.001;
      group.add(bezel);

      // ── Magnetic grille (white perforated steel disc)
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.5, metalness: 0.12 });
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(outerR - 0.006, outerR - 0.006, 0.004, 40), grilleMat);
      grille.position.y = -0.020;
      group.add(grille);

      // Perforation rings on grille
      const holeMat = new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.8 });
      [0.02, 0.04, 0.06, 0.08, 0.10].forEach(r => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.003, 6, 32), holeMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.022;
        group.add(ring);
      });

      // ── Woofer cone (6.5" polypropylene, visible through grille)
      const wooferMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.6, metalness: 0.1 });
      const woofer = new THREE.Mesh(new THREE.ConeGeometry(drvR, 0.025, 32), wooferMat);
      woofer.rotation.x = Math.PI; // pointing down
      woofer.position.y = -0.036;
      group.add(woofer);

      // Woofer surround ring
      const surrMat = new THREE.MeshStandardMaterial({ color: "#555555", roughness: 0.8, metalness: 0.0 });
      const surround = new THREE.Mesh(new THREE.TorusGeometry(drvR + 0.003, 0.008, 8, 32), surrMat);
      surround.rotation.x = Math.PI / 2;
      surround.position.y = -0.026;
      group.add(surround);

      // ── Hexagonal tweeter bridge + ¾" dome tweeter
      const bridgeMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.3, metalness: 0.3 });
      // 3 bridge arms (like a Y-shape)
      for (let i = 0; i < 3; i++) {
        const angle = (i / 3) * Math.PI * 2;
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.004, drvR * 0.7), bridgeMat);
        arm.position.set(Math.cos(angle) * drvR * 0.35, -0.032, Math.sin(angle) * drvR * 0.35);
        arm.rotation.y = angle;
        group.add(arm);
      }
      // Dome tweeter (¾" = 19mm diameter)
      const twtMat = new THREE.MeshStandardMaterial({ color: "#b0b0c8", roughness: 0.1, metalness: 0.6 });
      const dome = new THREE.Mesh(new THREE.SphereGeometry(0.010, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), twtMat);
      dome.rotation.x = Math.PI;
      dome.position.y = -0.033;
      group.add(dome);

      // ── Rotary power tap switch (behind grille, 6-position)
      const switchMat = new THREE.MeshStandardMaterial({ color: "#444444", roughness: 0.6 });
      const sw = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.010, 12), switchMat);
      sw.position.set(outerR * 0.55, -0.010, 0);
      group.add(sw);

      // ── Terminal block on back (4-pole captive screw)
      const termMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.7 });
      const term = new THREE.Mesh(new THREE.BoxGeometry(0.040, 0.018, 0.025), termMat);
      term.position.set(0, backH - 0.010, outerR * 0.5);
      group.add(term);
      // Green/grey screw heads
      ["#22c55e", "#94a3b8", "#22c55e", "#94a3b8"].forEach((col, i) => {
        const screw = new THREE.Mesh(new THREE.CylinderGeometry(0.003, 0.003, 0.006, 8),
          new THREE.MeshStandardMaterial({ color: col, roughness: 0.3, metalness: 0.8 }));
        screw.rotation.z = Math.PI / 2;
        screw.position.set(-0.015 + i * 0.010, backH - 0.010, outerR * 0.5 + 0.014);
        group.add(screw);
      });

      // ── V-rail mounting clips (3 positions around rim)
      const clipMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.010, 0.008), clipMat);
        clip.position.set(Math.cos(a) * (outerR + 0.012), 0, Math.sin(a) * (outerR + 0.012));
        clip.rotation.y = a;
        group.add(clip);
      }
    }

    // ─────────────────────────────────────────────
    // EXTRON SF 8CT SUB — 8" in-ceiling subwoofer
    // Large round enclosure (324mm Ø), deep back can (367mm),
    // big 8" polyprop woofer, integrated low-pass filter,
    // heavy-duty look, same white grille as SF series
    // ─────────────────────────────────────────────
    else if (pid === "extron-sf8ctsub") {
      const outerR = 0.162;  // 324mm/2
      const backH = 0.330;  // 367mm total, visible back portion
      const drvR = 0.101;  // 8" woofer = 203mm/2

      // ── Composite back can — deep cylinder
      const canMat = new THREE.MeshStandardMaterial({ color: "#222222", roughness: 0.65, metalness: 0.15 });
      const backCan = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.96, outerR * 0.92, backH, 40), canMat);
      backCan.position.y = backH / 2;
      backCan.castShadow = true;
      group.add(backCan);

      // Can ribbing (structural reinforcement ribs)
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        const rib = new THREE.Mesh(new THREE.BoxGeometry(0.008, backH * 0.8, 0.008), canMat);
        rib.position.set(Math.cos(a) * (outerR * 0.95 - 0.004), backH * 0.5, Math.sin(a) * (outerR * 0.95 - 0.004));
        group.add(rib);
      }

      // ── Baffle plate (white composite front)
      const baffleMat = new THREE.MeshStandardMaterial({ color: "#eeeeee", roughness: 0.4, metalness: 0.05 });
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(outerR, outerR, 0.022, 40), baffleMat);
      baffle.position.y = -0.011;
      group.add(baffle);

      // ── Thin bezel edge
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(outerR - 0.004, 0.006, 10, 40),
        new THREE.MeshStandardMaterial({ color: "#dcdcdc", roughness: 0.3, metalness: 0.2 }));
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = 0;
      group.add(bezel);

      // ── Magnetic grille (large white disc)
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#f4f4f4", roughness: 0.5, metalness: 0.08 });
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(outerR - 0.007, outerR - 0.007, 0.005, 40), grilleMat);
      grille.position.y = -0.025;
      group.add(grille);

      // Perforation rings — larger spacing for subwoofer
      const holeMat = new THREE.MeshStandardMaterial({ color: "#c8c8c8", roughness: 0.8 });
      [0.03, 0.055, 0.080, 0.105, 0.130].forEach(r => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.004, 6, 32), holeMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.027;
        group.add(ring);
      });

      // ── 8" woofer cone (large, deep cone)
      const wooferMat = new THREE.MeshStandardMaterial({ color: "#7a7a7a", roughness: 0.65, metalness: 0.05 });
      const woofer = new THREE.Mesh(new THREE.ConeGeometry(drvR, 0.038, 36), wooferMat);
      woofer.rotation.x = Math.PI;
      woofer.position.y = -0.045;
      group.add(woofer);

      // Dust cap (center dome)
      const dustMat = new THREE.MeshStandardMaterial({ color: "#666666", roughness: 0.5 });
      const dustCap = new THREE.Mesh(new THREE.SphereGeometry(0.022, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), dustMat);
      dustCap.rotation.x = Math.PI;
      dustCap.position.y = -0.055;
      group.add(dustCap);

      // Surround ring (foam/rubber)
      const surrMat = new THREE.MeshStandardMaterial({ color: "#444444", roughness: 0.9, metalness: 0.0 });
      const surround = new THREE.Mesh(new THREE.TorusGeometry(drvR + 0.004, 0.010, 10, 36), surrMat);
      surround.rotation.x = Math.PI / 2;
      surround.position.y = -0.030;
      group.add(surround);

      // ── Low-pass filter module (on back of enclosure)
      const filterMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.7 });
      const filterBox = new THREE.Mesh(new THREE.BoxGeometry(0.060, 0.030, 0.050), filterMat);
      filterBox.position.set(0, backH * 0.6, -outerR * 0.7);
      group.add(filterBox);

      // LP filter switch
      const swMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 0.5, roughness: 0 });
      const lpSwitch = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.008, 0.025), swMat);
      lpSwitch.position.set(0, backH * 0.6 + 0.018, -outerR * 0.7);
      group.add(lpSwitch);

      // ── Terminal block (large, 4-pole)
      const termMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.7 });
      const term = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.022, 0.030), termMat);
      term.position.set(0, backH * 0.85, outerR * 0.55);
      group.add(term);

      // ── V-rail clips (3 around rim, heavy duty)
      const clipMat = new THREE.MeshStandardMaterial({ color: "#b8b8b8", roughness: 0.3, metalness: 0.8 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.014, 0.010), clipMat);
        clip.position.set(Math.cos(a) * (outerR + 0.016), 0, Math.sin(a) * (outerR + 0.016));
        clip.rotation.y = a;
        group.add(clip);
      }
    }

    // ─────────────────────────────────────────────
    // EXTRON SF 26CT — 6.5" 2-way ceiling speaker, STANDARD 8" back can
    // Identical look to SF 26CT LP but TALLER back can (203mm)
    // UL 2043 composite can, same bezel/grille/driver layout
    // ─────────────────────────────────────────────
    else if (pid === "extron-sf26ct") {
      const outerR = 0.135;
      const backH = 0.203;  // 8" standard back can
      const drvR = 0.0825;

      const canMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.7, metalness: 0.1 });
      const backCan = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.94, outerR * 0.92, backH, 40), canMat);
      backCan.position.y = backH / 2;
      backCan.castShadow = true;
      group.add(backCan);

      // Can taper at bottom (composite molded shape)
      const canBottom = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.72, outerR * 0.92, 0.040, 40), canMat);
      canBottom.position.y = backH + 0.020;
      group.add(canBottom);

      // Top cap
      const capMat = new THREE.MeshStandardMaterial({ color: "#222222", roughness: 0.6 });
      const topCap = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.72, outerR * 0.72, 0.012, 40), capMat);
      topCap.position.y = backH + 0.040 + 0.006;
      group.add(topCap);

      // Baffle
      const baffleMat = new THREE.MeshStandardMaterial({ color: "#f0f0f0", roughness: 0.45, metalness: 0.05 });
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(outerR, outerR, 0.018, 40), baffleMat);
      baffle.position.y = -0.009;
      group.add(baffle);

      // Bezel
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(outerR - 0.004, 0.005, 10, 40),
        new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.3, metalness: 0.15 }));
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = -0.001;
      group.add(bezel);

      // Grille
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(outerR - 0.006, outerR - 0.006, 0.004, 40),
        new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.5, metalness: 0.12 }));
      grille.position.y = -0.020;
      group.add(grille);

      const holeMat = new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.8 });
      [0.02, 0.04, 0.06, 0.08, 0.10].forEach(r => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.003, 6, 32), holeMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.022;
        group.add(ring);
      });

      // Woofer
      const woofer = new THREE.Mesh(new THREE.ConeGeometry(drvR, 0.025, 32),
        new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.6, metalness: 0.1 }));
      woofer.rotation.x = Math.PI;
      woofer.position.y = -0.036;
      group.add(woofer);

      const surround = new THREE.Mesh(new THREE.TorusGeometry(drvR + 0.003, 0.008, 8, 32),
        new THREE.MeshStandardMaterial({ color: "#555555", roughness: 0.8 }));
      surround.rotation.x = Math.PI / 2;
      surround.position.y = -0.026;
      group.add(surround);

      // Tweeter bridge + dome
      const bridgeMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.3, metalness: 0.3 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.004, drvR * 0.7), bridgeMat);
        arm.position.set(Math.cos(a) * drvR * 0.35, -0.032, Math.sin(a) * drvR * 0.35);
        arm.rotation.y = a;
        group.add(arm);
      }
      const dome = new THREE.Mesh(new THREE.SphereGeometry(0.010, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: "#b0b0c8", roughness: 0.1, metalness: 0.6 }));
      dome.rotation.x = Math.PI;
      dome.position.y = -0.033;
      group.add(dome);

      // Rotary switch (larger back can → switch on side)
      const sw = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.010, 12),
        new THREE.MeshStandardMaterial({ color: "#444444", roughness: 0.6 }));
      sw.position.set(outerR * 0.55, -0.010, 0);
      group.add(sw);

      // Terminal block
      const term = new THREE.Mesh(new THREE.BoxGeometry(0.040, 0.018, 0.025),
        new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.7 }));
      term.position.set(0, backH * 0.3, outerR * 0.7);
      group.add(term);

      // V-rail clips
      const clipMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.010, 0.008), clipMat);
        clip.position.set(Math.cos(a) * (outerR + 0.012), 0, Math.sin(a) * (outerR + 0.012));
        clip.rotation.y = a;
        group.add(clip);
      }
    }

    // ─────────────────────────────────────────────
    // EXTRON SF 26X — 6.5" 2-way OPEN BACK ceiling speaker
    // Slimmer profile (no enclosure), coaxial driver design,
    // pivoting tweeter, thin-edge bezel, magnetic grille
    // Open back = no cylinder body above baffle
    // ─────────────────────────────────────────────
    else if (pid === "extron-sf26x") {
      const outerR = 0.096;  // outer radius ≈ 193mm cutout / 2
      const drvR = 0.0825; // 6.5" woofer

      // ── Open-back baffle only (no back can body)
      const baffleMat = new THREE.MeshStandardMaterial({ color: "#f2f2f2", roughness: 0.45, metalness: 0.05 });
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(outerR, outerR, 0.022, 40), baffleMat);
      baffle.position.y = -0.011;
      group.add(baffle);

      // Shallow housing ring (just enough for wiring)
      const housingMat = new THREE.MeshStandardMaterial({ color: "#e0e0e0", roughness: 0.5, metalness: 0.05 });
      const housing = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.88, outerR * 0.88, 0.060, 36), housingMat);
      housing.position.y = 0.030;
      group.add(housing);

      // ── Thin-edge bezel
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(outerR - 0.003, 0.005, 10, 40),
        new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.3, metalness: 0.15 }));
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = 0;
      group.add(bezel);

      // ── Magnetic grille
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(outerR - 0.005, outerR - 0.005, 0.004, 40),
        new THREE.MeshStandardMaterial({ color: "#f6f6f6", roughness: 0.5, metalness: 0.1 }));
      grille.position.y = -0.022;
      group.add(grille);

      [0.02, 0.04, 0.06, 0.075].forEach(r => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.003, 6, 32),
          new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.8 }));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.024;
        group.add(ring);
      });

      // ── Coaxial woofer
      const woofer = new THREE.Mesh(new THREE.ConeGeometry(drvR, 0.022, 32),
        new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.65, metalness: 0.05 }));
      woofer.rotation.x = Math.PI;
      woofer.position.y = -0.032;
      group.add(woofer);

      const surround = new THREE.Mesh(new THREE.TorusGeometry(drvR + 0.003, 0.007, 8, 32),
        new THREE.MeshStandardMaterial({ color: "#555555", roughness: 0.8 }));
      surround.rotation.x = Math.PI / 2;
      surround.position.y = -0.023;
      group.add(surround);

      // ── Pivoting dome tweeter (coaxial center, can tilt)
      // Mount arm (short pivot bracket)
      const pivotMat = new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.3, metalness: 0.5 });
      const pivotArm = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.018, 0.005), pivotMat);
      pivotArm.position.y = -0.028;
      group.add(pivotArm);

      // Tweeter dome (slightly off-center to show pivot)
      const twtDome = new THREE.Mesh(new THREE.SphereGeometry(0.009, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: "#a8a8c0", roughness: 0.1, metalness: 0.7 }));
      twtDome.rotation.x = Math.PI;
      twtDome.position.y = -0.030;
      group.add(twtDome);

      // ── 4-pole terminal on back of baffle
      const term = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.016, 0.022),
        new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.7 }));
      term.position.set(0, 0.050, outerR * 0.60);
      group.add(term);

      // ── Mounting clips (spring-loaded, 3 around)
      const clipMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.008, 0.008), clipMat);
        clip.position.set(Math.cos(a) * (outerR + 0.010), 0, Math.sin(a) * (outerR + 0.010));
        clip.rotation.y = a;
        group.add(clip);
      }
    }

    // ─────────────────────────────────────────────
    // EXTRON SF 3C LP — 3" full-range 8 ohm, LOW PROFILE (4" back can)
    // Small round speaker, compact back can (102mm),
    // 3" paper cone, no tweeter, wider 165° dispersion
    // ─────────────────────────────────────────────
    else if (pid === "extron-sf3clp") {
      const outerR = 0.113;  // 8.9" outer = 226mm/2
      const backH = 0.102;  // 4" back can
      const drvR = 0.038;  // 3" driver = 76mm/2

      // Back can
      const canMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.7, metalness: 0.1 });
      const backCan = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.90, outerR * 0.88, backH, 36), canMat);
      backCan.position.y = backH / 2;
      backCan.castShadow = true;
      group.add(backCan);

      // Baffle
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(outerR, outerR, 0.015, 36),
        new THREE.MeshStandardMaterial({ color: "#f0f0f0", roughness: 0.45, metalness: 0.05 }));
      baffle.position.y = -0.0075;
      group.add(baffle);

      // Bezel
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(outerR - 0.004, 0.005, 10, 36),
        new THREE.MeshStandardMaterial({ color: "#e6e6e6", roughness: 0.3, metalness: 0.15 }));
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = 0;
      group.add(bezel);

      // Grille (small, thin)
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(outerR - 0.005, outerR - 0.005, 0.004, 36),
        new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.5, metalness: 0.1 }));
      grille.position.y = -0.018;
      group.add(grille);

      // Perforations (small speaker, fewer rings)
      [0.018, 0.038, 0.060, 0.080].forEach(r => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.003, 6, 28),
          new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.8 }));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.020;
        group.add(ring);
      });

      // 3" paper cone (small, shallow)
      const woofer = new THREE.Mesh(new THREE.ConeGeometry(drvR, 0.016, 24),
        new THREE.MeshStandardMaterial({ color: "#a0916e", roughness: 0.7, metalness: 0.0 })); // paper = light brown
      woofer.rotation.x = Math.PI;
      woofer.position.y = -0.028;
      group.add(woofer);

      // Dust cap
      const dustCap = new THREE.Mesh(new THREE.SphereGeometry(0.010, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: "#888878", roughness: 0.6 }));
      dustCap.rotation.x = Math.PI;
      dustCap.position.y = -0.030;
      group.add(dustCap);

      // Surround
      const surround = new THREE.Mesh(new THREE.TorusGeometry(drvR + 0.004, 0.006, 8, 28),
        new THREE.MeshStandardMaterial({ color: "#444444", roughness: 0.85 }));
      surround.rotation.x = Math.PI / 2;
      surround.position.y = -0.020;
      group.add(surround);

      // Terminal (2-pole, small)
      const term = new THREE.Mesh(new THREE.BoxGeometry(0.030, 0.014, 0.020),
        new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.7 }));
      term.position.set(0, backH * 0.4, outerR * 0.65);
      group.add(term);

      // V-rail clips
      const clipMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.009, 0.007), clipMat);
        clip.position.set(Math.cos(a) * (outerR + 0.010), 0, Math.sin(a) * (outerR + 0.010));
        clip.rotation.y = a;
        group.add(clip);
      }
    }

    // ─────────────────────────────────────────────
    // EXTRON SF 3CT LP — 3" full-range + 70/100V transformer, LOW PROFILE
    // Identical to SF 3C LP but with rotary tap switch (transformer version)
    // Visual difference: tap switch knob visible on front/side
    // ─────────────────────────────────────────────
    else if (pid === "extron-sf3ctlp") {
      const outerR = 0.113;
      const backH = 0.102;
      const drvR = 0.038;

      // ── Same body as SF 3C LP ──
      const canMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.7, metalness: 0.1 });
      const backCan = new THREE.Mesh(new THREE.CylinderGeometry(outerR * 0.90, outerR * 0.88, backH, 36), canMat);
      backCan.position.y = backH / 2;
      backCan.castShadow = true;
      group.add(backCan);

      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(outerR, outerR, 0.015, 36),
        new THREE.MeshStandardMaterial({ color: "#f0f0f0", roughness: 0.45, metalness: 0.05 }));
      baffle.position.y = -0.0075;
      group.add(baffle);

      const bezel = new THREE.Mesh(new THREE.TorusGeometry(outerR - 0.004, 0.005, 10, 36),
        new THREE.MeshStandardMaterial({ color: "#e6e6e6", roughness: 0.3, metalness: 0.15 }));
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = 0;
      group.add(bezel);

      const grille = new THREE.Mesh(new THREE.CylinderGeometry(outerR - 0.005, outerR - 0.005, 0.004, 36),
        new THREE.MeshStandardMaterial({ color: "#f5f5f5", roughness: 0.5, metalness: 0.1 }));
      grille.position.y = -0.018;
      group.add(grille);

      [0.018, 0.038, 0.060, 0.080].forEach(r => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.003, 6, 28),
          new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.8 }));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -0.020;
        group.add(ring);
      });

      const woofer = new THREE.Mesh(new THREE.ConeGeometry(drvR, 0.016, 24),
        new THREE.MeshStandardMaterial({ color: "#a0916e", roughness: 0.7, metalness: 0.0 }));
      woofer.rotation.x = Math.PI;
      woofer.position.y = -0.028;
      group.add(woofer);

      const dustCap = new THREE.Mesh(new THREE.SphereGeometry(0.010, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: "#888878", roughness: 0.6 }));
      dustCap.rotation.x = Math.PI;
      dustCap.position.y = -0.030;
      group.add(dustCap);

      const surround = new THREE.Mesh(new THREE.TorusGeometry(drvR + 0.004, 0.006, 8, 28),
        new THREE.MeshStandardMaterial({ color: "#444444", roughness: 0.85 }));
      surround.rotation.x = Math.PI / 2;
      surround.position.y = -0.020;
      group.add(surround);

      // ── KEY VISUAL DIFFERENCE: 6-position rotary tap switch (behind grille)
      // Switch body
      const switchMat = new THREE.MeshStandardMaterial({ color: "#333333", roughness: 0.6 });
      const switchBody = new THREE.Mesh(new THREE.CylinderGeometry(0.010, 0.010, 0.008, 12), switchMat);
      switchBody.position.set(outerR * 0.48, -0.013, 0);
      group.add(switchBody);

      // Switch indicator line
      const indicMat = new THREE.MeshStandardMaterial({ color: "#f0f0f0", roughness: 0.3 });
      const indicator = new THREE.Mesh(new THREE.BoxGeometry(0.002, 0.005, 0.009), indicMat);
      indicator.position.set(outerR * 0.48, -0.008, 0.004);
      group.add(indicator);

      // Tap labels (small dots around switch)
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const dot = new THREE.Mesh(new THREE.CircleGeometry(0.002, 6),
          new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5 }));
        dot.position.set(outerR * 0.48 + Math.cos(a) * 0.014, -0.013, Math.sin(a) * 0.014);
        dot.rotation.x = Math.PI / 2;
        group.add(dot);
      }

      // Terminal (with transformer: 7-pole)
      const term = new THREE.Mesh(new THREE.BoxGeometry(0.038, 0.014, 0.022),
        new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.7 }));
      term.position.set(0, backH * 0.4, outerR * 0.65);
      group.add(term);

      const clipMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.3, metalness: 0.7 });
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.009, 0.007), clipMat);
        clip.position.set(Math.cos(a) * (outerR + 0.010), 0, Math.sin(a) * (outerR + 0.010));
        clip.rotation.y = a;
        group.add(clip);
      }
    }

    // ─────────────────────────────────────────────
    // CISCO TABLE MICROPHONE PRO
    // Compact round puck, 9.8cm diameter × 2.9cm tall
    // Perforated steel top, capacitive touch mute button,
    // tri-color LED ring, Cable outlet at base,
    // Available in First Light (white/silver) or Carbon Black
    // ─────────────────────────────────────────────
    else if (pid === "cisco-table-mic-pro") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.25, metalness: 0.75 });
      const topMat = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.2, metalness: 0.8 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.15, metalness: 0.9 });
      const ledActiveMat = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 2.0, roughness: 0 });
      const ledMuteMat = new THREE.MeshStandardMaterial({ color: "#ef4444", emissive: "#ef4444", emissiveIntensity: 0.5, roughness: 0 });
      const buttonMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.3, metalness: 0.6 });

      const R = 0.049; // radius 9.8cm/2
      const H = 0.029; // height 2.9cm

      // ── Main body cylinder
      const body = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 1.02, H, 48), bodyMat);
      body.castShadow = true;
      group.add(body);

      // ── Angled rim bevel at bottom
      const bevel = new THREE.Mesh(new THREE.CylinderGeometry(R * 1.02, R * 0.97, H * 0.12, 48), rimMat);
      bevel.position.y = -H / 2 + H * 0.06;
      group.add(bevel);

      // ── Perforated top face (stamped steel grille)
      const topFace = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.97, R * 0.97, 0.003, 48), topMat);
      topFace.position.y = H / 2 - 0.001;
      group.add(topFace);

      // Perforation hole grid (concentric circles pattern)
      const holeMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 1.0 });
      [0.010, 0.018, 0.026, 0.033, 0.040].forEach(r => {
        const count = Math.max(6, Math.round(2 * Math.PI * r / 0.007));
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.0018, 0.0018, 0.004, 6), holeMat);
          dot.position.set(Math.cos(angle) * r, H / 2, Math.sin(angle) * r);
          group.add(dot);
        }
      });

      // ── Capacitive mute touch button (concave circle, center-top)
      const btnOuter = new THREE.Mesh(new THREE.CylinderGeometry(0.010, 0.010, 0.002, 28), buttonMat);
      btnOuter.position.y = H / 2 + 0.001;
      group.add(btnOuter);

      const btnInner = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.002, 24),
        new THREE.MeshStandardMaterial({ color: "#080808", roughness: 0.15, metalness: 0.9 }));
      btnInner.position.y = H / 2 + 0.002;
      group.add(btnInner);

      // ── Tri-color LED ring (around button) — green = active
      const ledRing = new THREE.Mesh(new THREE.TorusGeometry(0.013, 0.002, 6, 32), ledActiveMat);
      ledRing.rotation.x = Math.PI / 2;
      ledRing.position.y = H / 2 + 0.002;
      group.add(ledRing);

      // ── Outer status LED strip (ring around perimeter, bottom half)
      // Shows mute status visible from across the table
      const statusRing = new THREE.Mesh(new THREE.TorusGeometry(R * 0.88, 0.003, 6, 48), ledMuteMat);
      statusRing.rotation.x = Math.PI / 2;
      statusRing.position.y = -H / 2 + H * 0.18;
      group.add(statusRing);

      // ── Ethernet port (RJ-45) at base
      const portMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.9 });
      const ethPort = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.007, 0.006), portMat);
      ethPort.position.set(0, -H / 2 + 0.005, R - 0.003);
      group.add(ethPort);

      // ── Magnetic table adapter plate (thin disc, sits under unit)
      const plateMat = new THREE.MeshStandardMaterial({ color: "#141414", roughness: 0.3, metalness: 0.85 });
      const plate = new THREE.Mesh(new THREE.CylinderGeometry(R * 1.04, R * 1.04, 0.003, 48), plateMat);
      plate.position.y = -H / 2 - 0.0015;
      group.add(plate);

      // ── Subtle Cisco/Webex branding emboss (laser engraved on top)
      const brandMat = new THREE.MeshStandardMaterial({ color: "#303030", roughness: 0.6, metalness: 0.5 });
      const brand = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.001, 0.006), brandMat);
      brand.position.set(0, H / 2 + 0.002, R * 0.55);
      group.add(brand);
    }

    // ─────────────────────────────────────────────
    // CISCO TABLE MICROPHONE (standard/analog)
    // 102mm round disc, very flat profile (25mm),
    // White perforated grille, analog connector,
    // Mute button on top, simple LED indicator
    // ─────────────────────────────────────────────
    else if (pid === "cisco-table-mic") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#f2f2f2", roughness: 0.35, metalness: 0.15 });
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.4, metalness: 0.1 });
      const holeMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.8 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 1.5, roughness: 0 });
      const btnMat = new THREE.MeshStandardMaterial({ color: "#d8d8d8", roughness: 0.3, metalness: 0.2 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#e0e0e0", roughness: 0.2, metalness: 0.3 });

      const R = 0.051; // 102mm radius
      const H = 0.025; // 25mm height

      // ── Main body
      const body = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 1.01, H, 48), bodyMat);
      body.castShadow = true;
      group.add(body);

      // ── Chrome/silver rim ring at base
      const rim = new THREE.Mesh(new THREE.TorusGeometry(R * 0.995, 0.003, 8, 48), rimMat);
      rim.rotation.x = Math.PI / 2;
      rim.position.y = -H / 2 + 0.003;
      group.add(rim);

      // ── Perforated top grille
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.96, R * 0.96, 0.003, 48), grilleMat);
      grille.position.y = H / 2;
      group.add(grille);

      // Concentric hole rings (omnidirectional mic design)
      [0.012, 0.022, 0.032, 0.042].forEach(r => {
        const count = Math.max(8, Math.round(2 * Math.PI * r / 0.008));
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.002, 0.002, 0.004, 6), holeMat);
          dot.position.set(Math.cos(angle) * r, H / 2, Math.sin(angle) * r);
          group.add(dot);
        }
      });

      // ── Mute button (raised, slightly off-center)
      const muteBtn = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, 0.004, 20), btnMat);
      muteBtn.position.set(R * 0.5, H / 2 + 0.002, 0);
      group.add(muteBtn);

      // ── LED indicator (small dot next to button)
      const led = new THREE.Mesh(new THREE.CylinderGeometry(0.003, 0.003, 0.003, 10), ledMat);
      led.position.set(R * 0.5, H / 2 + 0.003, 0.012);
      group.add(led);

      // ── Analog cable outlet (4-pin mini jack port at rear base)
      const portMat = new THREE.MeshStandardMaterial({ color: "#c0c0c0", roughness: 0.4, metalness: 0.6 });
      const jackPort = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, 0.008, 12), portMat);
      jackPort.rotation.x = Math.PI / 2;
      jackPort.position.set(0, -H / 2 + 0.006, R - 0.004);
      group.add(jackPort);

      // ── Magnetic table plate
      const plateMat = new THREE.MeshStandardMaterial({ color: "#e5e5e5", roughness: 0.3, metalness: 0.2 });
      const plate = new THREE.Mesh(new THREE.CylinderGeometry(R * 1.03, R * 1.03, 0.003, 48), plateMat);
      plate.position.y = -H / 2 - 0.0015;
      group.add(plate);

      // ── Cisco logo indentation (top, subtle emboss)
      const brandMat = new THREE.MeshStandardMaterial({ color: "#d8d8d8", roughness: 0.5 });
      const brand = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.001, 0.005), brandMat);
      brand.position.set(-R * 0.45, H / 2 + 0.002, 0);
      group.add(brand);
    }

    // ─────────────────────────────────────────────
    // CISCO CEILING MICROPHONE PRO
    // 220mm diameter circular disc, 44mm total height
    // 64-element mic array, 8 AI adaptive beams,
    // Aluminum grille flush with ceiling,
    // Rear panel with interfaces & cable management
    // Available in Arctic White / Carbon Black
    // Mounting: bracket, drop ceiling grid, or wire hanging
    // ─────────────────────────────────────────────
    else if (pid === "cisco-ceiling-mic-pro") {
      const R = 0.110; // 220mm/2
      const H = 0.044; // 44mm total height

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#f4f4f4", roughness: 0.3, metalness: 0.15 });
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#efefef", roughness: 0.35, metalness: 0.2 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#e0e0e0", roughness: 0.2, metalness: 0.4 });
      const holeMat = new THREE.MeshStandardMaterial({ color: "#d0d0d0", roughness: 0.8 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 1.8, roughness: 0 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.5, metalness: 0.6 });
      const mountMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.3, metalness: 0.6 });

      // ── Main circular enclosure body
      const body = new THREE.Mesh(new THREE.CylinderGeometry(R, R, H * 0.65, 52), bodyMat);
      body.castShadow = true;
      group.add(body);

      // Tapered bottom lip (for flush ceiling look)
      const taper = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 0.94, H * 0.2, 52), bodyMat);
      taper.position.y = H * 0.425;
      group.add(taper);

      // ── Aluminum perforated grille (bottom face — faces room)
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.97, R * 0.97, 0.006, 52), grilleMat);
      grille.position.y = -H * 0.325 + 0.003;
      group.add(grille);

      // ── Outer slim bezel ring
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(R - 0.004, 0.006, 10, 52), rimMat);
      bezel.rotation.x = Math.PI / 2;
      bezel.position.y = -H * 0.325 + 0.003;
      group.add(bezel);

      // ── Concentric perforation rings (simulating grille pattern)
      [0.018, 0.032, 0.048, 0.062, 0.076, 0.090].forEach(r => {
        const count = Math.max(10, Math.round(2 * Math.PI * r / 0.008));
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.002, 0.002, 0.007, 6), holeMat);
          dot.position.set(Math.cos(angle) * r, -H * 0.325, Math.sin(angle) * r);
          group.add(dot);
        }
      });

      // ── 8 AI beam indicator LEDs (green ring in grille, indicates beam directions)
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const ledDot = new THREE.Mesh(new THREE.CylinderGeometry(0.003, 0.003, 0.005, 8), ledMat);
        ledDot.position.set(Math.cos(angle) * R * 0.72, -H * 0.32, Math.sin(angle) * R * 0.72);
        group.add(ledDot);
      }

      // ── Status LED ring (thin ring near outer edge of grille)
      const statusRing = new THREE.Mesh(new THREE.TorusGeometry(R * 0.82, 0.004, 8, 52), ledMat);
      statusRing.rotation.x = Math.PI / 2;
      statusRing.position.y = -H * 0.32;
      group.add(statusRing);

      // ── Rear panel — interfaces (facing ceiling/up)
      // Ethernet PoE port (RJ-45)
      const ethPort = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.006, 0.010), portMat);
      ethPort.position.set(-R * 0.55, H * 0.30, 0);
      group.add(ethPort);

      // Reset pinhole
      const pin = new THREE.Mesh(new THREE.CylinderGeometry(0.002, 0.002, 0.005, 8), portMat);
      pin.position.set(-R * 0.38, H * 0.32, 0.005);
      group.add(pin);

      // USB-C service port
      const usbPort = new THREE.Mesh(new THREE.BoxGeometry(0.010, 0.004, 0.008), portMat);
      usbPort.position.set(-R * 0.55, H * 0.30, 0.020);
      group.add(usbPort);

      // Cable management cutout guides
      const cmMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.5 });
      [-0.025, 0, 0.025].forEach(cx => {
        const cm = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.008, 0.012), cmMat);
        cm.position.set(R * 0.45, H * 0.28, cx);
        group.add(cm);
      });

      // ── Ceiling mount bracket slots (visible as raised clips around rim)
      // 3 spring-loaded mounting clips
      for (let i = 0; i < 3; i++) {
        const angle = (i / 3) * Math.PI * 2;
        const clip = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.014, 0.010), mountMat);
        clip.position.set(Math.cos(angle) * (R + 0.014), 0, Math.sin(angle) * (R + 0.014));
        clip.rotation.y = angle;
        group.add(clip);

        // Clip screw
        const screwMat = new THREE.MeshStandardMaterial({ color: "#aaaaaa", roughness: 0.3, metalness: 0.7 });
        const screw = new THREE.Mesh(new THREE.CylinderGeometry(0.003, 0.003, 0.008, 8), screwMat);
        screw.rotation.x = Math.PI / 2;
        screw.position.set(Math.cos(angle) * (R + 0.014), 0.006, Math.sin(angle) * (R + 0.014));
        group.add(screw);
      }

      // ── Wire hanging mount point (center top)
      const wireMat = new THREE.MeshStandardMaterial({ color: "#b0b0b0", roughness: 0.3, metalness: 0.7 });
      const wireAnchor = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.010, 0.014, 12), wireMat);
      wireAnchor.position.y = H * 0.32 + 0.007;
      group.add(wireAnchor);

      // ── Cisco Webex logo (laser etched, bottom face)
      const logoMat = new THREE.MeshStandardMaterial({ color: "#d8d8d8", roughness: 0.5 });
      const logo = new THREE.Mesh(new THREE.BoxGeometry(0.030, 0.001, 0.008), logoMat);
      logo.position.set(0, -H * 0.325 + 0.004, R * 0.50);
      group.add(logo);

      // ── Circular design aesthetic: thin structural rings on body
      [0.2, 0.5, 0.8].forEach(t => {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(R * 0.90, 0.002, 6, 52),
          new THREE.MeshStandardMaterial({ color: "#e8e8e8", roughness: 0.4, metalness: 0.2 }));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = -H * 0.325 + t * H;
        group.add(ring);
      });
    }

    // ─────────────────────────────────────────────
    // CISCO DESK CAMERA 1080p
    // Ultra-slim horizontal bar webcam, 90.64×19.5×41mm
    // Carbon Black matte finish, sliding privacy shutter,
    // single large front-center lens with LED ring,
    // monitor clip at bottom, USB-C cable exit rear
    // ─────────────────────────────────────────────
    else if (pid === "cisco-desk-cam-1080") {
      const bW = 0.0906, bH = 0.0195, bD = 0.041;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.22, metalness: 0.70 });
      const frontMat = new THREE.MeshStandardMaterial({ color: "#141414", roughness: 0.18, metalness: 0.75 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#080818", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#020210", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#252525", roughness: 0.12, metalness: 0.95 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2.0, roughness: 0 });
      const shutterMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.3, metalness: 0.6 });
      const clipMat = new THREE.MeshStandardMaterial({ color: "#222222", roughness: 0.25, metalness: 0.80 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.9 });

      // ── Main horizontal bar body
      const body = new THREE.Mesh(new THREE.BoxGeometry(bW, bH, bD), bodyMat);
      body.castShadow = true;
      group.add(body);

      // Rounded end caps (left & right)
      [-bW / 2, bW / 2].forEach(ex => {
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(bH / 2, bH / 2, bD, 20), bodyMat);
        cap.rotation.x = Math.PI / 2;
        cap.position.set(ex, 0, 0);
        group.add(cap);
      });

      // ── Front face plate (slightly recessed, darker)
      const facePlate = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.96, bH * 0.82, 0.003), frontMat);
      facePlate.position.set(0, 0, bD / 2 - 0.001);
      group.add(facePlate);

      // ── Center lens assembly (single large lens, centered)
      const barrelOuter = new THREE.Mesh(new THREE.CylinderGeometry(0.0075, 0.0078, 0.012, 24), lensRingMat);
      barrelOuter.rotation.x = Math.PI / 2;
      barrelOuter.position.set(0, 0, bD / 2 + 0.002);
      group.add(barrelOuter);

      const barrelInner = new THREE.Mesh(new THREE.CylinderGeometry(0.0058, 0.0062, 0.008, 20), bodyMat);
      barrelInner.rotation.x = Math.PI / 2;
      barrelInner.position.set(0, 0, bD / 2 + 0.007);
      group.add(barrelInner);

      const lensGlass = new THREE.Mesh(new THREE.CircleGeometry(0.0052, 24), glassMat);
      lensGlass.position.set(0, 0, bD / 2 + 0.012);
      group.add(lensGlass);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.0030, 20), lensMat);
      lensCore.position.set(0, 0, bD / 2 + 0.013);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.0052, 0.0010, 8, 24), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0, bD / 2 + 0.011);
      group.add(lensRim);

      // ── Blue status LED dot (right of lens)
      const led = new THREE.Mesh(new THREE.CircleGeometry(0.0015, 10), ledMat);
      led.position.set(0.020, 0, bD / 2 + 0.002);
      group.add(led);

      // ── Privacy shutter (horizontal slider, partially visible at top-center)
      const shutter = new THREE.Mesh(new THREE.BoxGeometry(0.020, bH * 0.35, 0.004), shutterMat);
      shutter.position.set(0.005, bH * 0.25, bD / 2 + 0.003);
      group.add(shutter);
      // Shutter grip knob
      const shutterGrip = new THREE.Mesh(new THREE.BoxGeometry(0.006, bH * 0.20, 0.006), shutterMat);
      shutterGrip.position.set(-0.008, bH * 0.25, bD / 2 + 0.006);
      group.add(shutterGrip);

      // ── Dual microphone pinhole array (flanking the lens)
      const micHoleMat = new THREE.MeshStandardMaterial({ color: "#080808", roughness: 1.0 });
      [-0.016, 0.016].forEach(mx => {
        for (let i = 0; i < 3; i++) {
          const hole = new THREE.Mesh(new THREE.CircleGeometry(0.0008, 6), micHoleMat);
          hole.position.set(mx + i * 0.003 - 0.003, -0.004, bD / 2 + 0.002);
          group.add(hole);
        }
      });

      // ── USB-C port (rear bottom)
      const usbPort = new THREE.Mesh(new THREE.BoxGeometry(0.007, 0.003, 0.004), portMat);
      usbPort.position.set(0, -bH / 2 + 0.004, -bD / 2 + 0.002);
      group.add(usbPort);

      // ── Monitor clip / stand (bottom of unit)
      // Main clip arm
      const clipArm = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.55, 0.006, 0.028), clipMat);
      clipArm.position.set(0, -bH / 2 - 0.003, bD * 0.1);
      group.add(clipArm);

      // Clip jaw (grips monitor bezel)
      const clipJaw = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.45, 0.005, 0.012), clipMat);
      clipJaw.position.set(0, -bH / 2 - 0.010, bD / 2 + 0.005);
      group.add(clipJaw);

      // Clip spring hinge
      const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, bW * 0.30, 12), clipMat);
      hinge.rotation.z = Math.PI / 2;
      hinge.position.set(0, -bH / 2 - 0.001, bD * 0.1 - 0.014);
      group.add(hinge);

      // ── Cisco/Webex wordmark (subtle laser etch, bottom-right of face)
      const brandMat = new THREE.MeshStandardMaterial({ color: "#282828", roughness: 0.65 });
      const brand = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.0006, 0.004), brandMat);
      brand.position.set(bW * 0.32, -bH * 0.22, bD / 2 + 0.002);
      group.add(brand);
    }


    // ─────────────────────────────────────────────
    // CISCO DESK CAMERA 4K
    // Identical form factor to 1080p but with 4K sensor
    // Slightly premium feel: subtle gold-tone lens ring,
    // "4K" text badge near right end
    // ─────────────────────────────────────────────
    else if (pid === "cisco-desk-cam-4k") {
      const bW = 0.0906, bH = 0.0195, bD = 0.041;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.20, metalness: 0.72 });
      const frontMat = new THREE.MeshStandardMaterial({ color: "#141414", roughness: 0.15, metalness: 0.78 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#050515", roughness: 0, metalness: 0.92, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#010108", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#1c6ea4", roughness: 0.08, metalness: 0.98 }); // subtle blue-chrome ring (4K differentiation)
      const ledMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 2.2, roughness: 0 });
      const shutterMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.3, metalness: 0.6 });
      const clipMat = new THREE.MeshStandardMaterial({ color: "#222222", roughness: 0.22, metalness: 0.82 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.9 });

      // Main body
      const body = new THREE.Mesh(new THREE.BoxGeometry(bW, bH, bD), bodyMat);
      body.castShadow = true;
      group.add(body);

      // Rounded end caps
      [-bW / 2, bW / 2].forEach(ex => {
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(bH / 2, bH / 2, bD, 20), bodyMat);
        cap.rotation.x = Math.PI / 2;
        cap.position.set(ex, 0, 0);
        group.add(cap);
      });

      const facePlate = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.96, bH * 0.82, 0.003), frontMat);
      facePlate.position.set(0, 0, bD / 2 - 0.001);
      group.add(facePlate);

      // Center lens — slightly larger than 1080p to show 4K premium
      const barrelOuter = new THREE.Mesh(new THREE.CylinderGeometry(0.0085, 0.0088, 0.014, 28), lensRingMat);
      barrelOuter.rotation.x = Math.PI / 2;
      barrelOuter.position.set(0, 0, bD / 2 + 0.002);
      group.add(barrelOuter);

      const barrelInner = new THREE.Mesh(new THREE.CylinderGeometry(0.0066, 0.0070, 0.009, 24), bodyMat);
      barrelInner.rotation.x = Math.PI / 2;
      barrelInner.position.set(0, 0, bD / 2 + 0.008);
      group.add(barrelInner);

      const lensGlass = new THREE.Mesh(new THREE.CircleGeometry(0.0060, 28), glassMat);
      lensGlass.position.set(0, 0, bD / 2 + 0.014);
      group.add(lensGlass);

      // Inner lens element (sapphire blue tint — 4K sensor)
      const lensInner = new THREE.Mesh(new THREE.CircleGeometry(0.0040, 24),
        new THREE.MeshStandardMaterial({ color: "#0a1860", roughness: 0, metalness: 0.9, transparent: true, opacity: 0.85 }));
      lensInner.position.set(0, 0, bD / 2 + 0.0145);
      group.add(lensInner);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(0.0022, 20), lensMat);
      lensCore.position.set(0, 0, bD / 2 + 0.015);
      group.add(lensCore);

      const lensRim = new THREE.Mesh(new THREE.TorusGeometry(0.0060, 0.0012, 8, 28), lensRingMat);
      lensRim.rotation.x = Math.PI / 2;
      lensRim.position.set(0, 0, bD / 2 + 0.013);
      group.add(lensRim);

      // Blue LED
      const led = new THREE.Mesh(new THREE.CircleGeometry(0.0015, 10), ledMat);
      led.position.set(0.022, 0, bD / 2 + 0.002);
      group.add(led);

      // Privacy shutter
      const shutter = new THREE.Mesh(new THREE.BoxGeometry(0.022, bH * 0.35, 0.004), shutterMat);
      shutter.position.set(0.005, bH * 0.25, bD / 2 + 0.003);
      group.add(shutter);
      const shutterGrip = new THREE.Mesh(new THREE.BoxGeometry(0.006, bH * 0.20, 0.006), shutterMat);
      shutterGrip.position.set(-0.009, bH * 0.25, bD / 2 + 0.006);
      group.add(shutterGrip);

      // Dual high-sensitivity microphone holes
      const micHoleMat = new THREE.MeshStandardMaterial({ color: "#080808", roughness: 1.0 });
      [-0.018, 0.018].forEach(mx => {
        for (let i = 0; i < 4; i++) {
          const hole = new THREE.Mesh(new THREE.CircleGeometry(0.0008, 6), micHoleMat);
          hole.position.set(mx + i * 0.003 - 0.0045, -0.004, bD / 2 + 0.002);
          group.add(hole);
        }
      });

      // USB-C 3.1 port (rear bottom, slightly wider than 1080p version)
      const usbPort = new THREE.Mesh(new THREE.BoxGeometry(0.009, 0.004, 0.005), portMat);
      usbPort.position.set(0, -bH / 2 + 0.004, -bD / 2 + 0.002);
      group.add(usbPort);

      // Monitor clip
      const clipArm = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.55, 0.006, 0.028), clipMat);
      clipArm.position.set(0, -bH / 2 - 0.003, bD * 0.1);
      group.add(clipArm);
      const clipJaw = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.45, 0.005, 0.012), clipMat);
      clipJaw.position.set(0, -bH / 2 - 0.010, bD / 2 + 0.005);
      group.add(clipJaw);
      const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, bW * 0.30, 12), clipMat);
      hinge.rotation.z = Math.PI / 2;
      hinge.position.set(0, -bH / 2 - 0.001, bD * 0.1 - 0.014);
      group.add(hinge);

      // "4K" badge emboss (right side of face)
      const badgeMat = new THREE.MeshStandardMaterial({ color: "#1c6ea4", roughness: 0.3, metalness: 0.9 });
      const badge = new THREE.Mesh(new THREE.BoxGeometry(0.010, 0.005, 0.0008), badgeMat);
      badge.position.set(bW * 0.35, bH * 0.15, bD / 2 + 0.002);
      group.add(badge);

      // Webex brand
      const brand = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.0006, 0.004),
        new THREE.MeshStandardMaterial({ color: "#282828", roughness: 0.65 }));
      brand.position.set(bW * 0.32, -bH * 0.22, bD / 2 + 0.002);
      group.add(brand);
    }


    // ─────────────────────────────────────────────
    // CISCO ROOM VISION PTZ
    // Premium AI tracking PTZ, 165×196×176mm (excl. mount)
    // Curved aluminum housing, modern minimal aesthetic
    // Dome-shaped head on U-bracket base, front LED ring,
    // Connector panel rear: Ethernet PoE+, HDMI, Kensington lock, reset
    // Available Carbon Black / Arctic White
    // ─────────────────────────────────────────────
    else if (pid === "cisco-room-vision-ptz") {
      const cW = 0.165, cH = 0.221, cD = 0.196;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#2e2e30", roughness: 0.40, metalness: 0.25 });
      const bodySoft = new THREE.MeshStandardMaterial({ color: "#363638", roughness: 0.45, metalness: 0.20 });
      const baseMat = new THREE.MeshStandardMaterial({ color: "#272729", roughness: 0.42, metalness: 0.28 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#161616", roughness: 0.35, metalness: 0.40 });
      const chromeMat = new THREE.MeshStandardMaterial({ color: "#c8c8c8", roughness: 0.15, metalness: 0.90 });
      // const glassMat = new THREE.MeshStandardMaterial({ color: "#0c2438", roughness: 0, metalness: 0.7, transparent: true, opacity: 0.92 });
      const lensCoreM = new THREE.MeshStandardMaterial({ color: "#040408", roughness: 0, metalness: 1 });
      const ledGreen = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 2.2, roughness: 0 });
      const logoMat = new THREE.MeshStandardMaterial({ color: "#9a9a9a", roughness: 0.4, metalness: 0.3 });

      // ── 1. CAMERA HEAD — kotak rounded ──
      const headW = cW * 0.95, headH = cH * 0.42, headD = cD * 0.80;
      const headY = cH * 0.22; // posisi head jelas di atas, terpisah dari base

      const headBody = new THREE.Mesh(new THREE.BoxGeometry(headW, headH, headD), bodyMat);
      headBody.castShadow = true;
      headBody.position.set(0, headY, 0);
      group.add(headBody);

      // ── 2. LENS pada sisi depan head ──
      const lensRing = new THREE.Mesh(new THREE.TorusGeometry(cW * 0.30, 0.006, 10, 36), chromeMat);
      lensRing.position.set(0, headY, headD / 2 + 0.001);
      group.add(lensRing);

      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.28, cW * 0.29, 0.030, 36), darkMat);
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(0, headY, headD / 2 + 0.016);
      group.add(barrel);

      // const glassOuter = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.22, 32), glassMat);
      // glassOuter.position.set(0, headY, headD / 2 + 0.032);
      // group.add(glassOuter);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.09, 22), lensCoreM);
      lensCore.position.set(0, headY, headD / 2 + 0.034);
      group.add(lensCore);

      const statusLed = new THREE.Mesh(new THREE.CircleGeometry(0.004, 10), ledGreen);
      statusLed.position.set(-cW * 0.20, headY + cW * 0.27, headD / 2 + 0.002);
      group.add(statusLed);

      // ── 3. YOKE — DUA BOX SEDERHANA yang jelas terlihat (kiri & kanan head) ──
      const yokeW = cW * 0.16;
      const yokeH = cH * 0.42; // tinggi cukup panjang agar terlihat sebagai "lengan"

      const yokeL = new THREE.Mesh(new THREE.BoxGeometry(yokeW, yokeH, cD * 0.50), bodySoft);
      yokeL.position.set(-headW / 2 - yokeW / 2 + 0.005, headY - headH * 0.30, 0);
      group.add(yokeL);

      const yokeR = new THREE.Mesh(new THREE.BoxGeometry(yokeW, yokeH, cD * 0.50), bodySoft);
      yokeR.position.set(headW / 2 + yokeW / 2 - 0.005, headY - headH * 0.30, 0);
      group.add(yokeR);

      // Bottom bar menghubungkan kedua yoke (membentuk huruf U yang jelas)
      const yokeBottomBar = new THREE.Mesh(new THREE.BoxGeometry(headW + yokeW * 1.5, yokeW, cD * 0.50), bodySoft);
      yokeBottomBar.position.set(0, headY - headH * 0.30 - yokeH / 2 + yokeW / 2, 0);
      group.add(yokeBottomBar);

      // ── 4. NECK — silinder pendek menghubungkan yoke bottom ke base ──
      const neckY = headY - headH * 0.30 - yokeH / 2 - 0.020;
      const neck = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.22, cW * 0.26, 0.040, 32), bodySoft);
      neck.position.set(0, neckY, 0);
      group.add(neck);

      // ── 5. BASE — silinder lebar dan jelas terlihat ──
      const baseR = cW * 0.62;
      const baseY = neckY - 0.020 - cH * 0.10;
      const base = new THREE.Mesh(new THREE.CylinderGeometry(baseR, baseR * 1.05, cH * 0.20, 48), baseMat);
      base.castShadow = true;
      base.position.set(0, baseY, 0);
      group.add(base);

      const baseBottom = new THREE.Mesh(new THREE.CylinderGeometry(baseR * 1.05, baseR * 1.08, 0.010, 48), darkMat);
      baseBottom.position.set(0, baseY - cH * 0.10 - 0.005, 0);
      group.add(baseBottom);

      // Logo Cisco di base
      const logoText = new THREE.Mesh(new THREE.BoxGeometry(0.050, 0.008, 0.002), logoMat);
      logoText.position.set(0, baseY - 0.02, baseR * 0.92);
      group.add(logoText);

      // ── 6. BACK PORTS ──
      const portMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.8 });
      [[-0.020, 0], [0.015, 0]].forEach(([px, py]) => {
        const port = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.010, 0.006), portMat);
        port.position.set(px, headY + py, -headD / 2 - 0.003);
        group.add(port);
      });
    }


    // ─────────────────────────────────────────────
    // CISCO QUAD CAMERA
    // Ultra-wide video bar 950×104×120mm
    // 4 lens array: 1x wide + 3x telephoto, fixed no-PTZ
    // Matte black aluminum/polymer, speaker grilles at ends,
    // HDMI + Ethernet ports at rear, wall mount integrated
    // ─────────────────────────────────────────────
    else if (pid === "cisco-quad-camera") {
      const bW = 0.950, bH = 0.104, bD = 0.119;

      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.20, metalness: 0.72 });
      const frontMat = new THREE.MeshStandardMaterial({ color: "#141414", roughness: 0.15, metalness: 0.80 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#050515", roughness: 0, metalness: 0.92, transparent: true, opacity: 0.92 });
      const lensMat = new THREE.MeshStandardMaterial({ color: "#010108", roughness: 0, metalness: 1 });
      const lensRingMat = new THREE.MeshStandardMaterial({ color: "#252525", roughness: 0.10, metalness: 0.96 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 1.8, roughness: 0 });
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.7, metalness: 0.3 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.9 });
      const mountMat = new THREE.MeshStandardMaterial({ color: "#1e1e1e", roughness: 0.28, metalness: 0.82 });

      // ── Main horizontal bar body
      const body = new THREE.Mesh(new THREE.BoxGeometry(bW, bH, bD), bodyMat);
      body.castShadow = true;
      group.add(body);

      // ── Beveled front face (slightly angled inward at top)
      const facePlate = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.90, bH * 0.72, 0.004), frontMat);
      facePlate.position.set(0, -bH * 0.05, bD / 2 - 0.001);
      group.add(facePlate);

      // ── TOP center: wide-angle main lens (83° FOV, slightly larger)
      const mainLensX = 0;
      const mainLensY = bH * 0.10;
      const mainLensZ = bD / 2 + 0.002;

      const mainBarrelOut = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.024, 0.025, 32), lensRingMat);
      mainBarrelOut.rotation.x = Math.PI / 2;
      mainBarrelOut.position.set(mainLensX, mainLensY, mainLensZ);
      group.add(mainBarrelOut);

      const mainBarrelIn = new THREE.Mesh(new THREE.CylinderGeometry(0.017, 0.020, 0.014, 28), bodyMat);
      mainBarrelIn.rotation.x = Math.PI / 2;
      mainBarrelIn.position.set(mainLensX, mainLensY, mainLensZ + 0.012);
      group.add(mainBarrelIn);

      const mainGlass = new THREE.Mesh(new THREE.CircleGeometry(0.015, 32), glassMat);
      mainGlass.position.set(mainLensX, mainLensY, mainLensZ + 0.020);
      group.add(mainGlass);

      const mainInner = new THREE.Mesh(new THREE.CircleGeometry(0.010, 28),
        new THREE.MeshStandardMaterial({ color: "#060820", roughness: 0, metalness: 0.90, transparent: true, opacity: 0.88 }));
      mainInner.position.set(mainLensX, mainLensY, mainLensZ + 0.0205);
      group.add(mainInner);

      const mainCore = new THREE.Mesh(new THREE.CircleGeometry(0.005, 24), lensMat);
      mainCore.position.set(mainLensX, mainLensY, mainLensZ + 0.021);
      group.add(mainCore);

      const mainRim = new THREE.Mesh(new THREE.TorusGeometry(0.015, 0.002, 8, 32), lensRingMat);
      mainRim.rotation.x = Math.PI / 2;
      mainRim.position.set(mainLensX, mainLensY, mainLensZ + 0.019);
      group.add(mainRim);

      // ── THREE telephoto lenses (50° FOV, smaller, evenly spaced in lower row)
      const tele_positions = [-bW * 0.28, 0, bW * 0.28];
      tele_positions.forEach(tx => {
        const ty = -bH * 0.15;
        const tz = bD / 2 + 0.002;

        const tBarrelOut = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.015, 0.018, 24), lensRingMat);
        tBarrelOut.rotation.x = Math.PI / 2;
        tBarrelOut.position.set(tx, ty, tz);
        group.add(tBarrelOut);

        const tBarrelIn = new THREE.Mesh(new THREE.CylinderGeometry(0.010, 0.012, 0.010, 20), bodyMat);
        tBarrelIn.rotation.x = Math.PI / 2;
        tBarrelIn.position.set(tx, ty, tz + 0.008);
        group.add(tBarrelIn);

        const tGlass = new THREE.Mesh(new THREE.CircleGeometry(0.009, 24), glassMat);
        tGlass.position.set(tx, ty, tz + 0.014);
        group.add(tGlass);

        const tInner = new THREE.Mesh(new THREE.CircleGeometry(0.006, 20),
          new THREE.MeshStandardMaterial({ color: "#041030", roughness: 0, metalness: 0.88, transparent: true, opacity: 0.85 }));
        tInner.position.set(tx, ty, tz + 0.0145);
        group.add(tInner);

        const tCore = new THREE.Mesh(new THREE.CircleGeometry(0.003, 16), lensMat);
        tCore.position.set(tx, ty, tz + 0.015);
        group.add(tCore);

        const tRim = new THREE.Mesh(new THREE.TorusGeometry(0.009, 0.0015, 6, 24), lensRingMat);
        tRim.rotation.x = Math.PI / 2;
        tRim.position.set(tx, ty, tz + 0.013);
        group.add(tRim);
      });

      // ── Speaker grille areas at both ends (left & right ~12cm)
      const grilleW = 0.080;
      [-bW / 2 + grilleW / 2 + 0.005, bW / 2 - grilleW / 2 - 0.005].forEach(gx => {
        const grilleBase = new THREE.Mesh(new THREE.BoxGeometry(grilleW, bH * 0.65, 0.004), grilleMat);
        grilleBase.position.set(gx, -bH * 0.05, bD / 2 + 0.002);
        group.add(grilleBase);
        // Horizontal grille slots
        for (let i = 0; i < 8; i++) {
          const slot = new THREE.Mesh(new THREE.BoxGeometry(grilleW * 0.85, 0.004, 0.006),
            new THREE.MeshStandardMaterial({ color: "#080808", roughness: 0.9 }));
          slot.position.set(gx, -bH * 0.25 + i * 0.010, bD / 2 + 0.004);
          group.add(slot);
        }
      });

      // ── LED status bar (thin strip above wide lens)
      const ledBar = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.20, 0.004, 0.004), ledMat);
      ledBar.position.set(0, bH * 0.36, bD / 2 + 0.003);
      group.add(ledBar);

      // ── Rear panel: HDMI (2x) + Ethernet + USB
      const rearY = 0;
      [-0.050, 0.010].forEach(hx => {
        const hdmi = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.008, 0.006), portMat);
        hdmi.position.set(hx, rearY, -bD / 2 - 0.003);
        group.add(hdmi);
      });
      const eth = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.012, 0.006), portMat);
      eth.position.set(0.060, rearY, -bD / 2 - 0.003);
      group.add(eth);
      const usb = new THREE.Mesh(new THREE.BoxGeometry(0.010, 0.007, 0.006), portMat);
      usb.position.set(0.095, rearY, -bD / 2 - 0.003);
      group.add(usb);

      // ── Wall mount integrated bracket (rear center)
      const wallMount = new THREE.Mesh(new THREE.BoxGeometry(bW * 0.55, 0.016, 0.040), mountMat);
      wallMount.position.set(0, -bH * 0.36, -bD / 2 - 0.020);
      group.add(wallMount);

      // ── Top logo strip (Cisco / Webex)
      const logoMat = new THREE.MeshStandardMaterial({ color: "#222222", roughness: 0.5 });
      const logo = new THREE.Mesh(new THREE.BoxGeometry(0.060, 0.0006, 0.010), logoMat);
      logo.position.set(bW * 0.28, bH / 2 - 0.002, bD / 2 - 0.005);
      group.add(logo);
    }


    // ─────────────────────────────────────────────
    // CISCO PTZ 4K CAMERA
    // Classic yoke-mount PTZ, 160×178×203mm (W×H×D)
    // Aluminum body, large dome head, prominent lens barrel,
    // Rear connector panel: HDMI 2.0, Ethernet, power
    // Available Carbon Black
    // Very similar yoke architecture to AVer cams but Cisco aesthetic:
    // cleaner lines, more rectangular head, wider lens
    // ─────────────────────────────────────────────
    else if (pid === "cisco-ptz-4k") {
      const cW = 0.165, cH = 0.221, cD = 0.196;

      const whiteMat = new THREE.MeshStandardMaterial({ color: "#f2f1ee", roughness: 0.30, metalness: 0.04 });
      const whiteSoft = new THREE.MeshStandardMaterial({ color: "#e8e7e3", roughness: 0.35, metalness: 0.03 });
      const darkMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.4, metalness: 0.2 });
      const glassMat = new THREE.MeshStandardMaterial({ color: "#0a1410", roughness: 0, metalness: 0.6, transparent: true, opacity: 0.9 });
      const lensGreen = new THREE.MeshStandardMaterial({ color: "#2a4a3a", roughness: 0.05, metalness: 0.7, transparent: true, opacity: 0.85 });
      const lensCoreM = new THREE.MeshStandardMaterial({ color: "#050505", roughness: 0, metalness: 1 });
      const rimMat = new THREE.MeshStandardMaterial({ color: "#3a3a3a", roughness: 0.2, metalness: 0.85 });
      const ledGreen = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 2.0, roughness: 0 });
      const textMat = new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 0.5 });

      // ── 1. DOME HEAD — bulat besar, putih, asimetris (lebih besar di depan untuk lensa) ──
      const domeGeo = new THREE.SphereGeometry(cW * 0.46, 40, 32, 0, Math.PI * 2, 0, Math.PI * 0.62);
      const dome = new THREE.Mesh(domeGeo, whiteMat);
      dome.castShadow = true;
      dome.position.set(0, cH * 0.20, -cD * 0.02);
      group.add(dome);

      // Sedikit pipih di bagian belakang dome agar tidak simetris sempurna
      const domeBack = new THREE.Mesh(new THREE.SphereGeometry(cW * 0.40, 32, 24, 0, Math.PI * 2, 0, Math.PI * 0.5), whiteSoft);
      domeBack.position.set(0, cH * 0.18, -cD * 0.10);
      domeBack.scale.set(1, 1, 0.7);
      group.add(domeBack);

      // ── 2. FRONT FACE FLAT — area datar untuk dudukan lensa ──
      const frontPlate = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.40, 36), whiteSoft);
      frontPlate.position.set(0, cH * 0.10, cD * 0.36);
      frontPlate.rotation.x = -0.15;
      group.add(frontPlate);

      // ── 3. LENS HOUSING — silinder besar menonjol depan-bawah dome ──
      const lensHousing = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.36, cW * 0.38, 0.045, 40), whiteMat);
      lensHousing.rotation.x = Math.PI / 2 - 0.15;
      lensHousing.position.set(0, cH * 0.06, cD * 0.38);
      group.add(lensHousing);

      // Inner bezel (gelap, dudukan lensa)
      const lensBezel = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.30, cW * 0.32, 0.012, 36), darkMat);
      lensBezel.rotation.x = Math.PI / 2 - 0.15;
      lensBezel.position.set(0, cH * 0.07, cD * 0.40);
      group.add(lensBezel);

      // ── 4. LENS BARREL — ciri khas lensa zoom besar dengan grooves ──
      const barrelOuter = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.27, cW * 0.29, 0.030, 32), darkMat);
      barrelOuter.rotation.x = Math.PI / 2 - 0.15;
      barrelOuter.position.set(0, cH * 0.075, cD * 0.415);
      group.add(barrelOuter);

      // Groove rings (efek lensa manual zoom/focus)
      for (let i = 0; i < 4; i++) {
        const groove = new THREE.Mesh(new THREE.TorusGeometry(cW * 0.255 - i * 0.004, 0.0015, 6, 28), rimMat);
        groove.rotation.x = Math.PI / 2 - 0.15;
        groove.position.set(0, cH * 0.075 + i * 0.0015, cD * 0.425 + i * 0.003);
        group.add(groove);
      }

      const barrelInner = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.22, cW * 0.25, 0.022, 30), darkMat);
      barrelInner.rotation.x = Math.PI / 2 - 0.15;
      barrelInner.position.set(0, cH * 0.078, cD * 0.44);
      group.add(barrelInner);

      // ── 5. LENS GLASS — hijau-tosca khas multi-coating ──
      const lensGlassOuter = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.20, 32), glassMat);
      lensGlassOuter.position.set(0, cH * 0.080, cD * 0.452);
      lensGlassOuter.rotation.x = -0.15;
      group.add(lensGlassOuter);

      const lensGlassMid = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.155, 28), lensGreen);
      lensGlassMid.position.set(0, cH * 0.081, cD * 0.4535);
      lensGlassMid.rotation.x = -0.15;
      group.add(lensGlassMid);

      const lensGlassInner = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.10, 24),
        new THREE.MeshStandardMaterial({ color: "#1a3530", roughness: 0, metalness: 0.75, transparent: true, opacity: 0.9 }));
      lensGlassInner.position.set(0, cH * 0.082, cD * 0.455);
      lensGlassInner.rotation.x = -0.15;
      group.add(lensGlassInner);

      const lensCore = new THREE.Mesh(new THREE.CircleGeometry(cW * 0.055, 20), lensCoreM);
      lensCore.position.set(0, cH * 0.083, cD * 0.4565);
      lensCore.rotation.x = -0.15;
      group.add(lensCore);

      // Lens highlight reflection
      const lensHL = new THREE.Mesh(new THREE.CircleGeometry(0.005, 10),
        new THREE.MeshStandardMaterial({ color: "#aaccbb", emissive: "#88aa99", emissiveIntensity: 0.5, roughness: 0 }));
      lensHL.position.set(-cW * 0.06, cH * 0.105, cD * 0.458);
      lensHL.rotation.x = -0.15;
      group.add(lensHL);

      // ── 6. BASE/COLLAR — leher penghubung dome ke housing ──
      const collar = new THREE.Mesh(new THREE.CylinderGeometry(cW * 0.48, cW * 0.50, 0.018, 40), whiteSoft);
      collar.position.set(0, -cH * 0.06, -cD * 0.02);
      group.add(collar);

      // ── 7. WEDGE WALL HOUSING — bentuk baji menonjol ke belakang-samping ──
      // Housing utama (bentuk trapesium memanjang ke kanan)
      const housingMain = new THREE.Mesh(new THREE.BoxGeometry(cW * 1.55, cH * 0.30, cD * 0.85), whiteMat);
      housingMain.castShadow = true;
      housingMain.position.set(cW * 0.25, -cH * 0.22, -cD * 0.05);
      group.add(housingMain);

      // Housing depan (lebih rendah, menyatu dengan collar dome)
      const housingFront = new THREE.Mesh(new THREE.BoxGeometry(cW * 0.95, cH * 0.22, cD * 0.55), whiteMat);
      housingFront.position.set(-cW * 0.05, -cH * 0.20, cD * 0.18);
      group.add(housingFront);

      // Tapered back edge (housing menyempit ke arah dinding)
      const housingBack = new THREE.Mesh(new THREE.BoxGeometry(cW * 1.45, cH * 0.26, 0.025), whiteSoft);
      housingBack.position.set(cW * 0.25, -cH * 0.22, -cD * 0.46);
      group.add(housingBack);

      // ── 8. VENTILATION GRILLE — di sisi kanan housing ──
      for (let i = 0; i < 6; i++) {
        const slat = new THREE.Mesh(new THREE.BoxGeometry(0.003, cH * 0.16, 0.006), darkMat);
        slat.position.set(cW * 0.62, -cH * 0.21, -cD * 0.05 + i * 0.012 - 0.030);
        group.add(slat);
      }

      // ── 9. POWER/STANDBY STRIP — strip hitam di depan-bawah ──
      const statusStrip = new THREE.Mesh(new THREE.BoxGeometry(cW * 1.10, 0.022, 0.008), darkMat);
      statusStrip.position.set(0, -cH * 0.34, cD * 0.32);
      group.add(statusStrip);

      // LED Power (hijau, kiri)
      const ledPower = new THREE.Mesh(new THREE.CircleGeometry(0.0035, 10), ledGreen);
      ledPower.position.set(-cW * 0.34, -cH * 0.34, cD * 0.324);
      group.add(ledPower);

      // LED Standby (kuning/amber, kanan power)
      const ledStandby = new THREE.Mesh(new THREE.CircleGeometry(0.0035, 10),
        new THREE.MeshStandardMaterial({ color: "#f59e0b", emissive: "#f59e0b", emissiveIntensity: 1.5, roughness: 0 }));
      ledStandby.position.set(-cW * 0.12, -cH * 0.34, cD * 0.324);
      group.add(ledStandby);

      // Text labels (representasi sederhana garis putih kecil)
      const powerLabel = new THREE.Mesh(new THREE.PlaneGeometry(0.022, 0.005), textMat);
      powerLabel.position.set(-cW * 0.30, -cH * 0.335, cD * 0.325);
      group.add(powerLabel);

      const standbyLabel = new THREE.Mesh(new THREE.PlaneGeometry(0.032, 0.005), textMat);
      standbyLabel.position.set(-cW * 0.02, -cH * 0.335, cD * 0.325);
      group.add(standbyLabel);

      // ── 10. BACK PORTS (tersembunyi di housing belakang) ──
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 });
      const ethPort = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.013, 0.005), portMat);
      ethPort.position.set(cW * 0.25, -cH * 0.22, -cD * 0.475);
      group.add(ethPort);

      // ── 11. WALL MOUNT BACK PLATE ──
      const mountMat = new THREE.MeshStandardMaterial({ color: "#d8d6d2", roughness: 0.4, metalness: 0.1 });
      const mountPlate = new THREE.Mesh(new THREE.BoxGeometry(cW * 1.0, cH * 0.20, 0.012), mountMat);
      mountPlate.position.set(cW * 0.25, -cH * 0.22, -cD * 0.49);
      group.add(mountPlate);
    }

    // ─────────────────────────────────────────────
    // CRESTRON CEILING SPEAKERS (round) — IC4T/IC6T/IC8T/ICI4T/ICI6T/
    // IC6ULPT/ICS8T/REF-IC6/REF-IC8/ULT-IC6/ULT-IC8/ULT-ICS8/ULT-IC8DT
    // Zero-bezel circular grille, magnetic mount, flush ceiling
    // ─────────────────────────────────────────────
    else if ([
      "crestron-ic4t", "crestron-ic6t", "crestron-ic8t",
      "crestron-ici4t", "crestron-ici6t", "crestron-ic6ulpt", "crestron-ics8t",
      "crestron-ic4p",
      "crestron-ref-ic6", "crestron-ref-ic8",
      "crestron-ult-ic6", "crestron-ult-ic8", "crestron-ult-ics8", "crestron-ult-ic8dt"
    ].includes(pid)) {
      const isSub = pid.includes("s8t") || pid.includes("ics8");
      const isLowProfile = pid === "crestron-ic6ulpt";
      const isPremium = pid.startsWith("crestron-ref") || pid.startsWith("crestron-ult");

      const R = width / 2;
      const H = height;

      const grilleMat = new THREE.MeshStandardMaterial({
        color: isPremium ? "#f5f3ef" : "#e8e6e2",
        roughness: 0.45, metalness: 0.05
      });
      const meshDotMat = new THREE.MeshStandardMaterial({ color: "#cbc9c4", roughness: 0.7 });
      const coneMat = new THREE.MeshStandardMaterial({ color: "#3a3835", roughness: 0.85, metalness: 0.05 });
      const domeMat = new THREE.MeshStandardMaterial({ color: "#9a978f", roughness: 0.3, metalness: 0.4 });
      const ringMat = new THREE.MeshStandardMaterial({ color: "#d4d2cd", roughness: 0.35, metalness: 0.15 });
      const switchMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.5, metalness: 0.3 });

      // ── 1. GRILLE FACE (terlihat dari bawah, menghadap ke ruangan — Y NEGATIF) ──
      const grilleFace = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 0.98, H * 0.12, 48), grilleMat);
      grilleFace.castShadow = true;
      grilleFace.position.set(0, -H * 0.06, 0);
      group.add(grilleFace);

      // Perforated dot pattern (concentric rings of small dots)
      const dotRings = [R * 0.25, R * 0.45, R * 0.62, R * 0.78, R * 0.92];
      dotRings.forEach((rad, ri) => {
        const count = 10 + ri * 8;
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.0025, 0.0025, 0.003, 6), meshDotMat);
          dot.position.set(Math.cos(angle) * rad, -H * 0.13, Math.sin(angle) * rad);
          group.add(dot);
        }
      });

      // ── 2. WOOFER CONE — terlihat sedikit di belakang grille (jika subwoofer, lebih besar) ──
      const coneR = isSub ? R * 0.85 : R * 0.55;
      const cone = new THREE.Mesh(new THREE.CylinderGeometry(coneR, coneR * 0.9, H * 0.30, 36), coneMat);
      cone.position.set(0, -H * 0.02, 0);
      group.add(cone);

      // ── 3. TWEETER DOME — kecil, di tengah (skip jika subwoofer) ──
      if (!isSub) {
        const tweeterDome = new THREE.Mesh(new THREE.SphereGeometry(R * 0.10, 20, 16, 0, Math.PI * 2, 0, Math.PI * 0.5), domeMat);
        tweeterDome.rotation.x = Math.PI;
        tweeterDome.position.set(0, -H * 0.16, 0);
        group.add(tweeterDome);

        const tweeterRing = new THREE.Mesh(new THREE.TorusGeometry(R * 0.13, 0.003, 8, 28), ringMat);
        tweeterRing.rotation.x = Math.PI / 2;
        tweeterRing.position.set(0, -H * 0.14, 0);
        group.add(tweeterRing);
      }

      // ── 4. BACKCAN — bagian belakang silinder (tersembunyi di plafon — Y POSITIF) ──
      const backcan = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.82, R * 0.88, H * 0.85, 36), grilleMat);
      backcan.position.set(0, H * 0.45, 0);
      group.add(backcan);

      // Transformer tap switch (kotak kecil di backcan, ciri khas Saros)
      if (!isPremium) {
        const tapSwitch = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.012, 0.006), switchMat);
        tapSwitch.position.set(R * 0.5, H * 0.35, R * 0.5);
        group.add(tapSwitch);
      }

      // ── 5. LOW-PROFILE variant: backcan lebih pendek ──
      if (isLowProfile) {
        backcan.scale.y = 0.5;
        backcan.position.y = H * 0.20;
      }
    }

    // ─────────────────────────────────────────────
    // CRESTRON ICT5T-22 — drop-ceiling TILE speaker, 2x2 ft
    // Bentuk kotak persegi, sangat tipis, dipasang di grid plafon
    // ─────────────────────────────────────────────
    else if (pid === "crestron-ict5t22") {
      const tileMat = new THREE.MeshStandardMaterial({ color: "#e8e6e2", roughness: 0.5, metalness: 0.03 });
      const gridMat = new THREE.MeshStandardMaterial({ color: "#c8c6c2", roughness: 0.6 });
      const coneMat = new THREE.MeshStandardMaterial({ color: "#3a3835", roughness: 0.85, metalness: 0.05 });
      const domeMat = new THREE.MeshStandardMaterial({ color: "#9a978f", roughness: 0.3, metalness: 0.4 });

      // ── 1. TILE PANEL — persegi 2x2ft ──
      const tile = new THREE.Mesh(new THREE.BoxGeometry(width, height * 0.4, depth), tileMat);
      tile.castShadow = true;
      group.add(tile);

      // Grid frame lines (tee bar khas drop ceiling)
      const frameThick = 0.012;
      [[-width / 2, 0], [width / 2, 0]].forEach(([fx]) => {
        const frame = new THREE.Mesh(new THREE.BoxGeometry(frameThick, height * 0.42, depth), gridMat);
        frame.position.set(fx, 0, 0);
        group.add(frame);
      });
      [[0, -depth / 2], [0, depth / 2]].forEach(([, fz]) => {
        const frame = new THREE.Mesh(new THREE.BoxGeometry(width, height * 0.42, frameThick), gridMat);
        frame.position.set(0, 0, fz);
        group.add(frame);
      });

      // ── 2. SPEAKER CONE — di tengah tile ──
      const coneR = width * 0.28;
      const cone = new THREE.Mesh(new THREE.CylinderGeometry(coneR, coneR * 0.85, height * 0.45, 32), coneMat);
      cone.position.set(0, -height * 0.20, 0);
      group.add(cone);

      // Perforated dot pattern di sekitar cone
      for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2;
        const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.0025, 0.0025, 0.003, 6),
          new THREE.MeshStandardMaterial({ color: "#cbc9c4", roughness: 0.7 }));
        dot.position.set(Math.cos(angle) * coneR * 1.4, height * 0.18, Math.sin(angle) * coneR * 1.4);
        group.add(dot);
      }

      // Tweeter dome kecil — sejajar dengan cone, mengarah ke ruangan (Y NEGATIF)
      const tweeter = new THREE.Mesh(new THREE.SphereGeometry(coneR * 0.15, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.5), domeMat);
      tweeter.rotation.x = Math.PI;
      tweeter.position.set(0, -height * 0.24, 0);
      group.add(tweeter);

      // Backcan di belakang — mengarah ke plafon (Y POSITIF)
      const backcan = new THREE.Mesh(new THREE.BoxGeometry(width * 0.6, height * 0.7, depth * 0.6), tileMat);
      backcan.position.set(0, height * 0.55, 0);
      group.add(backcan);
    }

    // ─────────────────────────────────────────────
    // CRESTRON SAROS SB-200-P — Sound Bar untuk under-display
    // Bentuk panjang horizontal, wall/shelf mount
    // ─────────────────────────────────────────────
    else if (pid === "crestron-sb200") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: 0.35, metalness: 0.3 });
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#0f0f0f", roughness: 0.9, metalness: 0.05 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#f59e0b", emissive: "#f59e0b", emissiveIntensity: 1.8, roughness: 0 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.8 });
      const mountMat = new THREE.MeshStandardMaterial({ color: "#333", roughness: 0.4, metalness: 0.7 });

      // ── 1. MAIN BODY — panjang horizontal, rounded edges ──
      const body = new THREE.Mesh(new THREE.BoxGeometry(width - 0.02, height, depth), bodyMat);
      body.castShadow = true;
      group.add(body);

      // Rounded end caps
      [-1, 1].forEach(side => {
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(height / 2, height / 2, depth, 20), bodyMat);
        cap.rotation.x = Math.PI / 2;
        cap.position.set(side * (width - 0.02) / 2, 0, 0);
        group.add(cap);
      });

      // ── 2. FRONT GRILLE — perforated fabric mesh, hampir penuh ──
      const grille = new THREE.Mesh(new THREE.BoxGeometry(width - 0.06, height * 0.7, 0.004), grilleMat);
      grille.position.set(0, 0, depth / 2 + 0.001);
      group.add(grille);

      // Perforation dots (grid pola)
      const dotCols = 60, dotRows = 4;
      for (let r = 0; r < dotRows; r++) {
        for (let c = 0; c < dotCols; c++) {
          const dot = new THREE.Mesh(new THREE.CircleGeometry(0.0015, 6),
            new THREE.MeshStandardMaterial({ color: "#000000", roughness: 1 }));
          dot.position.set(
            -width / 2 + 0.04 + c * ((width - 0.08) / dotCols),
            -height * 0.25 + r * (height * 0.5 / (dotRows - 1)),
            depth / 2 + 0.002
          );
          group.add(dot);
        }
      }

      // ── 3. STATUS LED — kecil, kanan bawah depan ──
      const led = new THREE.Mesh(new THREE.CircleGeometry(0.003, 10), ledMat);
      led.position.set(width / 2 - 0.04, -height * 0.30, depth / 2 + 0.002);
      group.add(led);

      // ── 4. BACK PORTS — terminal block + RCA ──
      const portBlock = new THREE.Mesh(new THREE.BoxGeometry(0.030, 0.012, 0.008), portMat);
      portBlock.position.set(-width * 0.30, 0, -depth / 2 - 0.002);
      group.add(portBlock);

      const rcaPort1 = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.008, 12), portMat);
      rcaPort1.rotation.x = Math.PI / 2;
      rcaPort1.position.set(width * 0.05, 0, -depth / 2 - 0.004);
      group.add(rcaPort1);

      const rcaPort2 = rcaPort1.clone();
      rcaPort2.position.set(width * 0.10, 0, -depth / 2 - 0.004);
      group.add(rcaPort2);

      // ── 5. WALL MOUNT BRACKET — di belakang ──
      const mountBracket = new THREE.Mesh(new THREE.BoxGeometry(width * 0.5, 0.015, 0.020), mountMat);
      mountBracket.position.set(0, -height * 0.3, -depth / 2 - 0.012);
      group.add(mountBracket);
    }

    // ─────────────────────────────────────────────
    // BOSE DESIGNMAX CEILING SPEAKERS (round) —
    // DM8C/DM6C/DM5C/DM3C/DM2C-LP/DM8C-SUB
    // Minimum-bezel grille, removable logo, QuickHold mount
    // Y NEGATIF = grille/woofer (ke ruangan), Y POSITIF = backcan (ke plafon)
    // ─────────────────────────────────────────────
    else if (["bose-dm8c", "bose-dm8c-sub", "bose-dm6c", "bose-dm5c", "bose-dm3c", "bose-dm2c-lp"].includes(pid)) {
      const isSub = pid === "bose-dm8c-sub";
      const R = width / 2;
      const H = height;

      const grilleMat  = new THREE.MeshStandardMaterial({ color: "#f0eee9", roughness: 0.40, metalness: 0.05 });
      const baffleMat  = new THREE.MeshStandardMaterial({ color: "#e8e6e1", roughness: 0.35, metalness: 0.04 });
      const meshDotMat = new THREE.MeshStandardMaterial({ color: "#c8c6c0", roughness: 0.7 });
      const coneMat    = new THREE.MeshStandardMaterial({ color: "#3a3835", roughness: 0.85, metalness: 0.05 });
      const domeMat    = new THREE.MeshStandardMaterial({ color: "#9a978f", roughness: 0.3, metalness: 0.4 });
      const logoMat    = new THREE.MeshStandardMaterial({ color: "#aaa8a2", roughness: 0.4, metalness: 0.2 });

      // ── 1. BAFFLE/GRILLE FACE (Y negatif, menghadap ruangan) ──
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 0.97, H * 0.10, 48), baffleMat);
      baffle.castShadow = true;
      baffle.position.set(0, -H * 0.05, 0);
      group.add(baffle);

      // Perforated grille disc (sedikit lebih kecil dari baffle)
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.93, R * 0.91, H * 0.06, 48), grilleMat);
      grille.position.set(0, -H * 0.11, 0);
      group.add(grille);

      // Perforation dot pattern (concentric rings)
      const dotRings = [R * 0.22, R * 0.40, R * 0.56, R * 0.70, R * 0.82];
      dotRings.forEach((rad, ri) => {
        const count = 12 + ri * 8;
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.0022, 0.0022, 0.0025, 6), meshDotMat);
          dot.position.set(Math.cos(angle) * rad, -H * 0.14, Math.sin(angle) * rad);
          group.add(dot);
        }
      });

      // ── 2. WOOFER CONE (Y negatif lebih dalam, terlihat melalui grille) ──
      const coneR = isSub ? R * 0.80 : R * 0.50;
      const cone = new THREE.Mesh(new THREE.CylinderGeometry(coneR, coneR * 0.88, H * 0.22, 36), coneMat);
      cone.position.set(0, -H * 0.04, 0);
      group.add(cone);

      // ── 3. COAXIAL TWEETER/COMPRESSION DRIVER — pusat (skip jika subwoofer) ──
      if (!isSub) {
        const tweeterDome = new THREE.Mesh(new THREE.SphereGeometry(R * 0.085, 20, 16, 0, Math.PI * 2, 0, Math.PI * 0.5), domeMat);
        tweeterDome.rotation.x = Math.PI;
        tweeterDome.position.set(0, -H * 0.18, 0);
        group.add(tweeterDome);
      }

      // ── 4. REMOVABLE LOGO — kecil, di tepi grille ──
      const logo = new THREE.Mesh(new THREE.CircleGeometry(R * 0.08, 16), logoMat);
      logo.rotation.x = Math.PI;
      logo.position.set(R * 0.55, -H * 0.13, R * 0.55);
      group.add(logo);

      // ── 5. BACKCAN (Y positif, masuk ke plafon) ──
      const backcan = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.83, R * 0.90, H * 0.82, 36), grilleMat);
      backcan.position.set(0, H * 0.43, 0);
      group.add(backcan);

      // QuickHold mounting arm nubs (3 titik kecil di backcan)
      for (let i = 0; i < 3; i++) {
        const angle = (i / 3) * Math.PI * 2;
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.008, 0.020),
          new THREE.MeshStandardMaterial({ color: "#888", roughness: 0.4, metalness: 0.6 }));
        arm.position.set(Math.cos(angle) * R * 0.85, H * 0.30, Math.sin(angle) * R * 0.85);
        arm.rotation.y = angle;
        group.add(arm);
      }
    }

    // ─────────────────────────────────────────────
    // BOSE FREESPACE FS4CE / FS2C / FS3 SATELLITE / FS3 ACOUSTIMASS
    // Round ceiling speakers, similar bauplan to DesignMax but slightly
    // simpler grille texture
    // ─────────────────────────────────────────────
    else if (["bose-fs4ce", "bose-fs2c", "bose-fs3-satellite", "bose-fs3-acoustimass"].includes(pid)) {
      const isSubModule = pid === "bose-fs3-acoustimass";
      const R = width / 2;
      const H = height;

      const grilleMat  = new THREE.MeshStandardMaterial({ color: "#f0eee9", roughness: 0.45, metalness: 0.05 });
      const baffleMat  = new THREE.MeshStandardMaterial({ color: "#e8e6e1", roughness: 0.40, metalness: 0.04 });
      const meshDotMat = new THREE.MeshStandardMaterial({ color: "#c8c6c0", roughness: 0.7 });
      const coneMat    = new THREE.MeshStandardMaterial({ color: "#3a3835", roughness: 0.85, metalness: 0.05 });

      // ── 1. BAFFLE (Y negatif) ──
      const baffle = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 0.96, H * 0.12, 40), baffleMat);
      baffle.castShadow = true;
      baffle.position.set(0, -H * 0.06, 0);
      group.add(baffle);

      const grille = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.90, R * 0.88, H * 0.06, 40), grilleMat);
      grille.position.set(0, -H * 0.13, 0);
      group.add(grille);

      // Dot pattern
      const dotRings = [R * 0.25, R * 0.45, R * 0.65, R * 0.80];
      dotRings.forEach((rad, ri) => {
        const count = 10 + ri * 7;
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.002, 0.002, 0.0025, 6), meshDotMat);
          dot.position.set(Math.cos(angle) * rad, -H * 0.16, Math.sin(angle) * rad);
          group.add(dot);
        }
      });

      // ── 2. WOOFER/DRIVER — lebih besar jika Acoustimass module (subwoofer) ──
      const coneR = isSubModule ? R * 0.78 : R * 0.45;
      const cone = new THREE.Mesh(new THREE.CylinderGeometry(coneR, coneR * 0.85, H * 0.20, 32), coneMat);
      cone.position.set(0, -H * 0.04, 0);
      group.add(cone);

      // ── 3. BACKCAN (Y positif) ──
      const backcan = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.80, R * 0.88, H * 0.80, 32), grilleMat);
      backcan.position.set(0, H * 0.42, 0);
      group.add(backcan);
    }

    // ─────────────────────────────────────────────
    // BOSE DESIGNMAX DM6PE / DM5P / DM3P — PENDANT speaker
    // Bentuk KERUCUT TERPOTONG (frustum) — sempit di atas, melebar ke bawah
    // ORIGIN (Y=0 lokal) = ujung atas kabel, tepat menempel di plafon
    // ─────────────────────────────────────────────
    else if (["bose-dm6pe", "bose-dm5p", "bose-dm3p"].includes(pid)) {
      const bodyMat   = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.55, metalness: 0.10 });
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#0d0d0d", roughness: 0.85, metalness: 0.05 });
      const ringMat   = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.30, metalness: 0.60 });
      const cableMat  = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.30, metalness: 0.70 });
      const logoMat   = new THREE.MeshStandardMaterial({ color: "#444", roughness: 0.4, metalness: 0.3 });

      const topR  = width * 0.28;
      const botR  = width * 0.50;
      const bodyH = height * 0.78;
      const cableLen = height * 0.30;

      // ── ANCHOR/ORIGIN — di Y=0 lokal, ini yang akan menempel ke plafon ──
      const anchor = new THREE.Mesh(new THREE.CylinderGeometry(0.010, 0.010, 0.006, 16), ringMat);
      anchor.position.set(0, -0.003, 0); // sedikit turun supaya tidak menembus plafon
      group.add(anchor);

      // ── SUSPENSION CABLE — menggantung TURUN dari anchor (Y negatif) ──
      const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.0018, 0.0018, cableLen, 8), cableMat);
      cable.position.set(0, -cableLen / 2 - 0.006, 0);
      group.add(cable);

      // Posisi puncak badan speaker = ujung bawah kabel
      const bodyTopY = -cableLen - 0.006;

      // ── MAIN BODY — frustum, puncak di bodyTopY, melebar ke bawah ──
      const body = new THREE.Mesh(new THREE.CylinderGeometry(topR, botR, bodyH, 40), bodyMat);
      body.castShadow = true;
      body.position.set(0, bodyTopY - bodyH / 2, 0);
      group.add(body);

      // Top cap kecil (penutup atas, tempat kabel masuk)
      const topCap = new THREE.Mesh(new THREE.CylinderGeometry(topR * 0.85, topR, 0.012, 32), bodyMat);
      topCap.position.set(0, bodyTopY, 0);
      group.add(topCap);

      // ── BOTTOM RING — cincin tipis di dasar, sebelum grille ──
      const bottomRingY = bodyTopY - bodyH;
      const bottomRing = new THREE.Mesh(new THREE.TorusGeometry(botR * 0.97, 0.004, 8, 40), ringMat);
      bottomRing.rotation.x = Math.PI / 2;
      bottomRing.position.set(0, bottomRingY, 0);
      group.add(bottomRing);

      // ── GRILLE BULAT — dasar paling bawah, menghadap ruangan ──
      const grilleY = bottomRingY - height * 0.05;
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(botR * 0.95, botR * 0.90, height * 0.10, 36), grilleMat);
      grille.position.set(0, grilleY, 0);
      group.add(grille);

      // Perforated dot pattern pada grille
      const dotY = grilleY - height * 0.04;
      const dotRings = [botR * 0.25, botR * 0.45, botR * 0.65, botR * 0.82];
      dotRings.forEach((rad, ri) => {
        const count = 10 + ri * 7;
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.0018, 0.0018, 0.002, 6),
            new THREE.MeshStandardMaterial({ color: "#000000", roughness: 0.9 }));
          dot.position.set(Math.cos(angle) * rad, dotY, Math.sin(angle) * rad);
          group.add(dot);
        }
      });

      // ── BOSE LOGO — kecil, di sisi badan kerucut ──
      const logo = new THREE.Mesh(new THREE.PlaneGeometry(width * 0.18, height * 0.025), logoMat);
      logo.position.set(0, bodyTopY - bodyH / 2, botR * 0.85);
      group.add(logo);

      // Simpan total tinggi efektif (untuk referensi jika diperlukan tooltip/measurement)
      group.userData.pendantTotalHeight = cableLen + bodyH + height * 0.19;
    }

    // ─────────────────────────────────────────────
    // BOSE DESIGNMAX LUNA DML88P — Ring Array pendant
    // Bentuk ring/donat besar dengan 8 driver melingkar + woofer tengah
    // ─────────────────────────────────────────────
    else if (pid === "bose-dml88p") {
      const bodyMat  = new THREE.MeshStandardMaterial({ color: "#181818", roughness: 0.30, metalness: 0.30 });
      const ringMat  = new THREE.MeshStandardMaterial({ color: "#0f0f0f", roughness: 0.35, metalness: 0.35 });
      const coneMat  = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.80, metalness: 0.10 });
      const driverMat = new THREE.MeshStandardMaterial({ color: "#3a3a3a", roughness: 0.5, metalness: 0.4 });
      const cableMat = new THREE.MeshStandardMaterial({ color: "#666", roughness: 0.4, metalness: 0.6 });
      const flangeMat = new THREE.MeshStandardMaterial({ color: "#222", roughness: 0.4, metalness: 0.3 });

      const R = width / 2;

      // ── 1. CEILING FLANGE + SUSPENSION ──
      const flange = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.18, R * 0.18, 0.008, 20), flangeMat);
      flange.position.set(0, height * 0.55, 0);
      group.add(flange);

      const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.0025, 0.0025, height * 0.20, 8), cableMat);
      cable.position.set(0, height * 0.42, 0);
      group.add(cable);

      // ── 2. MAIN RING BODY — donat besar, ciri khas Luna ──
      const ringTube = new THREE.Mesh(new THREE.TorusGeometry(R * 0.72, R * 0.28, 20, 48), bodyMat);
      ringTube.rotation.x = Math.PI / 2;
      ringTube.castShadow = true;
      group.add(ringTube);

      // Outer rim trim
      const outerTrim = new THREE.Mesh(new THREE.TorusGeometry(R * 0.98, 0.006, 8, 48), ringMat);
      outerTrim.rotation.x = Math.PI / 2;
      group.add(outerTrim);

      // ── 3. RING ARRAY — 8 mid-high drivers melingkar di permukaan bawah ring ──
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const dx = Math.cos(angle) * R * 0.72;
        const dz = Math.sin(angle) * R * 0.72;

        const driverHousing = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.11, R * 0.11, 0.025, 18), driverMat);
        driverHousing.position.set(dx, -height * 0.12, dz);
        group.add(driverHousing);

        const driverCone = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.075, R * 0.06, 0.012, 16), coneMat);
        driverCone.position.set(dx, -height * 0.135, dz);
        group.add(driverCone);
      }

      // ── 4. INTEGRATED 8" WOOFER — di tengah ring (lubang donat) ──
      const centerWoofer = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.55, R * 0.50, height * 0.18, 36), coneMat);
      centerWoofer.position.set(0, -height * 0.05, 0);
      group.add(centerWoofer);

      const wooferDustcap = new THREE.Mesh(new THREE.SphereGeometry(R * 0.18, 20, 16, 0, Math.PI * 2, 0, Math.PI * 0.5), driverMat);
      wooferDustcap.rotation.x = Math.PI;
      wooferDustcap.position.set(0, -height * 0.13, 0);
      group.add(wooferDustcap);

      // ── 5. TOP COVER — connection panel cover di atas ring ──
      const topCover = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.30, R * 0.30, height * 0.10, 28), bodyMat);
      topCover.position.set(0, height * 0.30, 0);
      group.add(topCover);
    }

    // ─────────────────────────────────────────────
    // BOSE FREESPACE FS2P — PENDANT speaker compact
    // Bentuk frustum kerucut, ORIGIN Y=0 = ujung atas kabel (menempel plafon)
    // ─────────────────────────────────────────────
    else if (pid === "bose-fs2p") {
      const bodyMat   = new THREE.MeshStandardMaterial({ color: "#1c1c1c", roughness: 0.55, metalness: 0.10 });
      const grilleMat = new THREE.MeshStandardMaterial({ color: "#0d0d0d", roughness: 0.85, metalness: 0.05 });
      const ringMat   = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.30, metalness: 0.60 });
      const cableMat  = new THREE.MeshStandardMaterial({ color: "#888888", roughness: 0.30, metalness: 0.70 });

      const topR  = width * 0.26;
      const botR  = width * 0.48;
      const bodyH = height * 0.76;
      const cableLen = height * 0.32;

      // ── ANCHOR — Y=0 lokal, menempel ke plafon ──
      const anchor = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.005, 14), ringMat);
      anchor.position.set(0, -0.0025, 0);
      group.add(anchor);

      // ── SUSPENSION CABLE — turun dari anchor ──
      const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.0015, 0.0015, cableLen, 8), cableMat);
      cable.position.set(0, -cableLen / 2 - 0.005, 0);
      group.add(cable);

      const bodyTopY = -cableLen - 0.005;

      // ── MAIN BODY — frustum ──
      const body = new THREE.Mesh(new THREE.CylinderGeometry(topR, botR, bodyH, 36), bodyMat);
      body.castShadow = true;
      body.position.set(0, bodyTopY - bodyH / 2, 0);
      group.add(body);

      const topCap = new THREE.Mesh(new THREE.CylinderGeometry(topR * 0.85, topR, 0.010, 28), bodyMat);
      topCap.position.set(0, bodyTopY, 0);
      group.add(topCap);

      // ── BOTTOM RING ──
      const bottomRingY = bodyTopY - bodyH;
      const bottomRing = new THREE.Mesh(new THREE.TorusGeometry(botR * 0.97, 0.0035, 8, 36), ringMat);
      bottomRing.rotation.x = Math.PI / 2;
      bottomRing.position.set(0, bottomRingY, 0);
      group.add(bottomRing);

      // ── GRILLE BAWAH ──
      const grilleY = bottomRingY - height * 0.045;
      const grille = new THREE.Mesh(new THREE.CylinderGeometry(botR * 0.95, botR * 0.90, height * 0.09, 32), grilleMat);
      grille.position.set(0, grilleY, 0);
      group.add(grille);

      // Perforation
      const dotY = grilleY - height * 0.035;
      const dotRings = [botR * 0.28, botR * 0.50, botR * 0.72];
      dotRings.forEach((rad, ri) => {
        const count = 8 + ri * 6;
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.0015, 0.0015, 0.0018, 6),
            new THREE.MeshStandardMaterial({ color: "#000000", roughness: 0.9 }));
          dot.position.set(Math.cos(angle) * rad, dotY, Math.sin(angle) * rad);
          group.add(dot);
        }
      });

      group.userData.pendantTotalHeight = cableLen + bodyH + height * 0.17;
    }

    // ─────────────────────────────────────────────
    // CRESTRON AIRMEDIA RECEIVER — AM-3000-WF / AM-3100-WF
    // Kotak persegi tipis (127×127×31mm), plastik hitam, ventilasi
    // sisi kiri-kanan, LED status depan, port di belakang.
    // Tidak ada file .glb — geometri dibuat manual di sini karena
    // form factornya sederhana (kotak persegi flat), jadi cukup presisi
    // dibangun dari primitive tanpa perlu model 3D asli.
    // ─────────────────────────────────────────────
    else if (pid === "crestron-am3000wf" || pid === "crestron-am3100wf") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#161616", roughness: 0.55, metalness: 0.15 });
      const topMat = new THREE.MeshStandardMaterial({ color: "#1f1f1f", roughness: 0.4, metalness: 0.2 });
      const ventMat = new THREE.MeshStandardMaterial({ color: "#050505", roughness: 0.9 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 1.6, roughness: 0.1 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.7, metalness: 0.4 });
      const logoMat = new THREE.MeshStandardMaterial({ color: "#3a3a3a", roughness: 0.3, metalness: 0.5 });

      // 1. BODY — kotak persegi tipis
      const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), bodyMat);
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);

      // Top panel — sedikit lebih terang, inset tipis
      const top = new THREE.Mesh(new THREE.BoxGeometry(width - 0.006, 0.002, depth - 0.006), topMat);
      top.position.set(0, height / 2 + 0.001, 0);
      group.add(top);

      // 2. VENT SLOTS — garis ventilasi tipis di sisi kiri & kanan
      const ventCount = 8;
      [-1, 1].forEach((side) => {
        for (let i = 0; i < ventCount; i++) {
          const vent = new THREE.Mesh(new THREE.BoxGeometry(0.001, height * 0.5, 0.012), ventMat);
          vent.position.set(side * (width / 2 + 0.0005), 0, -depth * 0.32 + i * (depth * 0.64 / (ventCount - 1)));
          group.add(vent);
        }
      });

      // 3. LOGO PLATE — persegi kecil di tengah top panel
      const logo = new THREE.Mesh(new THREE.BoxGeometry(width * 0.28, 0.0015, depth * 0.12), logoMat);
      logo.position.set(0, height / 2 + 0.0025, -depth * 0.22);
      group.add(logo);

      // 4. STATUS LED STRIP — tipis, depan, menyala (warna ikut accentColor produk)
      const led = new THREE.Mesh(new THREE.BoxGeometry(width * 0.22, 0.003, 0.004), ledMat);
      led.position.set(0, height / 2 + 0.0025, depth * 0.34);
      group.add(led);

      // 5. BACK PORTS — HDMI out, Ethernet, USB, DC power, berjajar rapi
      const portsBack = [
        { w: 0.014, h: 0.005 }, // HDMI out
        { w: 0.010, h: 0.008 }, // Ethernet RJ-45
        { w: 0.008, h: 0.003 }, // USB 3.0 (setup/firmware)
        { w: 0.006, h: 0.006 }, // DC power jack
      ];
      let cursorX = -width * 0.36;
      portsBack.forEach((p) => {
        const port = new THREE.Mesh(new THREE.BoxGeometry(p.w, p.h, 0.006), portMat);
        port.position.set(cursorX, -height / 2 + p.h / 2 + 0.003, -depth / 2 - 0.002);
        group.add(port);
        cursorX += p.w + width * 0.06;
      });

      // 6. RUBBER FEET — 4 kaki karet kecil di bawah
      [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) => {
        const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, 0.002, 10), ventMat);
        foot.position.set(sx * width * 0.4, -height / 2 - 0.001, sz * depth * 0.4);
        group.add(foot);
      });
    }

    // ─────────────────────────────────────────────
    // CRESTRON AIRMEDIA RECEIVER — AM-3200
    // Chassis lebih besar (188×177×33mm) & lebih lengkap: rack/rail-
    // mountable, 2 antena eksternal, ventilasi top panel, port lebih
    // banyak (dual HDMI in/out + Ethernet PoE). Bukan model .glb —
    // dibangun dari primitive three.js seperti AM-3000/3100-WF di atas.
    // ─────────────────────────────────────────────
    else if (pid === "crestron-am3200") {
      const bodyMat = new THREE.MeshStandardMaterial({ color: "#161616", roughness: 0.5, metalness: 0.2 });
      const topMat = new THREE.MeshStandardMaterial({ color: "#1f1f1f", roughness: 0.35, metalness: 0.25 });
      const ventMat = new THREE.MeshStandardMaterial({ color: "#050505", roughness: 0.9 });
      const ledMat = new THREE.MeshStandardMaterial({ color: "#3b82f6", emissive: "#3b82f6", emissiveIntensity: 1.6, roughness: 0.1 });
      const portMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.7, metalness: 0.4 });
      const antennaMat = new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.4, metalness: 0.5 });
      const earMat = new THREE.MeshStandardMaterial({ color: "#333333", roughness: 0.4, metalness: 0.7 });

      // 1. BODY — kotak rectangular, lebih besar dari AM-3000/3100
      const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), bodyMat);
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);

      const top = new THREE.Mesh(new THREE.BoxGeometry(width - 0.01, 0.002, depth - 0.01), topMat);
      top.position.set(0, height / 2 + 0.001, 0);
      group.add(top);

      // 2. VENT SLOTS — baris ventilasi di top panel (chassis lebih besar dari AM-3000/3100)
      const ventRows = 10, ventCols = 3;
      for (let r = 0; r < ventRows; r++) {
        for (let c = 0; c < ventCols; c++) {
          const vent = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.001, width * 0.03), ventMat);
          vent.rotation.y = Math.PI / 2;
          vent.position.set(
            -width * 0.32 + c * (width * 0.32),
            height / 2 + 0.0015,
            -depth * 0.3 + r * (depth * 0.6 / (ventRows - 1))
          );
          group.add(vent);
        }
      }

      // 3. EXTERNAL ANTENNAS — 2 buah, sedikit miring keluar, di sisi belakang
      [-1, 1].forEach((side) => {
        const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.005, 0.09, 10), antennaMat);
        antenna.position.set(side * width * 0.38, height / 2 + 0.045, -depth * 0.4);
        antenna.rotation.z = side * 0.12;
        group.add(antenna);

        const antennaBase = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.01, 10), portMat);
        antennaBase.position.set(side * width * 0.38, height / 2 + 0.005, -depth * 0.4);
        group.add(antennaBase);
      });

      // 4. STATUS LED — depan tengah, menyala
      const led = new THREE.Mesh(new THREE.BoxGeometry(width * 0.16, 0.003, 0.004), ledMat);
      led.position.set(0, height / 2 + 0.0025, depth * 0.44);
      group.add(led);

      // 5. BACK PORTS — dual HDMI in/out, Ethernet PoE, USB, DC power
      const portsBack = [
        { w: 0.014, h: 0.005 }, // HDMI in
        { w: 0.014, h: 0.005 }, // HDMI out
        { w: 0.010, h: 0.008 }, // Ethernet PoE RJ-45
        { w: 0.008, h: 0.003 }, // USB
        { w: 0.007, h: 0.007 }, // DC power
      ];
      let cursorX2 = -width * 0.4;
      portsBack.forEach((p) => {
        const port = new THREE.Mesh(new THREE.BoxGeometry(p.w, p.h, 0.006), portMat);
        port.position.set(cursorX2, -height / 2 + p.h / 2 + 0.004, -depth / 2 - 0.002);
        group.add(port);
        cursorX2 += p.w + width * 0.045;
      });

      // 6. RACK/RAIL MOUNT EARS — tab kecil di sisi kiri & kanan
      [-1, 1].forEach((side) => {
        const ear = new THREE.Mesh(new THREE.BoxGeometry(0.012, height * 0.7, 0.03), earMat);
        ear.position.set(side * (width / 2 + 0.006), 0, depth * 0.32);
        group.add(ear);
      });

      // 7. RUBBER FEET
      [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz]) => {
        const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.003, 10), ventMat);
        foot.position.set(sx * width * 0.42, -height / 2 - 0.0015, sz * depth * 0.42);
        group.add(foot);
      });
    }

    // ─────────────────────────────────────────────
    // EXTRON CABLE CUBBY SERIES — enclosure kabel yang dibenamkan
    // (recessed) ke lubang meja. Field `height` produk ini SENGAJA
    // diisi ketebalan lid yang kelihatan DI ATAS meja (beberapa mm
    // saja), BUKAN "enclosure depth" resmi dari datasheet Extron
    // (yang itu kedalaman DI BAWAH meja, tidak kelihatan di ruangan).
    // Nilai depth resmi disimpan di _meta.trueDepthBelowSurface untuk
    // referensi. Satu blok dipakai bersama untuk 8 varian, dibedakan
    // lewat _meta.type3D: "cablecubby-round" (100), "cablecubby-single"
    // (202/500/700/1202/1252ms), "cablecubby-double" (1402, dua lid
    // berdampingan), "cablecubby-open" (650ut, tray terbuka tanpa lid
    // metal baku — lid aslinya kayu custom dibuat tukang kayu instalasi).
    // ─────────────────────────────────────────────
    else if (pid.startsWith("extron-cablecubby")) {
      const shape = item.product._meta?.type3D || "cablecubby-single";
      const lidMat = new THREE.MeshStandardMaterial({ color: color || "#1c1c1c", roughness: 0.35, metalness: 0.6 });
      const seamMat = new THREE.MeshStandardMaterial({ color: "#000000", roughness: 0.8 });
      const frameMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.5, metalness: 0.5 });
      const hingeMat = new THREE.MeshStandardMaterial({ color: "#555555", roughness: 0.3, metalness: 0.8 });

      if (shape === "cablecubby-round") {
        // CC 100 — enclosure bundar, kompak
        const radius = width / 2;
        const lid = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 32), lidMat);
        lid.castShadow = true;
        group.add(lid);

        // seam ring — garis tepi lid
        const ring = new THREE.Mesh(new THREE.TorusGeometry(radius - 0.004, 0.0012, 6, 32), seamMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = height / 2 + 0.0004;
        group.add(ring);

        // hinge kecil di satu sisi (lid tilt-up)
        const hinge = new THREE.Mesh(new THREE.BoxGeometry(radius * 0.35, height * 0.5, 0.006), hingeMat);
        hinge.position.set(0, 0, -radius + 0.003);
        group.add(hinge);
      } else if (shape === "cablecubby-open") {
        // CC 650 UT — tray terbuka di bawah meja, TANPA lid metal baku
        // (lid aslinya material kayu/meja custom, dipasang instalator —
        // jadi hanya rangka/frame metal yang digambar, bukan lid solid)
        const wallT = 0.006;
        [[-1, 0], [1, 0], [0, -1], [0, 1]].forEach(([sx, sz]) => {
          const isX = sx !== 0;
          const w = isX ? wallT : width;
          const d = isX ? depth : wallT;
          const rim = new THREE.Mesh(new THREE.BoxGeometry(w, Math.max(height, 0.004), d), frameMat);
          rim.position.set(sx * (width / 2 - wallT / 2), 0, sz * (depth / 2 - wallT / 2));
          group.add(rim);
        });
      } else if (shape === "cablecubby-double") {
        // CC 1402 — double-sided, dua lid berdampingan dipisah seam tengah
        const halfW = width / 2;
        [-1, 1].forEach((side) => {
          const lid = new THREE.Mesh(new THREE.BoxGeometry(halfW - 0.006, height, depth - 0.01), lidMat);
          lid.position.set((side * halfW) / 2, 0, 0);
          lid.castShadow = true;
          group.add(lid);
        });
        const centerSeam = new THREE.Mesh(new THREE.BoxGeometry(0.004, height + 0.001, depth - 0.01), seamMat);
        group.add(centerSeam);
      } else {
        // "cablecubby-single" — CC202/500/700/1202/1252MS: lid persegi tunggal, flush
        const lid = new THREE.Mesh(new THREE.BoxGeometry(width - 0.004, height, depth - 0.004), lidMat);
        lid.castShadow = true;
        group.add(lid);

        // garis seam di tepi (kesan lid yang bisa dibuka)
        const seamEdge = new THREE.Mesh(new THREE.BoxGeometry(width - 0.004, 0.0012, 0.002), seamMat);
        seamEdge.position.set(0, height / 2 + 0.0006, -depth / 2 + 0.01);
        group.add(seamEdge);
      }

      // Bezel/trim tipis di sekeliling — semua varian kecuali open-tray
      if (shape !== "cablecubby-open") {
        const bezel = new THREE.Mesh(new THREE.BoxGeometry(width + 0.006, Math.max(height * 0.3, 0.0025), depth + 0.006), frameMat);
        bezel.position.y = -height / 2 - Math.max(height * 0.3, 0.0025) / 2 + 0.0005;
        group.add(bezel);
      }
    }

    // ─────────────────────────────────────────────
    // FALLBACK for any other product type
    // ─────────────────────────────────────────────
    else {
      const mainMat = new THREE.MeshStandardMaterial({ color, roughness: 0.3, metalness: 0.6 });
      const accentMat = new THREE.MeshStandardMaterial({ color: accentColor, roughness: 0.2, metalness: 0.8, emissive: accentColor, emissiveIntensity: 0.15 });

      if (item.surface === "floor" || item.surface === "table") {
        const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), mainMat);
        body.castShadow = true; body.receiveShadow = true; group.add(body);
        const strip = new THREE.Mesh(new THREE.BoxGeometry(width * 0.8, 0.005, depth * 0.3), accentMat);
        strip.position.y = height / 2 + 0.003; group.add(strip);
        const ledM = new THREE.MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 1, roughness: 0 });
        const led = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 8), ledM);
        led.position.set(width * 0.3, height / 2 + 0.015, depth * 0.3); group.add(led);
        group.position.set(item.x, item.y + height / 2, item.z);
      } else if (item.surface && item.surface.startsWith("wall")) {
        const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), mainMat);
        body.castShadow = true; group.add(body);
        const wallOffset = depth / 2 + 0.02;
        if (item.surface === "wall-back") { group.position.set(item.x, item.y, wallOffset); group.rotation.y = 0; }
        else if (item.surface === "wall-left") { group.rotation.y = Math.PI / 2; group.position.set(wallOffset, item.y, item.z); }
        else if (item.surface === "wall-right") { group.rotation.y = -Math.PI / 2; group.position.set(this.room.width - wallOffset, item.y, item.z); }
      } 
      // else if (item.surface === "ceiling") {
      //   const ceilingSpeakers = [
      //     "extron-ff120t", "extron-ff220t",
      //     "extron-sf26ctlp", "extron-sf8ctsub", "extron-sf26ct",
      //     "extron-sf26x", "extron-sf3clp", "extron-sf3ctlp"
      //   ];

      //   if (pid === "extron-ff120t" || pid === "extron-ff220t") {
      //     // Flat tile speaker — center di plafon, flip agar grille ke bawah
      //     group.position.set(item.x, this.room.height - 0.001, item.z);
      //     group.rotation.x = Math.PI;

      //   } else if (ceilingSpeakers.includes(pid)) {
      //     // Round speaker — grille flush di permukaan plafon bawah, back can ke atas
      //     group.position.set(item.x, this.room.height, item.z);
      //     group.rotation.x = Math.PI;

      //   } else {
      //     // Device lain (bukan speaker) yang dipasang di ceiling
      //     const ph = item.product.height || 0.2;
      //     group.position.set(item.x, this.room.height - ph / 2 - 0.01, item.z);
      //     group.rotation.set(0, 0, 0);
      //   }
      // }
    }

    // ─────────────────────────────────────────────
    // POSITIONING — wall-mounted products
    // ─────────────────────────────────────────────
    // ─────────────────────────────────────────────
    // POSITIONING — menentukan posisi & rotasi group
    // berdasarkan surface tempat device diletakkan
    // ─────────────────────────────────────────────
   if (item.surface === "floor") {
      // Device di lantai — posisi Y = setengah tinggi device
      const ph = item.product.height || 0.2;
      group.position.set(item.x, ph / 2, item.z);
      group.rotation.set(0, 0, 0);

    } else if (item.surface === "table") {
      // Device di atas meja — Y = tinggi meja + setengah tinggi device
      const ph = item.product.height || 0.2;
      group.position.set(item.x, item.y + ph / 2, item.z);
      group.rotation.set(0, 0, 0);

    } else if (item.surface === "wall-back") {
      const pd = item.product.depth || 0.2;
      group.position.set(item.x, item.y, pd / 2 + 0.02);
      group.rotation.set(0, 0, 0);

    } else if (item.surface === "wall-left") {
      const pd = item.product.depth || 0.2;
      group.rotation.set(0, Math.PI / 2, 0);
      group.position.set(pd / 2 + 0.02, item.y, item.z);

    } else if (item.surface === "wall-right") {
      const pd = item.product.depth || 0.2;
      group.rotation.set(0, -Math.PI / 2, 0);
      group.position.set(this.room.width - pd / 2 - 0.02, item.y, item.z);

    } 
    else if (item.surface === "ceiling") {
      // const ph = item.product.height || 0.1;
      group.position.set(item.x, this.room.height - 0.01, item.z);
      group.rotation.set(0, 0, 0);
    }

    // ─────────────────────────────────────────────
    // HIGHLIGHT BOX (selection wireframe)
    // ─────────────────────────────────────────────
    const hlMat = new THREE.MeshBasicMaterial({ color: "#3b82f6", wireframe: true, transparent: true, opacity: 0 });
    const hl = new THREE.Mesh(new THREE.BoxGeometry(
      (width || 0.3) + 0.04, (height || 0.2) + 0.04, (depth || 0.2) + 0.04), hlMat);
    group.add(hl);
    group.userData = Object.assign(group.userData || {}, { itemId: item.id, highlightMesh: hl, highlightMat: hlMat });
    this.scene.add(group);
    this.objects3D.set(item.id, group);
    if (this.showCoverage) this._addCoverageZone(item);

    if (hasModel) {
      this._loadDynamicModel(item, group);
    }

    return group;
  }

  // Load model .glb secara async untuk produk yang punya modelUrl, lalu
  // ganti loading spinner dengan model asli sebagai child dari group yang
  // sama. Kalau group sudah dihapus dari scene sebelum loading selesai
  // (misal user buru-buru hapus device), hasil load diabaikan supaya
  // tidak nyangkut menambahkan child ke group yang sudah "mati".
  async _loadDynamicModel(item, group) {
    const { width, height, depth } = item.product;
    const modelScale = item.product.modelScale || 1;
    const modelRotationY = item.product.modelRotationY || 0;
    try {
      const model = await loadScaledModel(item.product.modelUrl, width, height, depth, modelScale, modelRotationY);

      // Device sudah dihapus / diganti sebelum loading selesai — batalkan.
      if (this.objects3D.get(item.id) !== group) return;

      if (group.userData.spinnerGroup) {
        group.remove(group.userData.spinnerGroup);
        this._spinnerTicks.delete(item.id);
      }
      group.add(model);
      group.userData.modelMesh = model;
    } catch (err) {
      console.error(`Gagal load model 3D untuk produk "${item.product.id}":`, err);
      // Spinner dibiarkan tetap tampil sebagai indikasi visual bahwa model
      // gagal dimuat, supaya user tahu ada masalah (bukan diam-diam kosong).
    }
  }

  _addCoverageZone(item) {
    const existing = this.coverageZones.get(item.id);
    if (existing) this.scene.remove(existing);
    const zone = buildCoverageZone(item, this.room.width, this.room.depth);
    this.scene.add(zone); this.coverageZones.set(item.id, zone);
  }

  removeDevice(id) {
    const mesh = this.objects3D.get(id);
    if (mesh) { this.scene.remove(mesh); this.objects3D.delete(id); }
    const zone = this.coverageZones.get(id);
    if (zone) { this.scene.remove(zone); this.coverageZones.delete(id); }
    this._spinnerTicks.delete(id);
  }

  setSelected(id) {
    this.selectedId = id;
    this.objects3D.forEach((group, gid) => { const mat = group.userData.highlightMat; if (mat) mat.opacity = gid === id ? 0.6 : 0; });
  }

  rotateDevice(id, pan, tilt, roll) {
    const group = this.objects3D.get(id);
    if (!group) return;

    if (!group.userData.baseRotation) {
      group.userData.baseRotation = {
        x: group.rotation.x,
        y: group.rotation.y,
        z: group.rotation.z,
      };
    }

    const base = group.userData.baseRotation;
    group.rotation.order = "YXZ";
    // Hanya pan (Y-axis) yang aktif, tilt & roll diabaikan
    group.rotation.y = base.y + (pan * Math.PI) / 180;
    group.rotation.x = base.x; // tetap
    group.rotation.z = base.z; // tetap

    // Update coverage zone dengan pan terbaru
    if (this.showCoverage) {
      const item = this._placedItemsRef.find(i => i.id === id);
      if (item) {
        this._addCoverageZone({
          ...item,
          rotation: { pan, tilt: 0, roll: 0 },
        });
      }
    }
  }

  toggleCoverage(show, items) {
    this.showCoverage = show;
    if (show) { items.forEach(item => this._addCoverageZone(item)); }
    else { this.coverageZones.forEach(zone => this.scene.remove(zone)); this.coverageZones.clear(); }
  }

  startGhost(product) {
    this.pendingProduct = product;
    if (this.ghostMesh) this.scene.remove(this.ghostMesh);
    const { width, height, depth, color } = product;
    this.ghostMesh = new THREE.Mesh(new THREE.BoxGeometry(width || 0.3, height || 0.2, depth || 0.2), new THREE.MeshStandardMaterial({ color, transparent: true, opacity: 0.45, roughness: 0.4 }));
    this.scene.add(this.ghostMesh);
  }

  updateGhost(mouseNDC) {
    if (!this.ghostMesh || !this.pendingProduct) return null;
    this.raycaster.setFromCamera(mouseNDC, this.camera);
    const hits = this.raycaster.intersectObjects([...this.surfaces, ...this.furnitureSurfaces], false);
    if (hits.length === 0) { this.ghostMesh.visible = false; return null; }
    const hit = hits[0]; const surf = hit.object.userData.surface;
    const p = this.pendingProduct; const ph = p.height || 0.2; const pd = p.depth || 0.2;
    if (surf === "floor") { this.ghostMesh.position.set(hit.point.x, ph / 2, hit.point.z); this.ghostMesh.rotation.set(0, 0, 0); }
    else if (surf === "table") { const tt = hit.object.userData.tableTop || 0.775; this.ghostMesh.position.set(hit.point.x, tt + ph / 2, hit.point.z); this.ghostMesh.rotation.set(0, 0, 0); }
    else if (surf === "wall-back") { this.ghostMesh.position.set(hit.point.x, hit.point.y, pd / 2 + 0.01); this.ghostMesh.rotation.set(0, 0, 0); }
    else if (surf === "wall-left") { this.ghostMesh.rotation.set(0, Math.PI / 2, 0); this.ghostMesh.position.set(pd / 2 + 0.01, hit.point.y, hit.point.z); }
    else if (surf === "wall-right") { this.ghostMesh.rotation.set(0, -Math.PI / 2, 0); this.ghostMesh.position.set(this.room.width - pd / 2 - 0.01, hit.point.y, hit.point.z); }
    else if (surf === "ceiling" || surf === "ceiling-fixture") { this.ghostMesh.position.set(hit.point.x, this.room.height - ph / 2 - 0.01, hit.point.z); this.ghostMesh.rotation.set(0, 0, 0); }
    this.ghostMesh.visible = true;
    return { surface: surf, hit };
  }

  endGhost() { if (this.ghostMesh) { this.scene.remove(this.ghostMesh); this.ghostMesh = null; } this.pendingProduct = null; }

  placeDeviceAt(mouseNDC) {
    if (!this.pendingProduct) return null;
    this.raycaster.setFromCamera(mouseNDC, this.camera);
    const hits = this.raycaster.intersectObjects([...this.surfaces, ...this.furnitureSurfaces], false);
    if (hits.length === 0) return null;
    const hit = hits[0]; const surf = hit.object.userData.surface;
    const p = this.pendingProduct; const ph = p.height || 0.2;
    let x, y, z, surface;
    if (surf === "floor") { x = hit.point.x; y = 0; z = hit.point.z; surface = "floor"; }
    else if (surf === "table") { const tt = hit.object.userData.tableTop || 0.775; x = hit.point.x; y = tt; z = hit.point.z; surface = "table"; }
    else if (surf === "wall-back") { x = hit.point.x; y = hit.point.y; z = 0; surface = "wall-back"; }
    else if (surf === "wall-left") { x = 0; y = hit.point.y; z = hit.point.z; surface = "wall-left"; }
    else if (surf === "wall-right") { x = this.room.width; y = hit.point.y; z = hit.point.z; surface = "wall-right"; }
    else if (surf === "ceiling" || surf === "ceiling-fixture") { x = hit.point.x; y = this.room.height - ph; z = hit.point.z; surface = "ceiling"; }
    else return null;
    return { x, y, z, surface, product: p };
  }

  moveDeviceAt(itemId, mouseNDC, currentSurface) {
    this.raycaster.setFromCamera(mouseNDC, this.camera);
    const hits = this.raycaster.intersectObjects([...this.surfaces, ...this.furnitureSurfaces], false);
    if (hits.length === 0) return null;
    const hit = hits[0]; const surf = hit.object.userData.surface;
    if (currentSurface.startsWith("wall") && surf !== currentSurface && surf !== "floor" && surf !== "table") return null;
    const group = this.objects3D.get(itemId); if (!group) return null;
    const existingItem = this._placedItemsRef.find(i => i.id === itemId);
    const ph = existingItem?.product?.height || 0.2;
    let nx, ny, nz;
    if (surf === "floor") { nx = hit.point.x; ny = 0; nz = hit.point.z; group.position.set(nx, ny + ph / 2, nz); }
    else if (surf === "table") { const tt = hit.object.userData.tableTop || 0.775; nx = hit.point.x; ny = tt; nz = hit.point.z; group.position.set(nx, ny + ph / 2, nz); }
    else if (surf === "wall-back") { nx = hit.point.x; ny = hit.point.y; nz = 0; group.position.set(nx, ny, (existingItem?.product?.depth || 0.2) / 2 + 0.02); group.rotation.set(0, 0, 0); }
    else if (surf === "wall-left") { nx = 0; ny = hit.point.y; nz = hit.point.z; group.position.set((existingItem?.product?.depth || 0.2) / 2 + 0.02, ny, nz); group.rotation.set(0, Math.PI / 2, 0); }
    else if (surf === "wall-right") { nx = this.room.width; ny = hit.point.y; nz = hit.point.z; group.position.set(this.room.width - (existingItem?.product?.depth || 0.2) / 2 - 0.02, ny, nz); group.rotation.set(0, -Math.PI / 2, 0); }
    else return null;
    return { x: nx, y: ny, z: nz, surface: surf };
  }

  tryStartFurnitureDrag(mouseNDC) {
    this.raycaster.setFromCamera(mouseNDC, this.camera);
    const furnitureMeshes = [];
    this.furnitureGroups.forEach(group => group.traverse(child => { if (child.isMesh) furnitureMeshes.push(child); }));
    const hits = this.raycaster.intersectObjects(furnitureMeshes, false);
    if (hits.length === 0) return null;
    let furnitureId = null; let cur = hits[0].object;
    while (cur) { if (cur.userData?.furnitureId) { furnitureId = cur.userData.furnitureId; break; } cur = cur.parent; }
    if (!furnitureId) return null;
    this.isDraggingFurniture = true; this.draggingFurnitureId = furnitureId;
    this.furnitureDragOffset.set(hits[0].point.x - this.furnitureGroups.get(furnitureId).position.x, 0, hits[0].point.z - this.furnitureGroups.get(furnitureId).position.z);
    return furnitureId;
  }

  moveFurnitureDrag(mouseNDC) {
    if (!this.isDraggingFurniture || !this.draggingFurnitureId) return;
    this.raycaster.setFromCamera(mouseNDC, this.camera);
    const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const target = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(floorPlane, target);
    if (!target) return;
    const group = this.furnitureGroups.get(this.draggingFurnitureId); if (!group) return;
    group.position.x = Math.max(0.5, Math.min(this.room.width - 0.5, target.x - this.furnitureDragOffset.x));
    group.position.z = Math.max(0.5, Math.min(this.room.depth - 0.5, target.z - this.furnitureDragOffset.z));
  }

  endFurnitureDrag() { this.isDraggingFurniture = false; this.draggingFurnitureId = null; }

  clickSelect(mouseNDC) {
    this.raycaster.setFromCamera(mouseNDC, this.camera);
    const meshes = Array.from(this.objects3D.values()).flatMap(g => g.children);
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (hits.length === 0) { this.setSelected(null); return null; }
    let obj = hits[0].object;
    while (obj && !obj.userData.itemId) obj = obj.parent;
    if (!obj) return null;
    this.setSelected(obj.userData.itemId);
    return obj.userData.itemId;
  }

  updateRoom(room) {
    this.room = room; this.buildRoom(); this.buildLights();
    this.controls.target.set(room.width / 2, room.height * 0.4, room.depth / 2);
    this.camera.position.set(room.width * 0.8, room.height * 1.4, room.depth * 1.2);
    this.camera.updateProjectionMatrix(); this.controls.update();
  }

  // ── FIX: updateFurnitureSize meneruskan chairSides dari React ──
  updateFurnitureSize(id, tableWidth, tableDepth, tableHeight, chairSides) {
    const group = this.furnitureGroups.get(id); if (!group) return;
    const pos = group.position.clone();
    // Gunakan chairSides dari parameter, fallback ke userData
    const sides = chairSides || group.userData.chairSides || { front: true, back: true, left: true, right: true };
    this.addFurniture({ id, type: "table", x: pos.x - tableWidth / 2, z: pos.z - tableDepth / 2, tableWidth, tableDepth, tableHeight, chairSides: sides });
  }

  // ── FIX UTAMA: updateChairSides yang benar-benar berfungsi ──
  updateChairSides(id, chairSides, tableWidth, tableDepth, tableHeight) {
    const group = this.furnitureGroups.get(id); if (!group) return;
    const pos = group.position.clone();
    // Ambil dimensi dari parameter, fallback ke userData group
    const tw = tableWidth ?? group.userData.tableWidth ?? 2.4;
    const td = tableDepth ?? group.userData.tableDepth ?? 1.2;
    const th = tableHeight ?? group.userData.tableHeight ?? 0.75;
    // Rebuild furniture dengan chairSides baru — ini yang menghilangkan/memunculkan kursi
    this.addFurniture({ id, type: "table", x: pos.x - tw / 2, z: pos.z - td / 2, tableWidth: tw, tableDepth: td, tableHeight: th, chairSides });
  }

  animate() {
    this.animId = requestAnimationFrame(() => this.animate());
    const now = performance.now();
    const delta = Math.min((now - this._lastFrameTime) / 1000, 0.1);
    this._lastFrameTime = now;
    this._spinnerTicks.forEach((tick) => tick(delta));
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }
  resize(w, h) { this.camera.aspect = w / h; this.camera.updateProjectionMatrix(); this.renderer.setSize(w, h); }

  exportPNG() { this.renderer.render(this.scene, this.camera); return this.renderer.domElement.toDataURL("image/png"); }

  exportPNGWithOverlay(svgElement) {
    return new Promise(resolve => {
      this.renderer.render(this.scene, this.camera);
      const canvasDataURL = this.renderer.domElement.toDataURL("image/png");
      const w = this.renderer.domElement.width, h = this.renderer.domElement.height;
      if (!svgElement) { resolve(canvasDataURL); return; }
      const svgClone = svgElement.cloneNode(true);
      svgClone.setAttribute("width", w); svgClone.setAttribute("height", h);
      const svgBlob = new Blob([new XMLSerializer().serializeToString(svgClone)], { type: "image/svg+xml;charset=utf-8" });
      const svgURL = URL.createObjectURL(svgBlob);
      const img3D = new Image();
      img3D.onload = () => {
        const svgImg = new Image();
        svgImg.onload = () => {
          const offscreen = document.createElement("canvas"); offscreen.width = w; offscreen.height = h;
          const ctx = offscreen.getContext("2d");
          ctx.drawImage(img3D, 0, 0, w, h); ctx.drawImage(svgImg, 0, 0, w, h);
          URL.revokeObjectURL(svgURL); resolve(offscreen.toDataURL("image/png"));
        };
        svgImg.onerror = () => { URL.revokeObjectURL(svgURL); resolve(canvasDataURL); };
        svgImg.src = svgURL;
      };
      img3D.src = canvasDataURL;
    });
  }

  dispose() { cancelAnimationFrame(this.animId); this.controls.dispose(); this.renderer.dispose(); }
}

/* ================= CHAIR SIDE TOGGLE COMPONENT ================= */
// Didefinisikan di LUAR App agar tidak di-recreate tiap render
const ChairSideToggle = React.memo(({ chairSides, tableWidth, tableDepth, onToggle }) => {
  const seatsPerSide = Math.max(1, Math.round(tableWidth / 0.7));
  const seatsPerEnd = Math.max(1, Math.round(tableDepth / 0.7));
  const totalSeats =
    (chairSides.front ? seatsPerSide : 0) +
    (chairSides.back ? seatsPerSide : 0) +
    (chairSides.left ? seatsPerEnd : 0) +
    (chairSides.right ? seatsPerEnd : 0);

  // Layout visual mirip denah meja: Back di atas, Front di bawah, Left/Right di samping
  const layout = [
    { key: "back", label: "Back" },
    { key: "front", label: "Front" },
    { key: "left", label: "Left" },
    { key: "right", label: "Right" },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Chair Sides</span>
        <span style={{ fontSize: 10, color: "#94a3b8", background: "#f1f5f9", borderRadius: 99, padding: "2px 8px" }}>
          ~{totalSeats} seats
        </span>
      </div>

      {/* Grid 2×2 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
        {layout.map(({ key, label }) => {
          const active = chairSides[key];
          const count = (key === "left" || key === "right") ? seatsPerEnd : seatsPerSide;
          return (
            <button
              key={key}
              onClick={() => onToggle(key)}
              style={{
                padding: "7px 8px",
                borderRadius: 8,
                border: `1.5px solid ${active ? "#86efac" : "#fca5a5"}`,
                background: active ? "#f0fdf4" : "#fff1f2",
                color: active ? "#166534" : "#991b1b",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                transition: "all 0.15s ease",
                userSelect: "none",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={active ? "#166534" : "#991b1b"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 19v-8" /><path d="M18 19v-8" /><path d="M6 11H18" /><path d="M8 11V7a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" />
                </svg>
                <span>{label}</span>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <span style={{ fontSize: 9, opacity: 0.7 }}>{count}x</span>
                <span style={{
                  width: 16, height: 16, borderRadius: "50%",
                  background: active ? "#22c55e" : "#ef4444",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: "#fff", fontWeight: 700, flexShrink: 0,
                }}>
                  {active ? "✓" : "✕"}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

/* ================= MAIN APP ================= */
export default function RoomDesigner() {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);

  const [room, setRoom] = useState({ width: 8, depth: 6, height: 3 });
  const [placedItems, setPlacedItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draggingProduct, setDraggingProduct] = useState(null);
  const [isPlacing, setIsPlacing] = useState(false);
  const [isMovingItem, setIsMovingItem] = useState(false);
  const [isMovingFurniture, setIsMovingFurniture] = useState(false);
  const [projectName, setProjectName] = useState("Untitled Room");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [, setCameraVersion] = useState(0);
  const [openCategory, setOpenCategory] = useState({ Display: false, Conference: false, Microphone: false, Speaker: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeBrand, setActiveBrand] = useState("All");
  const [viewMode, setViewMode] = useState("list");
  const [surfaceHint, setSurfaceHint] = useState("");
  const [tableSettings, setTableSettings] = useState({ width: 2.4, depth: 1.2, height: 0.75 });

  // ── Satu-satunya sumber kebenaran untuk chair sides ──
  const [chairSides, setChairSides] = useState({ front: true, back: true, left: true, right: true });
  const [importNotice, setImportNotice] = useState(null); // { type: "success"|"error", message: string }

  // ── Katalog produk: diambil dari Supabase saat komponen mount.
  // Kalau fetch gagal, otomatis fallback ke FALLBACK_PRODUCTS (data statis
  // lama) supaya aplikasi tetap bisa dipakai walau database tidak bisa
  // dihubungi (offline, error konfigurasi, dsb). ──
  const [PRODUCTS, setPRODUCTS] = useState(FALLBACK_PRODUCTS);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsSource, setProductsSource] = useState("fallback"); // "database" | "fallback"

  // ── Kategori & Brand untuk filter chip sidebar -- diambil dari tabel
  // `categories`/`brands` (bukan array tetap), supaya begitu admin
  // tambah/ubah/hapus lewat halaman "Kategori, Brand & Tipe", sidebar
  // publik ini langsung ikut berubah tanpa perlu deploy ulang. Kalau
  // fetch gagal/kosong, tetap pakai daftar cadangan di bawah supaya
  // sidebar tidak pernah kosong. ──
  const FALLBACK_CATEGORY_LABELS = ["Display", "Conference", "Microphone", "Speaker"];
  const FALLBACK_BRAND_LABELS = ["AVer", "Cisco", "Bose", "Extron", "Hisense", "Crestron", "Samsung"];
  const [categoryList, setCategoryList] = useState(FALLBACK_CATEGORY_LABELS);
  const [brandList, setBrandList] = useState(FALLBACK_BRAND_LABELS);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const [{ categories: catRows }, { brands: brandRows }] = await Promise.all([
        fetchCategories(),
        fetchBrands(),
      ]);
      if (!isMounted) return;
      if (catRows.length > 0) setCategoryList(catRows.map((c) => c.label));
      if (brandRows.length > 0) setBrandList(brandRows.map((b) => b.label));
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const { products, error } = await fetchProducts();
      if (!isMounted) return;
      if (error || products.length === 0) {
        // Gagal fetch ATAU tabel kosong — tetap pakai FALLBACK_PRODUCTS,
        // jangan biarkan sidebar katalog kosong total.
        setPRODUCTS(FALLBACK_PRODUCTS);
        setProductsSource("fallback");
        if (error) console.warn("Gagal memuat produk dari Supabase, memakai data cadangan:", error.message);
      } else {
        setPRODUCTS(products);
        setProductsSource("database");
      }
      setProductsLoading(false);
    })();
    return () => { isMounted = false; };
  }, []);

  // ── Tema UI (background, warna aksen, nama app, footer text) — diambil
  // dari tabel app_settings, dengan fallback ke DEFAULT_THEME kalau fetch
  // gagal. Diterapkan via CSS custom property di root container, supaya
  // elemen yang sudah memakai var(--app-bg) dst otomatis ikut berubah
  // tanpa perlu re-render style inline satu-satu. ──
  const [theme, setTheme] = useState(DEFAULT_THEME);

  // ── Warna kategori produk (dari tabel `categories`, field coverage_color)
  // — disimpan sebagai React state (bukan cuma object mutable module-level)
  // supaya SEMUA elemen UI yang memakainya (footer, sidebar katalog, list
  // "Placed Devices", DAN coverage zone 3D) otomatis re-render begitu warna
  // diganti dari halaman admin, alih-alih hanya coverage zone 3D saja. ──
  const [categoryColorsByType, setCategoryColorsByType] = useState(() => {
    const initial = {};
    for (const [categoryId, typeKey] of Object.entries(CATEGORY_ID_TO_TYPE_KEY)) {
      initial[typeKey] = DEFAULT_CATEGORY_COLORS[categoryId];
    }
    return initial;
  });

  // Ambil warna kategori sesuai `type` produk (display/camera/microphone/
  // speaker), fallback ke accentColor produk itu sendiri kalau tidak ada
  // mapping kategori, lalu fallback terakhir ke abu-abu netral.
  const getCategoryColor = useCallback((product) => {
    if (!product) return "#94a3b8";
    return categoryColorsByType[product.type] || product.accentColor || "#94a3b8";
  }, [categoryColorsByType]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const [{ theme: fetchedTheme }, { categoryColors }] = await Promise.all([
        fetchTheme(),
        fetchCategoryColors(),
      ]);
      if (!isMounted) return;
      setTheme(fetchedTheme);
      applyCategoryColorsToCoverageMap(categoryColors);
      const byType = {};
      for (const [categoryId, typeKey] of Object.entries(CATEGORY_ID_TO_TYPE_KEY)) {
        if (categoryColors[categoryId]) byType[typeKey] = categoryColors[categoryId];
      }
      setCategoryColorsByType(prev => ({ ...prev, ...byType }));

      // Terapkan model 3D custom meja/kursi (kalau admin sudah upload
      // .glb lewat halaman Pengaturan Tampilan) — kosong berarti tetap
      // pakai geometri bawaan (box meja + kursi kantor prosedural).
      sceneRef.current?.setFurnitureModels({
        tableUrl: fetchedTheme.table_model_url || null,
        tableScale: Number(fetchedTheme.table_model_scale) || 1,
        tableRotationY: Number(fetchedTheme.table_model_rotation_y) || 0,
        chairUrl: fetchedTheme.chair_model_url || null,
        chairScale: Number(fetchedTheme.chair_model_scale) || 1,
        chairRotationY: Number(fetchedTheme.chair_model_rotation_y) || 0,
      });
    })();
    return () => { isMounted = false; };
  }, []);

  const nextId = useRef(100);

  useEffect(() => { if (sceneRef.current) sceneRef.current._placedItemsRef = placedItems; }, [placedItems]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const scene = new RoomScene(canvasRef.current, room);
    sceneRef.current = scene;
    const ro = new ResizeObserver(() => {
      if (containerRef.current) {
        const { clientWidth: w, clientHeight: h } = containerRef.current;
        scene.resize(w, h); setCanvasSize({ width: w, height: h });
      }
    });
    ro.observe(containerRef.current);
    if (containerRef.current) setCanvasSize({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
    let rafId;
    const tick = () => { setCameraVersion(v => v + 1); rafId = requestAnimationFrame(tick); };
    rafId = requestAnimationFrame(tick);
    return () => { ro.disconnect(); scene.dispose(); cancelAnimationFrame(rafId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (sceneRef.current) sceneRef.current.setSelected(selectedId); }, [selectedId]);
  useEffect(() => { if (sceneRef.current) sceneRef.current.toggleCoverage(showCoverage, placedItems); }, [showCoverage, placedItems]);

  const getNDC = useCallback(e => {
    const rect = canvasRef.current.getBoundingClientRect();
    return new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
  }, []);

  const handleMouseMove = useCallback(e => {
    if (!sceneRef.current) return;
    const ndc = getNDC(e);
    if (isMovingFurniture) { sceneRef.current.moveFurnitureDrag(ndc); return; }
    if (isMovingItem && selectedId) {
      const updated = sceneRef.current.moveDeviceAt(selectedId, ndc, placedItems.find(i => i.id === selectedId)?.surface || "floor");
      if (updated) setPlacedItems(prev => prev.map(i => i.id === selectedId ? { ...i, ...updated } : i));
      return;
    }
    if (!isPlacing) return;
    const result = sceneRef.current.updateGhost(ndc);
    if (result) {
      const surf = result.surface;
      if (surf === "floor") setSurfaceHint("Floor");
      else if (surf === "table") setSurfaceHint("Table Top");
      else if (surf.startsWith("wall")) setSurfaceHint("Wall");
      else if (surf === "ceiling" || surf === "ceiling-fixture") setSurfaceHint("Ceiling");
      else setSurfaceHint("");
    }
  }, [isPlacing, isMovingItem, isMovingFurniture, selectedId, getNDC, placedItems]);

  const handleMouseDown = useCallback(e => {
    if (isPlacing || !sceneRef.current) return;
    const ndc = getNDC(e);
    const deviceId = sceneRef.current.clickSelect(ndc);
    if (deviceId) {
      setSelectedId(deviceId); setIsMovingItem(true);
      if (sceneRef.current.controls) sceneRef.current.controls.enabled = false;
      return;
    }
    const furnitureId = sceneRef.current.tryStartFurnitureDrag(ndc);
    if (furnitureId) {
      setIsMovingFurniture(true); setSelectedId(null);
      if (sceneRef.current.controls) sceneRef.current.controls.enabled = false;
    }
  }, [isPlacing, getNDC]);

  const handleMouseUp = useCallback(() => {
    if (isMovingItem) { setIsMovingItem(false); if (sceneRef.current?.controls) sceneRef.current.controls.enabled = true; }
    if (isMovingFurniture) { sceneRef.current?.endFurnitureDrag(); setIsMovingFurniture(false); if (sceneRef.current?.controls) sceneRef.current.controls.enabled = true; }
  }, [isMovingItem, isMovingFurniture]);

  const handleCanvasClick = useCallback(e => {
    if (!sceneRef.current || isMovingItem || isMovingFurniture) return;
    const ndc = getNDC(e);
    if (isPlacing && draggingProduct) {
      const placed = sceneRef.current.placeDeviceAt(ndc);
      if (placed) {
        const id = nextId.current++;
        const item = { id, ...placed, rotation: { pan: 0, tilt: 0, roll: 0 } };
        sceneRef.current.addDevice(item);
        setPlacedItems(prev => [...prev, item]);
        sceneRef.current.endGhost(); setIsPlacing(false); setDraggingProduct(null); setSurfaceHint(""); setSelectedId(id);
      }
      return;
    }
    const id = sceneRef.current.clickSelect(ndc);
    setSelectedId(id || null);
  }, [isPlacing, isMovingItem, isMovingFurniture, draggingProduct, getNDC]);

  useEffect(() => {
    const handler = e => {
      if (e.key === "Escape") {
        if (isPlacing) { sceneRef.current?.endGhost(); setIsPlacing(false); setDraggingProduct(null); setSurfaceHint(""); }
        if (isMovingItem) { setIsMovingItem(false); if (sceneRef.current?.controls) sceneRef.current.controls.enabled = true; }
        if (isMovingFurniture) { sceneRef.current?.endFurnitureDrag(); setIsMovingFurniture(false); if (sceneRef.current?.controls) sceneRef.current.controls.enabled = true; }
      }
      if (e.key === "Delete" && selectedId && !isMovingItem) handleDeleteItem(selectedId);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlacing, isMovingItem, isMovingFurniture, selectedId]);

  const handleProductClick = product => {
    if (sceneRef.current) sceneRef.current.startGhost(product);
    setDraggingProduct(product); setIsPlacing(true);
    setSurfaceHint("Click on floor, wall, table or ceiling to place");
  };

  const handleDeleteItem = id => {
    sceneRef.current?.removeDevice(id);
    setPlacedItems(prev => prev.filter(i => i.id !== id));
    setSelectedId(null);
  };

  // ── FIX: handleTableChange meneruskan chairSides terkini ──
  const handleTableChange = (key, value) => {
    const next = { ...tableSettings, [key]: value };
    setTableSettings(next);
    sceneRef.current?.updateFurnitureSize("default-table", next.width, next.depth, next.height, chairSides);
  };

  // ── FIX UTAMA: gunakan functional update agar nilai chairSides selalu fresh ──
  const handleChairSideToggle = useCallback(side => {
    setChairSides(prev => {
      const next = { ...prev, [side]: !prev[side] };
      // Panggil scene dengan nilai terbaru sekarang juga
      sceneRef.current?.updateChairSides(
        "default-table",
        next,
        tableSettings.width,
        tableSettings.depth,
        tableSettings.height
      );
      return next;
    });
    // tableSettings adalah deendency — sengaja di-memoize agar tidak stale
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSettings]);

  const selectedItem = placedItems.find(i => i.id === selectedId);

  const getSurfaceLabel = surf => {
    if (!surf) return "—";
    if (surf === "floor") return "Floor";
    if (surf === "table") return "Table Top";
    if (surf === "ceiling") return "Ceiling";
    if (surf.startsWith("wall")) return "Wall";
    return surf;
  };

  const getTypeIcon = type => {
    if (type === "camera") return <Icon.Camera size={12} color="currentColor" />;
    if (type === "microphone") return <Icon.Mic size={12} color="currentColor" />;
    if (type === "speaker") return <Icon.Speaker size={12} color="currentColor" />;
    if (type === "display") return <Icon.Display size={12} color="currentColor" />;
    return null;
  };

  const handleExportPNG = async () => {
    if (!sceneRef.current) return;
    const svgEl = (showMeasurements || showCoverage) ? overlayRef.current : null;
    const dataURL = await sceneRef.current.exportPNGWithOverlay(svgEl);
    const link = document.createElement("a");
    link.download = `${projectName.replace(/\s+/g, "-")}.png`; link.href = dataURL; link.click();
  };

  const handleExportPDF = async () => {
    if (!sceneRef.current) return;

    // ── Simpan state toggle saat ini, supaya bisa dikembalikan setelah
    // capture selesai (PDF lengkap menyertakan ketiga variasi gambar
    // terlepas dari apa yang sedang aktif di UI saat user klik Export). ──
    const prevMeasurements = showMeasurements;
    const prevCoverage = showCoverage;

    const setOverlay = async (measurements, coverage) => {
      setShowMeasurements(measurements);
      setShowCoverage(coverage);
      // Tunggu re-render React + toggleCoverage (3D) + SVG overlay selesai.
      await new Promise((r) => setTimeout(r, 120));
    };

    // 1) Gambar DASAR — tanpa overlay apa pun
    await setOverlay(false, false);
    const baseImageDataURL = await sceneRef.current.exportPNGWithOverlay(null);

    // 2) Gambar khusus MEASUREMENTS — garis ukuran ruangan & meja saja,
    // tanpa area coverage, supaya tidak saling menutupi secara visual.
    await setOverlay(true, false);
    const measurementsImageDataURL = await sceneRef.current.exportPNGWithOverlay(overlayRef.current);

    // 3) Gambar khusus COVERAGE — area pancar/cakupan device saja, tanpa
    // garis ukuran, supaya zona coverage terlihat jelas tanpa gangguan.
    await setOverlay(false, true);
    const coverageImageDataURL = await sceneRef.current.exportPNGWithOverlay(overlayRef.current);

    // Kembalikan toggle ke kondisi semula sebelum export
    await setOverlay(prevMeasurements, prevCoverage);

    const placedDeviceCount = placedItems.filter(i => i.product).length;

    const printWindow = window.open("", "_blank"); if (!printWindow) return;
    const devices = placedItems.filter(i => i.product);
    const tableGroup = sceneRef.current.furnitureGroups?.get("default-table");
    const tablePos = tableGroup ? { cx: tableGroup.position.x, cz: tableGroup.position.z, tw: tableSettings.width, td: tableSettings.depth } : null;
    const leftEdge = tablePos ? (tablePos.cx - tablePos.tw / 2).toFixed(2) : "—";
    const rightEdge = tablePos ? (room.width - (tablePos.cx + tablePos.tw / 2)).toFixed(2) : "—";
    const backEdge = tablePos ? (tablePos.cz - tablePos.td / 2).toFixed(2) : "—";
    const frontEdge = tablePos ? (room.depth - (tablePos.cz + tablePos.td / 2)).toFixed(2) : "—";
    const cameras = devices.filter(i => i.product.type === "camera"), mics = devices.filter(i => i.product.type === "microphone");
    const speakers = devices.filter(i => i.product.type === "speaker"), displays = devices.filter(i => i.product.type === "display");
    const sps = Math.max(1, Math.round(tableSettings.width / 0.7));
    const spe = Math.max(1, Math.round(tableSettings.depth / 0.7));
    const totalSeats = (chairSides.front ? sps : 0) + (chairSides.back ? sps : 0) + (chairSides.left ? spe : 0) + (chairSides.right ? spe : 0);

    // Label tampilan untuk key spesifikasi teknis yang umum dipakai di _meta
    // produk (driver, power, dst) — supaya tidak menampilkan camelCase mentah.
    const META_LABELS = {
      driver: "Driver", power: "Power Handling", freqRange: "Frequency Response",
      freqResponse: "Frequency Response", freqLow: "Low Frequency Cutoff",
      dispersion: "Dispersion Angle", tech: "Technology", connector: "Connector",
      zoom: "Zoom", aiLensFOV: "AI Lens FOV", wideFOV: "Wide FOV", weight: "Weight",
      tracking: "Tracking Feature", pixelPitch: "Pixel Pitch", cutout: "Cutout Diameter",
      backcan: "Back-can Dimensions", dia: "Diameter", depth: "Mounting Depth",
      tier: "Product Tier", rated: "Rating", format: "Mount Format", type: "Type",
      mount: "Mounting Options", coverage: "Coverage Pattern", maxSPL: "Max SPL",
      colors: "Available Colors",
    };

    const formatMetaValue = (v) => {
      if (v === true) return "Yes";
      if (v === false) return "No";
      return String(v);
    };

    printWindow.document.write(`<!DOCTYPE html><html><head><title>${projectName}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:#fff;color:#111827}.header{padding:24px 32px 16px;border-bottom:2px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center}.title{font-size:22px;font-weight:700;color:#0f172a}.subtitle{font-size:13px;color:#64748b;margin-top:2px}.meta{text-align:right;font-size:12px;color:#94a3b8;line-height:1.8}.canvas-wrap{padding:20px 32px 8px;display:flex;justify-content:center}.canvas-wrap img{max-width:100%;border:1px solid #e2e8f0;border-radius:10px;box-shadow:0 4px 20px rgba(0,0,0,.08)}.canvas-label{text-align:center;font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;padding:6px 0 18px}.section{padding:0 32px 28px}.section-title{font-size:16px;font-weight:700;color:#0f172a;margin-bottom:12px;padding-bottom:6px;border-bottom:1.5px solid #e2e8f0}.stats-row{display:flex;gap:12px;margin-bottom:24px}.stat-card{flex:1;padding:14px 16px;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc}.stat-val{font-size:24px;font-weight:700;color:#0f172a}.stat-lbl{font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;margin-top:2px}table.data{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px}table.data thead th{background:#f1f5f9;padding:8px 10px;text-align:left;font-weight:700;color:#374151;font-size:11px;text-transform:uppercase;letter-spacing:.5px;border-bottom:1.5px solid #e2e8f0}table.data tbody td{padding:9px 10px;border-bottom:1px solid #f1f5f9;color:#374151;vertical-align:middle}table.data tbody tr:last-child td{border-bottom:none}table.data tbody tr:nth-child(even) td{background:#fafafa}.badge{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700}.badge-cam{background:#dbeafe;color:#1e40af}.badge-mic{background:#dcfce7;color:#166534}.badge-spk{background:#fef3c7;color:#92400e}.badge-disp{background:#f3e8ff;color:#6b21a8}.badge-surf{background:#f1f5f9;color:#475569}.badge-on{background:#dcfce7;color:#166534}.badge-off{background:#fee2e2;color:#991b1b}.meas-table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px}.meas-table th{background:#f1f5f9;padding:8px 10px;text-align:left;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#374151;border-bottom:1.5px solid #e2e8f0}.meas-table td{padding:8px 10px;border-bottom:1px solid #f1f5f9}.meas-table tr:last-child td{border-bottom:none}.meas-val{font-weight:700;font-family:'Courier New',monospace;color:#1d4ed8}.coverage-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:24px}.coverage-card{border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px}.coverage-card .name{font-size:13px;font-weight:700;color:#0f172a;margin-bottom:6px}.coverage-card .row{display:flex;justify-content:space-between;font-size:11px;color:#64748b;margin-bottom:3px}.coverage-card .row span:last-child{font-weight:600;color:#374151}.spec-card{border:1px solid #e2e8f0;border-radius:10px;padding:16px 18px;margin-bottom:14px;display:flex;gap:16px;page-break-inside:avoid}.spec-card img{width:72px;height:72px;object-fit:contain;background:#f8fafc;border-radius:8px;border:1px solid #f1f5f9;flex-shrink:0}.spec-card .body{flex:1;min-width:0}.spec-card .spec-name{font-size:14px;font-weight:700;color:#0f172a;margin-bottom:2px}.spec-card .spec-pos{font-size:11px;color:#94a3b8;font-family:'Courier New',monospace;margin-bottom:8px}.spec-table{width:100%;border-collapse:collapse;font-size:11px}.spec-table td{padding:4px 0;border-bottom:1px solid #f8fafc;vertical-align:top}.spec-table td:first-child{font-weight:600;color:#64748b;width:38%;padding-right:10px}.spec-table td:last-child{color:#374151}.footer{padding:14px 32px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:11px;color:#94a3b8}@media print{.section{page-break-inside:avoid}}</style></head><body>
    <div class="header"><div><div class="title">${projectName}</div><div class="subtitle">Room Layout &amp; Equipment Report &nbsp;·&nbsp; ${room.width}m × ${room.depth}m × ${room.height}m</div></div>
    <div class="meta"><div style="font-weight:700;color:#334155">${theme.app_name || DEFAULT_THEME.app_name} ${theme.app_subtitle || DEFAULT_THEME.app_subtitle}</div><div>${new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</div><div>Room area: ${(room.width * room.depth).toFixed(1)} m² &nbsp;|&nbsp; Vol: ${(room.width * room.depth * room.height).toFixed(1)} m³</div></div></div>

    <div class="canvas-wrap"><img src="${baseImageDataURL}" /></div>
    <div class="canvas-label">Room Layout — Base View</div>

    <div class="canvas-wrap"><img src="${measurementsImageDataURL}" /></div>
    <div class="canvas-label">Room Layout — with Measurements</div>

    ${placedDeviceCount > 0 ? `
    <div class="canvas-wrap"><img src="${coverageImageDataURL}" /></div>
    <div class="canvas-label">Room Layout — with Coverage Area</div>
    ` : ""}

    <div class="section"><div class="stats-row">
      <div class="stat-card"><div class="stat-val">${devices.length}</div><div class="stat-lbl">Total Devices</div></div>
      <div class="stat-card"><div class="stat-val" style="color:#1d4ed8">${cameras.length}</div><div class="stat-lbl">Cameras</div></div>
      <div class="stat-card"><div class="stat-val" style="color:#166534">${mics.length}</div><div class="stat-lbl">Microphones</div></div>
      <div class="stat-card"><div class="stat-val" style="color:#92400e">${speakers.length}</div><div class="stat-lbl">Speakers</div></div>
      <div class="stat-card"><div class="stat-val" style="color:#6b21a8">${displays.length}</div><div class="stat-lbl">Displays</div></div>
    </div></div>

    <div class="section"><div class="section-title">Device List</div><table class="data"><thead><tr><th>#</th><th>Device Name</th><th>Type</th><th>Surface</th><th>Position (X/Y/Z)</th><th>Size (W×H×D)</th></tr></thead><tbody>
    ${devices.map((item, i) => { const bc = item.product.type === "camera" ? "badge-cam" : item.product.type === "microphone" ? "badge-mic" : item.product.type === "speaker" ? "badge-spk" : item.product.type === "display" ? "badge-disp" : "badge-surf"; return `<tr><td style="font-weight:700;color:#6b7280">${i + 1}</td><td style="font-weight:700">${item.product.name}</td><td><span class="badge ${bc}">${item.product.type}</span></td><td><span class="badge badge-surf">${getSurfaceLabel(item.surface)}</span></td><td style="font-family:'Courier New',monospace;font-size:11px">${item.x?.toFixed(2)}m/${item.y?.toFixed(2)}m/${item.z?.toFixed(2)}m</td><td style="font-family:'Courier New',monospace;font-size:11px">${item.product.width}×${item.product.height}×${item.product.depth}m</td></tr>`; }).join("")}
    </tbody></table></div>

    <div class="section"><div class="section-title">Product Specifications</div>
    ${devices.length === 0 ? `<p style="color:#94a3b8;font-size:13px">No devices placed yet.</p>` : devices.map((item, i) => {
      const p = item.product;
      const metaEntries = Object.entries(p._meta || {}).filter(([k]) => k !== "type3D");
      return `<div class="spec-card">
        ${p.image ? `<img src="${p.image}" alt="" />` : ""}
        <div class="body">
          <div class="spec-name">#${i + 1} — ${p.name} <span style="font-size:11px;color:#94a3b8;font-weight:400">(${p.brand})</span></div>
          <div class="spec-pos">${getSurfaceLabel(item.surface)} · X:${item.x?.toFixed(2)}m Y:${item.y?.toFixed(2)}m Z:${item.z?.toFixed(2)}m · ${p.width}×${p.height}×${p.depth}m</div>
          <table class="spec-table">
            <tr><td>Coverage</td><td>${p.coverageAngle >= 300 ? "360° Omnidirectional" : `${p.coverageAngle}° Directional`} · ${p.coverageDistance}m range</td></tr>
            ${metaEntries.map(([k, v]) => `<tr><td>${META_LABELS[k] || k}</td><td>${formatMetaValue(v)}</td></tr>`).join("")}
          </table>
        </div>
      </div>`;
    }).join("")}
    </div>

    <div class="section"><div class="section-title">Room &amp; Table Measurements</div><table class="meas-table"><thead><tr><th>Measurement</th><th>Value</th><th>Notes</th></tr></thead><tbody>
    <tr><td>Room Width</td><td><span class="meas-val">${room.width} m</span></td><td>Left wall to right wall</td></tr>
    <tr><td>Room Length</td><td><span class="meas-val">${room.depth} m</span></td><td>Back wall to front wall</td></tr>
    <tr><td>Room Height</td><td><span class="meas-val">${room.height} m</span></td><td>Floor to ceiling</td></tr>
    <tr><td>Room Area</td><td><span class="meas-val">${(room.width * room.depth).toFixed(2)} m²</span></td><td>Usable floor area</td></tr>
    <tr><td>Room Volume</td><td><span class="meas-val">${(room.width * room.depth * room.height).toFixed(2)} m³</span></td><td>For acoustic calculations</td></tr>
    <tr><td colspan="3" style="background:#f8fafc;font-weight:700;color:#475569;font-size:11px;text-transform:uppercase;padding:8px 10px">Conference Table</td></tr>
    <tr><td>Table Width</td><td><span class="meas-val">${tableSettings.width} m</span></td><td></td></tr>
    <tr><td>Table Length</td><td><span class="meas-val">${tableSettings.depth} m</span></td><td></td></tr>
    <tr><td>Table Height</td><td><span class="meas-val">${tableSettings.height} m</span></td><td></td></tr>
    <tr><td>Table → Left Wall</td><td><span class="meas-val">${leftEdge} m</span></td><td>Edge-to-wall clearance</td></tr>
    <tr><td>Table → Right Wall</td><td><span class="meas-val">${rightEdge} m</span></td><td>Edge-to-wall clearance</td></tr>
    <tr><td>Table → Back Wall</td><td><span class="meas-val">${backEdge} m</span></td><td>Edge-to-wall clearance</td></tr>
    <tr><td>Table → Front Wall</td><td><span class="meas-val">${frontEdge} m</span></td><td>Edge-to-wall clearance</td></tr>
    <tr><td>Active Seats</td><td><span class="meas-val">${totalSeats}</span></td><td>Based on active chair sides</td></tr>
    <tr><td colspan="3" style="background:#f8fafc;font-weight:700;color:#475569;font-size:11px;text-transform:uppercase;padding:8px 10px">Chair Sides</td></tr>
    ${["back", "front", "left", "right"].map(side => { const count = (side === "left" || side === "right") ? spe : sps; return `<tr><td>Side: ${side.charAt(0).toUpperCase() + side.slice(1)}</td><td><span class="badge ${chairSides[side] ? "badge-on" : "badge-off"}">${chairSides[side] ? "Active" : "Disabled"}</span></td><td>${chairSides[side] ? `${count} chair(s)` : "No chairs"}</td></tr>`; }).join("")}
    </tbody></table></div>

    <div class="section"><div class="section-title">Coverage Analysis</div>
    ${devices.length === 0 ? `<p style="color:#94a3b8;font-size:13px">No devices placed yet.</p>` : `<div class="coverage-grid">${devices.map(item => { const p = item.product; const ct = p.coverageAngle >= 300 ? "Omnidirectional (360°)" : `Directional (${p.coverageAngle}°)`; const sa = p.coverageAngle >= 300 ? (Math.PI * p.coverageDistance * p.coverageDistance).toFixed(1) : ((p.coverageAngle / 360) * Math.PI * p.coverageDistance * p.coverageDistance).toFixed(1); const bc = p.type === "camera" ? "badge-cam" : p.type === "microphone" ? "badge-mic" : p.type === "speaker" ? "badge-spk" : p.type === "display" ? "badge-disp" : "badge-surf"; return `<div class="coverage-card"><div class="name"><span class="badge ${bc}" style="margin-right:6px">${p.type}</span>${p.name}</div><div class="row"><span>Coverage Type</span><span>${ct}</span></div><div class="row"><span>Angle</span><span>${p.coverageAngle}°</span></div><div class="row"><span>Max Range</span><span>${p.coverageDistance} m</span></div><div class="row"><span>Coverage Area</span><span>${sa} m²</span></div><div class="row"><span>Surface</span><span>${getSurfaceLabel(item.surface)}</span></div></div>`; }).join("")}</div>`}
    </div>

    <div class="footer"><span>Total Devices: ${devices.length} | Room: ${room.width}×${room.depth}×${room.height}m | Area: ${(room.width * room.depth).toFixed(1)} m²</span><span>${(theme.footer_text || DEFAULT_THEME.footer_text).replace("{year}", new Date().getFullYear())}</span></div>
    <script>window.onload=()=>window.print();</script></body></html>`);
    printWindow.document.close();
  };

  // ─────────────────────────────────────────────
  // PROJECT EXPORT — simpan seluruh state design sebagai file .json
  // Product di-reference via product.id saja (bukan object penuh,
  // karena mengandung imported image module yang tidak bisa diserialize)
  // ─────────────────────────────────────────────
  const handleExportProject = () => {
    const projectData = {
      _format: "mlv-roomdesigner-project",
      _version: 1,
      savedAt: new Date().toISOString(),
      projectName,
      room,
      tableSettings,
      chairSides,
      nextItemId: nextId.current,
      placedItems: placedItems.map(item => ({
        id: item.id,
        productId: item.product?.id,
        x: item.x,
        y: item.y,
        z: item.z,
        surface: item.surface,
        rotation: item.rotation || { pan: 0, tilt: 0, roll: 0 },
      })),
    };

    const blob = new Blob([JSON.stringify(projectData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = projectName.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-_]/g, "");
    link.download = `${safeName || "room-project"}.mlvroom.json`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────
  // PROJECT IMPORT — baca file .json, validasi, lalu rebuild scene
  // ─────────────────────────────────────────────
  const handleImportProjectFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      let data;
      try {
        data = JSON.parse(e.target.result);
      } catch {
        setImportNotice({ type: "error", message: "File tidak valid. Pastikan ini adalah file project (.json) yang benar." });
        return;
      }

      if (!data || data._format !== "mlv-roomdesigner-project") {
        setImportNotice({ type: "error", message: "File ini bukan project RoomDesigner 3D yang valid." });
        return;
      }

      // Rebuild product references dari PRODUCTS array berdasarkan productId
      const restoredItems = [];
      const skippedProducts = [];
      (data.placedItems || []).forEach(raw => {
        const product = PRODUCTS.find(p => p.id === raw.productId);
        if (!product) { skippedProducts.push(raw.productId); return; }
        restoredItems.push({
          id: raw.id,
          product,
          x: raw.x, y: raw.y, z: raw.z,
          surface: raw.surface,
          rotation: raw.rotation || { pan: 0, tilt: 0, roll: 0 },
        });
      });

      // Bersihkan scene saat ini: hapus semua device & furniture lama
      if (sceneRef.current) {
        placedItems.forEach(item => sceneRef.current.removeDevice(item.id));
      }
      setSelectedId(null);
      setIsPlacing(false);
      setDraggingProduct(null);
      sceneRef.current?.endGhost();

      // Terapkan room baru ke scene
      const newRoom = data.room || { width: 8, depth: 6, height: 3 };
      setRoom(newRoom);
      sceneRef.current?.updateRoom(newRoom);

      // Terapkan table settings + chair sides baru
      const newTableSettings = data.tableSettings || { width: 2.4, depth: 1.2, height: 0.75 };
      const newChairSides = data.chairSides || { front: true, back: true, left: true, right: true };
      setTableSettings(newTableSettings);
      setChairSides(newChairSides);
      sceneRef.current?.updateFurnitureSize(
        "default-table",
        newTableSettings.width, newTableSettings.depth, newTableSettings.height,
        newChairSides
      );

      // Rebuild semua device di scene 3D
      restoredItems.forEach(item => sceneRef.current?.addDevice(item));
      setPlacedItems(restoredItems);

      // Restore nama project & counter ID
      if (data.projectName) setProjectName(data.projectName);
      nextId.current = data.nextItemId || (Math.max(0, ...restoredItems.map(i => i.id)) + 1);

      if (skippedProducts.length > 0) {
        setImportNotice({
          type: "error",
          message: `Project dimuat, tapi ${skippedProducts.length} device dilewati karena produk tidak ditemukan di katalog saat ini.`
        });
      } else {
        setImportNotice({ type: "success", message: `Project "${data.projectName || "Untitled"}" berhasil dimuat (${restoredItems.length} device).` });
      }
      setTimeout(() => setImportNotice(null), 4500);
    };
    reader.onerror = () => {
      setImportNotice({ type: "error", message: "Gagal membaca file. Silakan coba lagi." });
    };
    reader.readAsText(file);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    handleImportProjectFile(file);
    e.target.value = ""; // reset agar bisa pilih file yang sama lagi
  };

  const categories = categoryList;
  const canvasCursor = isPlacing ? "crosshair" : isMovingItem || isMovingFurniture ? "grabbing" : "default";

  const getTablePos = () => {
    if (!sceneRef.current) return null;
    const tg = sceneRef.current.furnitureGroups?.get("default-table"); if (!tg) return null;
    return { cx: tg.position.x, cz: tg.position.z, tw: tableSettings.width, td: tableSettings.depth };
  };

  const allBrands = ["All", ...brandList];

  const filteredProducts = PRODUCTS.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q);
    const matchCat = activeCategory === "All" || p.category === activeCategory;
    const matchBrand = activeBrand === "All" || p.brand === activeBrand;
    return matchSearch && matchCat && matchBrand;
  });

  const groupedFiltered = categories.reduce((acc, cat) => {
    acc[cat] = filteredProducts.filter(p => p.category === cat);
    return acc;
  }, {});

  // const getTypeIconLarge = (type) => {
  //   const s = { fontSize: 18, color: "#64748b" };
  //   if (type === "camera") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>;
  //   if (type === "microphone") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" /></svg>;
  //   if (type === "speaker") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>;
  //   if (type === "display") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>;
  //   return null;
  // };

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", height: "100vh",
        background: "var(--app-bg)", fontFamily: "system-ui,sans-serif",
        "--app-bg": theme.theme_bg,
        "--app-bg-alt": theme.theme_preset === "dark" ? "#162032" : "#f8fafc",
        "--app-surface": theme.theme_surface,
        "--app-text": theme.theme_text || "#0f172a",
        "--app-text-muted": theme.theme_text_muted || "#94a3b8",
        "--app-border": theme.theme_border || "#e2e8f0",
        "--app-accent": theme.theme_accent,
        "--app-accent-text": theme.theme_accent_text,
        // Hover & selected state — dulunya hardcode warna biru MUDA
        // ("#eff6ff" dkk) yang bagus di light mode, tapi di dark mode
        // membuat teks (var(--app-text), putih) jadi tidak terbaca karena
        // nyaris sewarna dengan background hover-nya. Sekarang state ini
        // ikut menyesuaikan preset tema seperti warna lainnya.
        "--app-hover-bg": theme.theme_preset === "dark" ? "rgba(59,130,246,0.16)" : "#eff6ff",
        "--app-hover-border": theme.theme_preset === "dark" ? "rgba(59,130,246,0.45)" : "#93c5fd",
        "--app-selected-bg": theme.theme_preset === "dark" ? "rgba(59,130,246,0.22)" : "#eff6ff",
        "--app-selected-border": theme.theme_preset === "dark" ? "rgba(59,130,246,0.5)" : "#bfdbfe",
      }}
    >
      {/* NAVBAR */}
      <nav style={{ height: 60, background: "var(--app-surface)", borderBottom: "1px solid var(--app-border)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0, zIndex: 100, boxShadow: "0 2px 8px rgba(15,23,42,0.04)", position: "relative" }}>
        <ModeSwitcher />
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={LogoMLV} alt="MLV" style={{ height: 34, objectFit: "contain" }} />
          <div style={{ width: 1, height: 20, background: "var(--app-border)" }} />
          {isEditingName ? (
            <input autoFocus value={projectName} onChange={e => setProjectName(e.target.value)} onBlur={() => setIsEditingName(false)} onKeyDown={e => e.key === "Enter" && setIsEditingName(false)} style={{ background: "var(--app-bg)", border: "1px solid var(--app-border)", borderRadius: 6, padding: "4px 10px", color: "var(--app-text)", fontSize: 14, outline: "none" }} />
          ) : (
            <button onClick={() => setIsEditingName(true)} style={{ background: "none", border: "none", color: "var(--app-text)", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, padding: "4px 8px", borderRadius: 6 }}>
              {projectName} <Icon.Edit size={11} color="var(--app-text)" />
            </button>
          )}
          {!productsLoading && productsSource === "fallback" && (
            <div title="Tidak bisa terhubung ke database — menampilkan katalog cadangan (offline)" style={{
              display: "flex", alignItems: "center", gap: 5, padding: "3px 10px",
              background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 999,
              fontSize: 11, fontWeight: 600, color: "#b45309",
            }}>
              <Icon.Dot size={6} color="#f59e0b" /> Mode offline
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isPlacing && (<div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "6px 14px", color: "#2563eb", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><Icon.Dot size={8} color="#3b82f6" />{surfaceHint || "Click to place — ESC to cancel"}</div>)}
          {isMovingItem && (<div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 14px", color: "#b45309", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><Icon.Hand size={14} color="#b45309" />Drag to move device — release to drop</div>)}
          {isMovingFurniture && (<div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "6px 14px", color: "#7e22ce", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><Icon.Table size={14} color="#7e22ce" />Dragging table — release to drop</div>)}
          <button onClick={() => setShowMeasurements(v => !v)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: showMeasurements ? "#e0f2fe" : "#f1f5f9", color: showMeasurements ? "#0369a1" : "#64748b", border: showMeasurements ? "1px solid #7dd3fc" : "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <Icon.Ruler size={15} color={showMeasurements ? "#0369a1" : "#64748b"} /> Measurements
          </button>
          <button onClick={() => setShowCoverage(v => !v)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: showCoverage ? "#dcfce7" : "#f1f5f9", color: showCoverage ? "#15803d" : "#64748b", border: showCoverage ? "1px solid #86efac" : "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <Icon.Signal size={15} color={showCoverage ? "#15803d" : "#64748b"} /> Coverage
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={handleImportFileChange}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Import project (.json)"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "var(--app-bg)", color: "var(--app-text-muted)", border: "1px solid var(--app-border)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            Import
          </button>
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowExportMenu(!showExportMenu)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "var(--app-accent)", color: "var(--app-accent-text)", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Icon.Download size={14} color="#fff" /> Export
            </button>
            {showExportMenu && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 10, overflow: "hidden", minWidth: 240, boxShadow: "0 16px 40px rgba(15,23,42,0.15)", zIndex: 1000 }}>
                {(showMeasurements || showCoverage) && (<div style={{ padding: "8px 14px 6px", fontSize: 10, color: "var(--app-text-muted)", borderBottom: "1px solid var(--app-border)", display: "flex", alignItems: "center", gap: 5 }}><Icon.Dot size={6} color="#22c55e" />Export includes: {[showMeasurements ? "Measurements" : "", showCoverage ? "Coverage" : ""].filter(Boolean).join(" + ")}</div>)}
                {[
                  { label: "PNG Image", icon: <Icon.Image size={14} color="var(--app-text-muted)" />, action: () => { handleExportPNG(); setShowExportMenu(false); } },
                  { label: "PDF Document", icon: <Icon.PDF size={14} color="var(--app-text-muted)" />, action: () => { handleExportPDF(); setShowExportMenu(false); } },
                ].map(opt => (
                  <button key={opt.label} onClick={opt.action} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "transparent", border: "none", color: "var(--app-text)", cursor: "pointer", textAlign: "left", fontSize: 13 }} onMouseEnter={e => e.currentTarget.style.background = "var(--app-hover-bg)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {opt.icon} {opt.label}
                  </button>
                ))}
                <div style={{ borderTop: "1px solid var(--app-border)" }} />
                <button onClick={() => { handleExportProject(); setShowExportMenu(false); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "transparent", border: "none", color: "#2563eb", cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 600 }} onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><polyline points="9 15 12 18 15 15" /></svg>
                  Save Project (.json)
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* IMPORT NOTICE TOAST */}
      {importNotice && (
        <div style={{
          position: "fixed", top: 70, right: 20, zIndex: 2000,
          background: importNotice.type === "success" ? "rgba(34,197,94,0.95)" : "rgba(239,68,68,0.95)",
          backdropFilter: "blur(8px)", borderRadius: 10, padding: "12px 18px", color: "#fff",
          fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 10, maxWidth: 380,
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        }}>
          {importNotice.type === "success"
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
          }
          <span style={{ flex: 1 }}>{importNotice.message}</span>
          <button onClick={() => setImportNotice(null)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 0, opacity: 0.8, flexShrink: 0 }}>
            <Icon.X size={14} color="#fff" />
          </button>
        </div>
      )}

      {/* BODY */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* LEFT SIDEBAR */}
        <div style={{ width: 272, background: "var(--app-surface)", borderRight: "1px solid var(--app-border)", display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden" }}>

          {/* Fixed top: search + filters */}
          <div style={{ padding: "12px 10px 8px", borderBottom: "1px solid var(--app-border)", flexShrink: 0 }}>

            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--app-bg)", border: "1px solid var(--app-border)", borderRadius: 8, padding: "7px 10px", marginBottom: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--app-text-muted)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                style={{ background: "none", border: "none", outline: "none", color: "var(--app-text)", fontSize: 12, flex: 1, fontFamily: "system-ui,sans-serif" }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--app-text-muted)", padding: 0, display: "flex", alignItems: "center" }}>
                  <Icon.X size={12} color="var(--app-text-muted)" />
                </button>
              )}
            </div>

            {/* Category label + chips */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--app-text-muted)", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 5 }}>
                Category
              </div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {["All", ...categoryList].map(cat => (
                  <button key={cat} onClick={() => setActiveCategory(cat)} style={{ padding: "3px 9px", borderRadius: 99, fontSize: 10, fontWeight: 600, cursor: "pointer", border: `1px solid ${activeCategory === cat ? "#93c5fd" : "#e2e8f0"}`, background: activeCategory === cat ? "#dbeafe" : "#f8fafc", color: activeCategory === cat ? "#1d4ed8" : "#64748b", transition: "all 0.15s" }}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Brand label + chips — NO scrollbar, wraps like category */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--app-text-muted)", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 5 }}>
                Brand
              </div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {allBrands.map(brand => (
                  <button key={brand} onClick={() => setActiveBrand(brand)} style={{ padding: "3px 9px", borderRadius: 99, fontSize: 10, fontWeight: 600, cursor: "pointer", border: `1px solid ${activeBrand === brand ? "#cbd5e1" : "#e2e8f0"}`, background: activeBrand === brand ? "#e2e8f0" : "#f8fafc", color: activeBrand === brand ? "#334155" : "#64748b", transition: "all 0.15s" }}>
                    {brand}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Scrollable product list */}
          <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px 14px" }}>

            {/* Empty state */}
            {filteredProducts.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--app-text-muted)", fontSize: 12 }}>
                <div style={{ marginBottom: 6, opacity: 0.5 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ display: "inline-block" }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                </div>
                No products found for<br />
                <strong style={{ color: "var(--app-text-muted)" }}>"{searchQuery}"</strong>
              </div>
            )}

            {categories.map(category => {
              const prods = groupedFiltered[category];
              if (!prods || prods.length === 0) return null;
              const isOpen = openCategory[category];

              return (
                <div key={category} style={{ marginBottom: 10 }}>

                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: isOpen ? 6 : 0 }}>
                    <button
                      onClick={() => setOpenCategory(prev => ({ ...prev, [category]: !prev[category] }))}
                      style={{ flex: 1, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "4px 2px" }}
                    >
                      <span style={{ fontSize: 10, fontWeight: 700, color: "var(--app-text-muted)", textTransform: "uppercase", letterSpacing: 1.5 }}>{category}</span>
                      <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 99, background: "var(--app-bg)", color: "var(--app-text-muted)" }}>{prods.length}</span>
                      <span style={{ marginLeft: "auto", transform: isOpen ? "rotate(180deg)" : "none", transition: "0.2s", color: "var(--app-text-muted)" }}>
                        <Icon.ChevronDown size={10} color="var(--app-text-muted)" />
                      </span>
                    </button>

                    {/* Grid/List toggle — only show when open */}
                    {isOpen && (
                      <div style={{ display: "flex", gap: 2 }}>
                        {["list", "grid"].map(mode => (
                          <button key={mode} onClick={() => setViewMode(mode)} style={{ width: 22, height: 22, borderRadius: 4, border: `1px solid ${viewMode === mode ? "#cbd5e1" : "#e2e8f0"}`, background: viewMode === mode ? "#e2e8f0" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                            {mode === "list"
                              ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={viewMode === mode ? "#334155" : "#94a3b8"} strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
                              : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={viewMode === mode ? "#334155" : "#94a3b8"} strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                            }
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Products */}
                  {isOpen && (
                    viewMode === "grid" ? (
                      /* GRID VIEW */
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                        {prods.map(p => (
                          <div
                            key={p.id}
                            onClick={() => handleProductClick(p)}
                            title={p.name}
                            style={{ background: "var(--app-bg-alt, var(--app-bg))", border: "1px solid var(--app-border)", borderRadius: 9, padding: "9px 7px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "var(--app-hover-bg)"; e.currentTarget.style.borderColor = "var(--app-hover-border)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "var(--app-bg-alt, var(--app-bg))"; e.currentTarget.style.borderColor = "var(--app-border)"; }}
                          >
                            <div style={{ width: 40, height: 40, borderRadius: 7, background: "var(--app-bg)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                              <img src={p.image} alt={p.name} draggable={false} style={{ width: "75%", height: "75%", objectFit: "contain" }} />
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--app-text)", textAlign: "center", lineHeight: 1.3, width: "100%", overflow: "hidden", textOverflow: "ellipsis", wordBreak: "break-word", overflowWrap: "break-word" }}>{p.name}</div>
                            <div style={{ fontSize: 9, color: "var(--app-text-muted)" }}>{p.brand}</div>
                            <div style={{ fontSize: 8, padding: "2px 6px", borderRadius: 99, background: "var(--app-bg)", color: "var(--app-text-muted)", fontWeight: 700, textTransform: "capitalize" }}>{p.mountOn}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* LIST VIEW */
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {prods.map(p => (
                          <div
                            key={p.id}
                            onClick={() => handleProductClick(p)}
                            style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 8px", background: "var(--app-bg-alt, var(--app-bg))", border: "1px solid var(--app-border)", borderRadius: 8, cursor: "pointer", transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "var(--app-hover-bg)"; e.currentTarget.style.borderColor = "var(--app-hover-border)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "var(--app-bg-alt, var(--app-bg))"; e.currentTarget.style.borderColor = "var(--app-border)"; }}
                          >
                            <div style={{ width: 32, height: 32, borderRadius: 6, background: "var(--app-bg)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                              <img src={p.image} alt={p.name} draggable={false} style={{ width: "75%", height: "75%", objectFit: "contain" }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--app-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                              <div style={{ fontSize: 10, color: "var(--app-text-muted)", marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>
                                <span>{p.brand}</span>
                                <span style={{ width: 2, height: 2, borderRadius: "50%", background: "#cbd5e1", display: "inline-block" }} />
                                <span style={{ textTransform: "capitalize" }}>{p.mountOn}</span>
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                              <div style={{ width: 6, height: 6, borderRadius: "50%", background: getCategoryColor(p) }} />
                              <Icon.Plus size={14} color="var(--app-text-muted)" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              );
            })}

            {/* Surface hint */}
            <div style={{ marginTop: 8, padding: "10px 12px", background: "var(--app-bg-alt, var(--app-bg))", borderRadius: 8, border: "1px solid var(--app-border)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--app-text-muted)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Placement Surfaces</div>
              {[
                { icon: <Icon.Floor size={13} color="var(--app-text-muted)" />, label: "Floor", desc: "Freestanding devices" },
                { icon: <Icon.Chair size={13} color="var(--app-text-muted)" />, label: "Table Top", desc: "Mics, Speakers" },
                { icon: <Icon.Wall size={13} color="var(--app-text-muted)" />, label: "Wall", desc: "Displays, Cameras" },
                { icon: <Icon.Ceiling size={13} color="var(--app-text-muted)" />, label: "Ceiling", desc: "Mics, Speakers" }
              ].map(s => (
                <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ width: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.icon}</span>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--app-text-muted)", fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontSize: 9, color: "var(--app-text-muted)" }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 3D CANVAS */}
        <div ref={containerRef} style={{ flex: 1, position: "relative", minWidth: 0, cursor: canvasCursor }}>
          <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onClick={handleCanvasClick} />

          {(showMeasurements || showCoverage) && sceneRef.current && (
            <svg ref={overlayRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 10 }} viewBox={`0 0 ${canvasSize.width} ${canvasSize.height}`}>
              {showMeasurements && (() => {
                const tablePos = getTablePos();
                const { width: rw, depth: rd, height: rh } = room;
                const project = (wx, wy, wz) => { const v = new THREE.Vector3(wx, wy, wz); v.project(sceneRef.current.camera); return { x: ((v.x + 1) / 2) * canvasSize.width, y: ((-v.y + 1) / 2) * canvasSize.height, behind: v.z > 1 }; };
                const p00 = project(0, 0.05, 0), pW0 = project(rw, 0.05, 0), p0D = project(0, 0.05, rd);
                const widthMid = project(rw / 2, 0.05, 0), depthMid = project(0, 0.05, rd / 2);
                const cornerBot = project(0, 0, 0), cornerTop = project(0, rh, 0);
                const arrowHead = (x1, y1, x2, y2, size = 6) => { const a = Math.atan2(y2 - y1, x2 - x1); return `M${x2 - size * Math.cos(a - 0.4)},${y2 - size * Math.sin(a - 0.4)} L${x2},${y2} L${x2 - size * Math.cos(a + 0.4)},${y2 - size * Math.sin(a + 0.4)}`; };
                const dimLabel = (p, text, ox, oy, color) => { if (!p || p.behind) return null; return (<g key={`d-${text}`}><rect x={p.x + ox - 24} y={p.y + oy - 9} width={48} height={18} rx={4} fill="rgba(15,23,42,0.82)" stroke={color} strokeWidth={0.8} /><text x={p.x + ox} y={p.y + oy + 4} textAnchor="middle" fill={color} fontSize={11} fontWeight="700" fontFamily="monospace">{text}</text></g>); };
                let tableLines = [];
                if (tablePos) {
                  const { cx, cz, tw, td } = tablePos; const yT = 0.05;
                  const lE = cx - tw / 2, rE = rw - (cx + tw / 2), bE = cz - td / 2, fE = rd - (cz + td / 2);
                  tableLines = [
                    { a: project(cx - tw / 2, yT, cz - td / 2), b: project(cx + tw / 2, yT, cz - td / 2), mid: project(cx, yT, cz - td / 2), label: `${tw.toFixed(1)}m`, color: "#B0674A" },
                    { a: project(cx - tw / 2, yT, cz - td / 2), b: project(cx - tw / 2, yT, cz + td / 2), mid: project(cx - tw / 2, yT, cz), label: `${td.toFixed(1)}m`, color: "#B0674A" },
                    { a: project(cx - tw / 2, yT, cz), b: project(0, yT, cz), mid: project(lE / 2, yT, cz), label: `${lE.toFixed(2)}m`, color: "#c084fc" },
                    { a: project(cx + tw / 2, yT, cz), b: project(rw, yT, cz), mid: project(cx + tw / 2 + rE / 2, yT, cz), label: `${rE.toFixed(2)}m`, color: "#c084fc" },
                    { a: project(cx, yT, cz - td / 2), b: project(cx, yT, 0), mid: project(cx, yT, bE / 2), label: `${bE.toFixed(2)}m`, color: "#c084fc" },
                    { a: project(cx, yT, cz + td / 2), b: project(cx, yT, rd), mid: project(cx, yT, cz + td / 2 + fE / 2), label: `${fE.toFixed(2)}m`, color: "#c084fc" },
                  ];
                }
                return (<g>
                  {!p00.behind && !pW0.behind && <g><line x1={p00.x} y1={p00.y} x2={pW0.x} y2={pW0.y} stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} /><path d={arrowHead(p00.x, p00.y, pW0.x, pW0.y)} stroke="#38bdf8" strokeWidth={1.5} fill="none" /><path d={arrowHead(pW0.x, pW0.y, p00.x, p00.y)} stroke="#38bdf8" strokeWidth={1.5} fill="none" /></g>}
                  {dimLabel(widthMid, `${rw}m`, 0, -14, "#38bdf8")}
                  {!p00.behind && !p0D.behind && <g><line x1={p00.x} y1={p00.y} x2={p0D.x} y2={p0D.y} stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} /><path d={arrowHead(p00.x, p00.y, p0D.x, p0D.y)} stroke="#38bdf8" strokeWidth={1.5} fill="none" /><path d={arrowHead(p0D.x, p0D.y, p00.x, p00.y)} stroke="#38bdf8" strokeWidth={1.5} fill="none" /></g>}
                  {dimLabel(depthMid, `${rd}m`, -28, 0, "#38bdf8")}
                  {!cornerBot.behind && !cornerTop.behind && <g><line x1={cornerBot.x} y1={cornerBot.y} x2={cornerTop.x} y2={cornerTop.y} stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} /><path d={arrowHead(cornerBot.x, cornerBot.y, cornerTop.x, cornerTop.y)} stroke="#38bdf8" strokeWidth={1.5} fill="none" /><path d={arrowHead(cornerTop.x, cornerTop.y, cornerBot.x, cornerBot.y)} stroke="#38bdf8" strokeWidth={1.5} fill="none" /></g>}
                  {dimLabel({ x: (cornerBot.x + cornerTop.x) / 2, y: (cornerBot.y + cornerTop.y) / 2, behind: cornerBot.behind }, `${rh}m`, -32, 0, "#38bdf8")}
                  {tableLines.map((tl, i) => { if (!tl.a || !tl.b || tl.a.behind || tl.b.behind) return null; return (<g key={`tl-${i}`}><line x1={tl.a.x} y1={tl.a.y} x2={tl.b.x} y2={tl.b.y} stroke={tl.color} strokeWidth={1.2} strokeDasharray="4 3" opacity={0.8} /><path d={arrowHead(tl.a.x, tl.a.y, tl.b.x, tl.b.y)} stroke={tl.color} strokeWidth={1.2} fill="none" /><path d={arrowHead(tl.b.x, tl.b.y, tl.a.x, tl.a.y)} stroke={tl.color} strokeWidth={1.2} fill="none" />{tl.mid && !tl.mid.behind && dimLabel(tl.mid, tl.label, 0, -12, tl.color)}</g>); })}
                </g>);
              })()}
              {showMeasurements && (<g transform={`translate(16,${canvasSize.height - 100})`}>
                <rect x={0} y={0} width={220} height={90} rx={10} fill="rgba(15,23,42,0.88)" stroke="rgba(56,189,248,0.25)" strokeWidth={1} />
                <text x={30} y={18} fill="#38bdf8" fontSize={10} fontWeight="700" fontFamily="monospace">MEASUREMENTS LEGEND</text>
                <line x1={10} y1={36} x2={34} y2={36} stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="4 3" />
                <text x={40} y={40} fill="#94a3b8" fontSize={10} fontFamily="sans-serif">Room dimensions (W × D × H)</text>
                <line x1={10} y1={56} x2={34} y2={56} stroke="#B0674A" strokeWidth={1.5} strokeDasharray="5 3" />
                <text x={40} y={60} fill="#94a3b8" fontSize={10} fontFamily="sans-serif">Table size</text>
                <line x1={10} y1={74} x2={34} y2={74} stroke="#c084fc" strokeWidth={1.2} strokeDasharray="4 3" />
                <text x={40} y={78} fill="#94a3b8" fontSize={10} fontFamily="sans-serif">Distance to walls</text>
              </g>)}
            </svg>
          )}

          {isPlacing && (
            <div style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", background: "rgba(59,130,246,0.9)", backdropFilter: "blur(8px)", borderRadius: 10, padding: "10px 20px", color: "#fff", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 20px rgba(59,130,246,0.4)" }}>
              <Icon.Pin size={14} color="#fff" /> Placing: <strong>{draggingProduct?.name}</strong>
              <span style={{ opacity: 0.7, fontSize: 11 }}>· {surfaceHint || "Hover over any surface"}</span>
              <button onClick={() => { sceneRef.current?.endGhost(); setIsPlacing(false); setDraggingProduct(null); setSurfaceHint(""); }} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, padding: "2px 8px", color: "#fff", cursor: "pointer", fontSize: 11, marginLeft: 4 }}>ESC</button>
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        <div style={{ width: 290, background: "var(--app-bg-alt, var(--app-bg))", borderLeft: "1px solid var(--app-border)", padding: 14, overflowY: "auto", flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: "var(--app-text-muted)", textTransform: "uppercase", marginBottom: 12 }}>Properties</div>

          {/* Room */}
          <div style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--app-text)", marginBottom: 10 }}>Room</div>
            {[{ label: "Width", key: "width", min: 3, max: 20, step: 0.1 }, { label: "Length", key: "depth", min: 3, max: 20, step: 0.1 }, { label: "Height", key: "height", min: 2, max: 6, step: 0.1 }].map(({ label, key, min, max, step }) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: "var(--app-text-muted)", display: "block", marginBottom: 3 }}>{label}: {room[key]}m</label>
                <input type="range" min={min} max={max} step={step} value={room[key]} onChange={e => { const val = Number(e.target.value); const newRoom = { ...room, [key]: val }; setRoom(newRoom); sceneRef.current?.updateRoom(newRoom); }} style={{ width: "100%", accentColor: "#3b82f6" }} />
              </div>
            ))}
            <div style={{ fontSize: 11, color: "var(--app-text-muted)", marginTop: 2 }}>Area: {(room.width * room.depth).toFixed(1)} m² · Vol: {(room.width * room.depth * room.height).toFixed(1)} m³</div>
          </div>

          {/* Table + Chair Sides */}
          <div style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--app-text)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon.Table size={14} color="#5c3d2e" /> Conference Table
            </div>
            {[{ label: "Width", key: "width", min: 1, max: 6, step: 0.1 }, { label: "Length", key: "depth", min: 0.8, max: 3, step: 0.1 }, { label: "Height", key: "height", min: 0.6, max: 1.0, step: 0.01 }].map(({ label, key, min, max, step }) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: "var(--app-text-muted)", display: "block", marginBottom: 3 }}>{label}: {tableSettings[key]}m</label>
                <input type="range" min={min} max={max} step={step} value={tableSettings[key]} onChange={e => handleTableChange(key, Number(e.target.value))} style={{ width: "100%", accentColor: "#5c3d2e" }} />
              </div>
            ))}
            <div style={{ borderTop: "1px solid var(--app-border)", paddingTop: 10, marginTop: 4 }}>
              <ChairSideToggle chairSides={chairSides} tableWidth={tableSettings.width} tableDepth={tableSettings.depth} onToggle={handleChairSideToggle} />
            </div>
          </div>

          {/* Selected item */}
          {selectedItem ? (
            <div style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--app-bg)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <img src={selectedItem.product?.image} alt="" style={{ width: "80%", height: "80%", objectFit: "contain" }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--app-text)" }}>{selectedItem.product?.name}</div>
                  <div style={{ fontSize: 10, color: "var(--app-text-muted)", textTransform: "capitalize", display: "flex", alignItems: "center", gap: 4 }}>{getTypeIcon(selectedItem.product?.type)}{selectedItem.product?.type} · {getSurfaceLabel(selectedItem.surface)}</div>
                </div>
              </div>
              <div style={{ padding: "8px 10px", background: "var(--app-bg-alt, var(--app-bg))", borderRadius: 8, border: "1px solid var(--app-border)", marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: "var(--app-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Position</div>
                <div style={{ fontSize: 11, color: "var(--app-text)", marginTop: 4, lineHeight: 1.8 }}>X: {selectedItem.x?.toFixed(2)}m &nbsp; Y: {selectedItem.y?.toFixed(2)}m &nbsp; Z: {selectedItem.z?.toFixed(2)}m</div>
              </div>
              {selectedItem.product?.coverageDistance && (
                <div style={{ padding: "8px 10px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", marginBottom: 8, fontSize: 11, color: "#166534", display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon.Signal size={13} color="#166534" />
                  Coverage: {selectedItem.product.coverageAngle >= 300 ? "360° (omnidirectional)" : `${selectedItem.product.coverageAngle}° (directional)`} · {selectedItem.product.coverageDistance}m range
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: selectedItem.surface?.startsWith("wall") ? "#eff6ff" : selectedItem.surface === "ceiling" ? "#f0fdf4" : selectedItem.surface === "table" ? "#fef9ec" : "#f8fafc", borderRadius: 999, border: "1px solid var(--app-border)", fontSize: 10, color: "#1e293b", fontWeight: 600 }}>
                  {selectedItem.surface === "floor" ? <Icon.Floor size={11} color="#374151" /> : selectedItem.surface === "table" ? <Icon.Chair size={11} color="#374151" /> : selectedItem.surface === "ceiling" ? <Icon.Ceiling size={11} color="#374151" /> : <Icon.Wall size={11} color="#374151" />}
                  {getSurfaceLabel(selectedItem.surface)}
                </div>
              </div>
              <button onClick={() => handleDeleteItem(selectedId)} style={{ width: "100%", padding: "7px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, color: "#ef4444", fontSize: 12, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <Icon.Trash size={13} color="#ef4444" /> Remove Device
              </button>

            </div>
          ) : (
            <div style={{ padding: 16, background: "var(--app-bg-alt, var(--app-bg))", borderRadius: 10, border: "1px dashed #e2e8f0", textAlign: "center", color: "var(--app-text-muted)", fontSize: 11 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}><Icon.Mouse size={24} color="#cbd5e1" /></div>
              Click a device to select · Drag table or chairs to move
            </div>
          )}

          {placedItems.length > 0 && (
            <div style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--app-text)", marginBottom: 10 }}>Placed Devices ({placedItems.length})</div>
              {placedItems.map(item => (
                <div key={item.id} onClick={() => { setSelectedId(item.id); sceneRef.current?.setSelected(item.id); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 7, cursor: "pointer", background: item.id === selectedId ? "var(--app-selected-bg)" : "transparent", border: item.id === selectedId ? "1px solid var(--app-selected-border)" : "1px solid transparent", marginBottom: 3, transition: "all 0.15s" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: getCategoryColor(item.product), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--app-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.product?.name}</div>
                    <div style={{ fontSize: 10, color: "var(--app-text-muted)" }}>{getSurfaceLabel(item.surface)}</div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleDeleteItem(item.id); }} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", padding: "0 2px", flexShrink: 0, display: "flex", alignItems: "center" }}>
                    <Icon.X size={13} color="#d1d5db" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ height: 44, background: "var(--app-surface)", borderTop: "1px solid var(--app-border)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          {[
            { label: "Room", value: `${room.width}×${room.depth}×${room.height}m` },
            { label: "Devices", value: placedItems.length },
            { label: "Displays", value: placedItems.filter(i => i.product?.type === "display").length, color: categoryColorsByType.display },
            { label: "Cameras", value: placedItems.filter(i => i.product?.type === "camera").length, color: categoryColorsByType.camera },
            { label: "Mics", value: placedItems.filter(i => i.product?.type === "microphone").length, color: categoryColorsByType.microphone },
            { label: "Speakers", value: placedItems.filter(i => i.product?.type === "speaker").length, color: categoryColorsByType.speaker }
          ].map((stat, i) => (
            <React.Fragment key={stat.label}>
              {i > 0 && <div style={{ width: 1, height: 16, background: "var(--app-border)" }} />}
              <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: stat.color || "var(--app-text)" }}>{stat.value}</span>
                <span style={{ fontSize: 10, color: "var(--app-text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{stat.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div style={{ fontSize: 10, color: "var(--app-text-muted)" }}>{(theme.footer_text || DEFAULT_THEME.footer_text).replace("{year}", new Date().getFullYear())}</div>
      </footer>
    </div>
  );
}